import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
  Bike,
  Car,
  Camera,
  ChevronUp,
  ChevronDown,
  MapPin,
  X,
  Droplets,
  Footprints,
  HelpCircle,
  Instagram,
  Landmark,
  MessageSquare,
  Trees,
  Check,
  Star,
  Loader2,
  Mic,
  RotateCw,
  Copy,
  Mail,
  Sparkles,
  Navigation,
  Settings,
  Map as MapIcon,
  
  Info,
  Heart,
  LifeBuoy,
  Shield,
  Languages,
  LogIn,
  LogOut,
  User as UserIcon,
  ArrowLeft,
  ChevronRight,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { useSavedTripsSync } from "@/hooks/useSavedTripsSync";
import { PleasantLogo } from "@/components/PleasantLogo";
import { Button } from "@/components/ui/button";
import { MapView } from "@/components/MapView";
import { visibleMapRoutes } from "@/utils/visibleMapRoutes";
import { DebugOverlay } from "@/components/DebugOverlay";
import type { RouteDiagnostics } from "@/lib/routeDiagnostics";
import { getRouteSource } from "@/lib/routeDiagnostics";
import { routeEtaFragment } from "@/utils/routeDebugId";
import { ContributeFab } from "@/components/ContributeFab";
import { DeleteAccountButton } from "@/components/DeleteAccountButton";
import { useHeading } from "@/hooks/useHeading";
import { loadPhotoPins, type PhotoPin } from "@/api/photoPins";
import { isContentVisible } from "@/utils/contentSafety";
import { recordCompletedTrip, maybeRequestReview } from "@/native/rateApp";
import {
  carShouldShowTrip,
  carplayPushRouteChoices,
  carplayAnnounce,
  carplaySendCurrentTrip,
  isCarplayConnected,
} from "@/native/carplayBridge";
import {
  isHighwayHeavy as isHighwayHeavyRoute,
  mergeEnginePoolIntoPleasantCandidates,
  pickAlternativeCard,
  pickDisplayFastAnchor,
  motorwayPct,
  scenicMotorwayBar,
  ALTERNATIVE_SCENIC_POINT_GAP,
} from "@/utils/pleasantSlot";
import { SHIPPED_JULY_14_ROUTING } from "@/utils/routingBaseline";

// Landing-screen "Take me home" chip. History: the ALWAYS-ON version was
// removed at Morgan's request ("i did not approve that"). July 29 friction
// batch: she approved the NARROW version — evenings only (4pm–midnight),
// dismissible forever via its ✕. Anything broader needs her sign-off again.
const SHOW_GOING_HOME_CHIP = true;
// Loops parked for 2.3 (Morgan, July 30) — flip to true to reopen.
const SHOW_LOOP_BUTTON = false;
const HOME_CHIP_DISMISS_KEY = "pleasant.homeChip.dismissed.v1";
const eveningNow = () => {
  const h = new Date().getHours();
  return h >= 16 && h <= 23;
};
import { loadTipPins, type TipPin } from "@/api/tips";
import { TripSearch, type TripPoint } from "@/components/TripSearch";
import { RouteCard } from "@/components/RouteCard";
import { useTrafficEtas } from "@/hooks/useTrafficEtas";
import { resolveBaseline, type BaselineMode } from "@/utils/baseline";
import { SignalsAlongRoute } from "@/components/SignalsAlongRoute";
import { MySavedTagsAlongRoute } from "@/components/MySavedTagsAlongRoute";
import { DriveByOptions } from "@/components/DriveByOptions";

import { Onboarding } from "@/components/Onboarding";
import { QuickSignIn, wasSignInSkipped } from "@/components/QuickSignIn";
import { QuickAuthDialog } from "@/components/QuickAuthDialog";
import { NoticeBanner } from "@/components/NoticeBanner";
import { WeatherBadge } from "@/components/WeatherBadge";
import { GoingHomeChip } from "@/components/GoingHomeChip";
import { PleasantPlusPaywall } from "@/components/PleasantPlusPaywall";
import { CarPlayAnnouncement } from "@/components/CarPlayAnnouncement";
import { isCarplayCardEligible, markCarplayCardEligible } from "@/utils/carplayCardGate";
import { ShareTripButton, parseTripPointParam, parseStopsParam, buildTripPath } from "@/components/ShareTripButton";
import { NameTripDialog } from "@/components/NameTripDialog";

// Heavy post-route / dialog components — lazy-loaded so the initial map
// render on mobile doesn't block on parsing them. They only mount once the
// user actually opens a route, dialog, or starts navigation.
const AddPhotoPinDialog = lazy(() => import("@/components/AddPhotoPinDialog").then((m) => ({ default: m.AddPhotoPinDialog })));
const PhotoPinDetailDialog = lazy(() => import("@/components/PhotoPinDetailDialog").then((m) => ({ default: m.PhotoPinDetailDialog })));
const AddTipDialog = lazy(() => import("@/components/AddTipDialog").then((m) => ({ default: m.AddTipDialog })));
const TipDetailDialog = lazy(() => import("@/components/TipDetailDialog").then((m) => ({ default: m.TipDetailDialog })));
const RouteExplainChat = lazy(() => import("@/components/RouteExplainChat").then((m) => ({ default: m.RouteExplainChat })));
const DirectionsView = lazy(() => import("@/components/DirectionsView").then((m) => ({ default: m.DirectionsView })));
const NavigationView = lazy(() => import("@/components/NavigationView").then((m) => ({ default: m.NavigationView })));
const TripEndSheet = lazy(() => import("@/components/TripEndSheet").then((m) => ({ default: m.TripEndSheet })));
const TripRecap = lazy(() => import("@/components/TripRecap").then((m) => ({ default: m.TripRecap })));
import type { TripRecapSnapshot } from "@/components/TripRecap";
const TripJournal = lazy(() => import("@/components/TripJournal").then((m) => ({ default: m.TripJournal })));
const SavePlaceDialog = lazy(() => import("@/components/SavePlaceDialog").then((m) => ({ default: m.SavePlaceDialog })));
import { useGoogleComparison } from "@/hooks/useGoogleComparison";
import { buildGoogleCarrier } from "@/utils/googleCarrier";
import { GoogleRouteSlot } from "@/components/GoogleRouteSlot";
import { BaselineDebugPanel } from "@/components/BaselineDebugPanel";
import { highwayPctOf, routeWaypointSignature } from "@/api/googleDirections";
// SoundtrackButton temporarily removed.
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { PreferenceSelector } from "@/components/PreferenceSelector";

import { checkRequiredEnv } from "@/lib/env";
import { OfflineBanner } from "@/components/OfflineBanner";
import { useUserLocation } from "@/hooks/useUserLocation";
import { readGeolocationPermission, requestUserLocation } from "@/utils/geolocation";
import { primeWebSpeech } from "@/utils/nativeVoice";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useIsMobile } from "@/hooks/use-mobile";
import { saveOfflineTrip, loadOfflineTrip, offlineTripAgeLabel, markActiveTrip, clearActiveTrip, wasTripInterrupted, type OfflineTripSnapshot } from "@/utils/offlineCache";
import { recordTrip, listTrips } from "@/utils/tripHistory";
// (ClassifiedRoute is imported in the grouped @/utils/scoring import below.)

/** Short list of "what was nice" chips for persistence in trip history.
 *  Mirrors the in-recap highlights logic but keeps it inline here so the
 *  history blob is self-contained and survives a TripRecap refactor. */
function buildTripHistoryHighlights(route: ClassifiedRoute): string[] {
  const f = route.features;
  const out: string[] = [];
  if (f.waterNames?.[0]) out.push(`Along ${f.waterNames[0]}`);
  else if ((f.waterPct ?? 0) >= 8) out.push(`${Math.round(f.waterPct ?? 0)}% along water`);
  if (f.parkNames?.[0]) out.push(`Through ${f.parkNames[0]}`);
  else if ((f.parks ?? 0) >= 2) out.push(`Past ${f.parks} parks`);
  const hist = f.historicNames?.[0] ?? f.landmarkNames?.[0];
  if (hist) out.push(`Past ${hist}`);
  const epic = route.route.epicHighwayLabels?.[0];
  const epicM = route.route.epicHighwayMeters ?? 0;
  if (epic && epicM > 1000) {
    // Aug 19 units audit: was a hardcoded "X.X mi" — fmtDistance honours the
    // driver's chosen system, matching the identical chip TripRecap builds.
    out.push(`${fmtDistance(epicM)} along ${epic}`);
  }
  return out.slice(0, 4);
}
import { isExampleTrip, maybeSaveExamplePreview } from "@/utils/examplePreviewCache";
import { OfflineRouteDetailsPanel } from "@/components/OfflineRouteDetailsPanel";
import { type TravelMode, fetchRoutes, reverseGeocode } from "@/api/routing";
import { type CommunityTag } from "@/api/community";
import { useSegmentPrefetch } from "@/hooks/useSegmentPrefetch";
import { useRouteRequest } from "@/hooks/useRouteRequest";
import type { GenerateRoutesArgs, RouteGenStatus } from "@/lib/generateRoutes";
import {
  BUFFER_BY_MODE,
  bbox,
  classifyRoutes,
  scoreRoute,
  summarizeFeatures,
  communityScore,
  geometryBuckets,
  jaccard,
  pleasantBudgetSeconds,
  type ClassifiedRoute,
  type Preference,
  pleasantPoolSnapshot,
} from "@/utils/scoring";
import { samplePolylineMax, haversine, type LngLat } from "@/utils/polyline";
import { routesAreSameRoad } from "@/utils/routeTwins";
import { buildRouteColorNotes } from "@/utils/routeColorNotes";
import { buildRouteIntroNarration, buildRouteReason, dominantRoadLabel } from "@/utils/routeReason";
import {
  loadProfile, defaultProfile, saveProfile,
  TOLERANCE_MULTIPLIER, TOLERANCE_LABELS,
  type UserProfile, type PleasantnessProfile, type DetourTolerance,
} from "@/utils/profile";
import { PleasantnessProfilePicker } from "@/components/PleasantnessProfilePicker";
import { DetourToleranceSlider } from "@/components/DetourToleranceSlider";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { VoicePicker } from "@/components/VoicePicker";
import { NarrationPicker } from "@/components/NarrationPicker";
import { UnitsPicker } from "@/components/UnitsPicker";
import { WeekendNudgePicker } from "@/components/WeekendNudgePicker";
import { LocaleMenuButton } from "@/components/LocaleMenuButton";
import { useLocale } from "@/hooks/useLocale";
import { useUnitSystem } from "@/hooks/useUnitSystem";
import { fmtDistance } from "@/utils/units";

import { toast } from "sonner";
import { buildLoopPlan, LOOP_CHOICES } from "@/lib/loopDrive";
import { avoidDrivePenalty } from "@/utils/scenicRoads";
// SavePlaceDialog lazy-loaded above
import { findSavedPlaceNear, findHomePlace, type SavedPlace } from "@/utils/savedPlaces";
import { recordRecentSearch } from "@/utils/recentSearches";
// SoundtrackButton lazy-loaded above

/** Order intermediate stops between start and destination so each one sits
 *  in the position along the trip that minimizes total added distance. Uses
 *  cheapest-insertion: starts with [start, end] and inserts each stop at
 *  the index that grows the path length the least. Good enough for the
 *  small handful of stops a user adds, and runs instantly. */
const orderStopsAlongRoute = (
  start: LngLat,
  end: LngLat,
  stops: TripPoint[],
): TripPoint[] => {
  if (stops.length <= 1) return stops;
  const path: LngLat[] = [start, end];
  const ordered: TripPoint[] = [];
  for (const stop of stops) {
    let bestIdx = 1;
    let bestDelta = Infinity;
    for (let i = 1; i < path.length; i++) {
      const before = path[i - 1];
      const after = path[i];
      const delta =
        haversine(before, stop.coords) + haversine(stop.coords, after) - haversine(before, after);
      if (delta < bestDelta) {
        bestDelta = delta;
        bestIdx = i;
      }
    }
    path.splice(bestIdx, 0, stop.coords);
    ordered.splice(bestIdx - 1, 0, stop);
  }
  return ordered;
};

// The SF → Half Moon Bay showcase trip. For this one iconic example we hardwire
// the presentation so Pacific Coast Highway (the coastal, westernmost route) IS
// the Pleasant pick — not the inland "less highway" alternative the generic
// scorer would otherwise prefer (PCH is technically a highway, so it loses the
// calm score even though it's the gorgeous one). Scoped to this trip only.
const HMB_DEMO_START: LngLat = [-122.3933, 37.7955];
const HMB_DEMO_END: LngLat = [-122.4286, 37.4636];
const isHmbDemoTrip = (start?: LngLat, end?: LngLat): boolean =>
  !!start && !!end &&
  haversine(start, HMB_DEMO_START) <= 3000 &&
  haversine(end, HMB_DEMO_END) <= 3000;


// Drive sits on the left because it's the most-used mode for the brand's
// core wedge (scenic drivers / road-trippers). Walk and Bike follow.
const MODES: { id: TravelMode; label: string; Icon: typeof Car }[] = [
  { id: "driving", label: "Drive", Icon: Car },
  { id: "walking", label: "Walk", Icon: Footprints },
  { id: "biking", label: "Bike", Icon: Bike },
];

const Index = () => {
  const env = useMemo(() => checkRequiredEnv(), []);
  const { user, signOut } = useAuth();
  // Keep saved trips reconciled with the account (sign-in, foreground, reconnect).
  useSavedTripsSync(user?.id ?? null);
  // Subscribe to the active locale so the whole page (RouteCards, banners,
  // etc. — anything that calls getRouteCopy()) re-renders when the user
  // changes their language in Settings. The actual value isn't used here;
  // the subscription is what triggers the re-render.
  useLocale();
  // Aug 19 units audit: same pattern for distance units. fmtDistance() reads
  // the setting at call time, so without this subscription every card on
  // screen kept its old units after a change in Settings until an unrelated
  // re-render — exactly the "the setting does not work" bug the hook's own
  // docstring warns about. RouteCard is not memoized (checked), so this
  // page-level re-render reaches every card; the value itself is unused.
  const [unitSystem] = useUnitSystem();

  // Profile (loves + default mode), persisted in localStorage. Falls back to a
  // neutral default so the app opens straight to the route search — the
  // multi-step onboarding intro was removed to cut the click-through (the
  // marketing page at "/" is the only intro now).
  const [profile, setProfile] = useState<UserProfile | null>(() => loadProfile() ?? defaultProfile());
  // Onboarding intro is no longer shown — the app renders the map/search
  // immediately. Kept as a constant (always dismissed) so the legacy gate below
  // is inert without ripping out the example-trip plumbing.
  const [introDismissed, setIntroDismissed] = useState<boolean>(true);

  /** True when the user arrived from the SF → Half Moon Bay example trip
   *  on the Onboarding screen. The DetourToleranceCoachmark uses this to
   *  decide whether to show the one-time arrow pointing at the "Scenic"
   *  pill. Read once on mount — we don't need it to react to nav changes. */
  const [cameFromOnboarding] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return new URLSearchParams(window.location.search).get("onboarded") === "1";
    } catch {
      return false;
    }
  });


  // Live map / location — see useUserLocation for the friendly permission
  // wrapper (handles iOS pre-prompt + denied-state Settings instructions).
  const {
    coords: userLocation,
    status: locationStatus,
    isRequesting: locating,
    request: requestLocation,
  } = useUserLocation(true);

  // Trip state — ALWAYS open on Drive (Morgan: "make the first thing
  // selected drive not walk"). The profile's saved mode no longer decides
  // the boot state; switching modes stays one tap away.
  const [mode, setMode] = useState<TravelMode>("driving");
  // Heading-up compass mode. Off by default — user opts in via the on-map button.
  const [headingUp, setHeadingUp] = useState(false);
  const { heading, requestPermission: requestHeadingPermission } = useHeading(headingUp);

  // Photo pins
  const [photoPins, setPhotoPins] = useState<PhotoPin[]>([]);
  const [addPinOpen, setAddPinOpen] = useState(false);
  const [pendingPinLocation, setPendingPinLocation] = useState<LngLat | null>(null);
  const [pendingPinLabel, setPendingPinLabel] = useState<string | undefined>(undefined);
  const [openPin, setOpenPin] = useState<PhotoPin | null>(null);

  // Tip pins (always public). Loaded alongside photo pins.
  const [tipPins, setTipPins] = useState<TipPin[]>([]);
  const [addTipOpen, setAddTipOpen] = useState(false);
  const [openTip, setOpenTip] = useState<TipPin | null>(null);
  // Long-press picker: when set, user has long-pressed a spot and we're
  // showing a tiny "Photo or Tip?" chooser before opening the right dialog.
  const [longPressChoice, setLongPressChoice] = useState<LngLat | null>(null);
  /** "Position the star" step (Morgan): after picking Photo/Tip from the
   *  long-press menu, a draggable star appears at the tapped spot with a
   *  confirm bar — drag to fine-tune, then the form dialog opens with the
   *  final coordinates. */
  const [pinAdjust, setPinAdjust] = useState<{ kind: "photo" | "tip"; coords: LngLat } | null>(null);

  // Toggle the Google Maps reference polyline overlay on the map. Persisted
  // so the user's preference survives reloads. Comparison cards stay intact
  // regardless of this toggle — it only affects the on-map overlay.
  //
  // Default behavior (no saved preference yet): auto-enable when Google's
  // route differs meaningfully from Pleasant's pick, so users see both
  // routes side by side without having to discover the toggle. Once they
  // explicitly click the toggle, their choice persists. Real-world
  // feedback: "it only showed one route." Wired below in a useEffect that
  // watches the loaded Google route and the selected Pleasant route.
  const GOOGLE_OVERLAY_KEY = "pleasant.showGoogleRoute";
  const [showGoogleRoute, setShowGoogleRouteState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(GOOGLE_OVERLAY_KEY) === "1";
  });
  const setShowGoogleRoute = (v: boolean) => {
    setShowGoogleRouteState(v);
    try { window.localStorage.setItem(GOOGLE_OVERLAY_KEY, v ? "1" : "0"); } catch { /* ignore quota */ }
  };

  // Comparison-baseline mode for the "+X min" pill across route cards.
  // Persisted so the user's preferred reference (Google ETA / our fastest /
  // our pleasant pick) survives page reloads. "auto" means: use Google when
  // available, else our fastest — preserving the previous default behavior.
  const BASELINE_KEY = "pleasant.baselineMode";
  const [baselineMode, setBaselineModeState] = useState<BaselineMode>(() => {
    if (typeof window === "undefined") return "auto";
    const v = window.localStorage.getItem(BASELINE_KEY);
    return v === "google" || v === "fastest" || v === "pleasant" ? v : "auto";
  });
  const setBaselineMode = (m: BaselineMode) => {
    setBaselineModeState(m);
    try { window.localStorage.setItem(BASELINE_KEY, m); } catch { /* ignore quota */ }
  };

  // Quick-auth modal: when an action requires sign-in, we stash the action
  // and pop a small dialog. After successful auth, we re-run the stashed
  // action so the user lands exactly where they were.
  const [authPrompt, setAuthPrompt] = useState<{ reason: string; resume: () => void } | null>(null);
  const requireAuth = (reason: string, action: () => void) => {
    if (user) { action(); return; }
    setAuthPrompt({ reason, resume: action });
  };
  // Resume any pending action once auth completes (covers OAuth redirects too).
  useEffect(() => {
    if (user && authPrompt) {
      const fn = authPrompt.resume;
      setAuthPrompt(null);
      // Defer one tick so any closing dialog doesn't fight the new one opening.
      setTimeout(() => fn(), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // (Sign-in invitation effect lives below, after navOpen/routeGenStatus are
  // declared — placing it here crashed the whole site with a TDZ error:
  // "Cannot access 'ie' before initialization", Aug 2. Effects that read
  // component state must sit BELOW every declaration they touch, because the
  // dependency array is evaluated during render, not inside the callback.)

  // Load pins (own + public) and tips once we know who the user is.
  useEffect(() => {
    let cancelled = false;
    Promise.all([loadPhotoPins(), loadTipPins()])
      .then(([pins, tips]) => {
        if (cancelled) return;
        // UGC moderation (Guideline 1.2): content the user reported/hid and
        // content from users they blocked must STAY gone. The dialogs write
        // hide/block state via contentSafety; this is the read side — without
        // it, hidden pins reappeared on the next load.
        setPhotoPins(pins.filter(isContentVisible));
        setTipPins(tips.filter(isContentVisible));
      })
      .catch(() => { /* silent — non-essential */ });
    return () => { cancelled = true; };
  }, [user?.id]);

  // Trip endpoints. `start === null` means "use current GPS location".
  const [start, setStart] = useState<TripPoint | null>(null);
  const [destination, setDestination] = useState<TripPoint | null>(null);
  /** Intermediate stops between start and destination, in order. */
  const [stops, setStops] = useState<TripPoint[]>([]);
  const [loading, setLoading] = useState(false);
  // When a WALK/BIKE route is taking a moment, show an honest hint that we're
  // working through the large curated-walk dataset (Morgan). Fires only after
  // a short delay so quick routes never flash it.
  const [slowWalkHint, setSlowWalkHint] = useState(false);
  useEffect(() => {
    if (!loading || (mode !== "walking" && mode !== "biking")) {
      setSlowWalkHint(false);
      return;
    }
    const t = setTimeout(() => setSlowWalkHint(true), 2200);
    return () => clearTimeout(t);
  }, [loading, mode]);
  // PLE-13 (Sep 2026): the first-run intro strip ("Every trip has a nicer
  // way…") and the "You're early" card were removed — first open was
  // stacking too many messages. (This removal was written by Morgan on
  // Sep 25, 2026 as commit 2c74e443 on the ple-13-first-open branch, but the
  // PR that actually got merged for PLE-13 took a different branch and
  // never included it — so the strip silently stayed in main and kept
  // stacking with NoticeBanner until PLE-37 caught it again.)
  /** Lifecycle of the current/most-recent route-generation request. See
   *  `RouteGenStatus` for the meaning of each value. `routeGenError` is
   *  populated only when status is "error". */
  const [routeGenStatus, setRouteGenStatusRaw] = useState<RouteGenStatus>("idle");
  const [routeGenError, setRouteGenError] = useState<string | null>(null);
  const setRouteGenStatus = (s: RouteGenStatus, error: string | null = null) => {
    setRouteGenStatusRaw(s);
    setRouteGenError(error);
  };
  // Monotonic id for in-flight route generation; bumped to cancel.
  const generationIdRef = useRef(0);
  const cancelGeneration = () => {
    generationIdRef.current += 1;
    setLoading(false);
    setRouteGenStatus("cancelled");
    toast.info("Trip search cancelled");
  };
  const [scored, setScored] = useState<ClassifiedRoute[]>([]);
  // After the first cards land, the scenic enrichment often keeps running and
  // refines the cards a few seconds later (intentional: show fast, enrich in
  // the background). Surface a brief "still refining" hint so that update reads
  // as polish rather than a glitch (Morgan: the cards seem to change right
  // after they first appear). NOTE: must be declared AFTER `scored` — the deps
  // array reads scored.length, which would TDZ-crash if placed earlier.
  const [refining, setRefining] = useState(false);
  const refiningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Start-button gate. Blocking Start for the WHOLE refining window (up to
  // 30 s of background enrichment) read as broken — "comparing goes way too
  // long" (Morgan). The real hazard is only the first seconds after cards
  // land, when a candidate can still displace the displayed lineup. So Start
  // is gated for at most START_GATE_MS; the banner stays as long as refining
  // does, but the button comes back.
  const START_GATE_MS = 5000;
  const [startGated, setStartGated] = useState(false);
  const startGateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (startGateTimerRef.current) { clearTimeout(startGateTimerRef.current); startGateTimerRef.current = null; }
    if (refining) {
      setStartGated(true);
      startGateTimerRef.current = setTimeout(() => setStartGated(false), START_GATE_MS);
    } else {
      setStartGated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refining]);
  // Drive the "still finding routes" note off the ACTUAL generation status, not
  // a fixed timer. generateRoutes paints the first cards and reports "partial"
  // (cards shown, but the scenic / calm / nicer-towns alternatives are still
  // resolving in the background), then flips to "ready" once everything has
  // settled. So: show the note exactly while status === "partial". A safety cap
  // hides it after 30s so a cold-Overpass enrichment can never leave it stuck.
  // "Still comparing" ends the moment the calm slot is FILLED (Morgan: "when
  // the routes come back I want the still-comparing thing to be done") — not
  // when the last background enrichment finishes. Once a pleasant/scenic card
  // is on screen the offering is complete in the user's eyes; any later
  // polish (descriptions, scores) lands quietly without an in-flux banner.
  const calmSlotFilled = scored.some(
    (s) => s.classification === "pleasant" || s.classification === "scenic",
  );
  useEffect(() => {
    if (refiningTimerRef.current) { clearTimeout(refiningTimerRef.current); refiningTimerRef.current = null; }
    if (!loading && routeGenStatus === "partial" && scored.length > 0 && !calmSlotFilled) {
      setRefining(true);
      refiningTimerRef.current = setTimeout(() => setRefining(false), 30000);
    } else {
      setRefining(false);
    }
  }, [loading, routeGenStatus, scored.length, calmSlotFilled]);
  // NOT FINAL = NOT SHOWN (Morgan, Aug 6, Boston→New City: the Scenic card
  // upgraded itself twice — I-84 → Newport/Mystic. First ask was a message;
  // upgraded the same hour to "hold ALL of them — if you hold only the
  // scenic they won't know it's an option"). While `improving` is true the
  // PHONE/WEB panel shows the searching card instead of any provisional
  // lineup, the map draws no route lines, and Start reads "Comparing…".
  // The final set lands once, complete. The CAR keeps streaming at 2 — that
  // was its own settled instruction (#61) and its "Found 2 routes — still
  // looking" banner already communicates the in-flux state.
  // 120s safety cap so a wedged enrichment can never hide real routes
  // forever.
  const [improving, setImproving] = useState(false);
  const improvingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (improvingTimerRef.current) { clearTimeout(improvingTimerRef.current); improvingTimerRef.current = null; }
    if (!loading && routeGenStatus === "partial" && scored.length > 0) {
      setImproving(true);
      improvingTimerRef.current = setTimeout(() => setImproving(false), 120000);
    } else {
      setImproving(false);
    }
  }, [loading, routeGenStatus, scored.length]);
  /** AMENDED by Morgan, Aug 10 2026 — the Aug 6 "NOT FINAL = NOT SHOWN" rule
   *  above is retired for the phone.
   *
   *  Her report, testing on the phone: "the first routes that came back looked
   *  like the right routes but 3 more showed later that were better." Under the
   *  Aug 6 rule that should have been impossible, because the panel was meant to
   *  hold everything until the set was final. It didn't hold — late routes fold
   *  in AFTER routeGenStatus flips to `ready`, by which point `improving` is
   *  already false and nothing is holding anything. So the rule was quietly not
   *  in force, and the experience was the worst of both: a lineup that looked
   *  settled and then rearranged itself, with nothing on screen admitting it.
   *
   *  Asked which she wanted — fix the hold, or show early and say so — she chose
   *  to show early with a settling message. That's a deliberate amendment, not
   *  me overriding a settled instruction (rule 11): faster first paint, and the
   *  honesty moves from hiding the churn to naming it.
   *
   *  The searching card still covers the case where NO calm option exists yet,
   *  because an empty pleasant slot reads as "there isn't one" rather than
   *  "still looking" — that part of the Aug 6 reasoning stands. */
  const cardsHeld = false;
  // Live-traffic ETAs per route classification — used to demote routes whose
  // optimistic "fastest" label evaporates in real-world congestion and to
  // surface a "+X min traffic" pill on the cards.
  // Live-traffic ETAs are computed against the DISPLAYED list further down
  // (not just `scored`), so the Google carrier card also gets adjusted. The
  // actual `useTrafficEtas` hook call lives after `displayedScored` is
  // declared — see `trafficEtas` further down the component.

  // Track whether the user has manually changed their selection on this
  // trip. If they haven't, we keep auto-syncing the selected card to the
  // recommended route. As soon as they tap a different card, we stop
  // overriding their choice. Real-world ride note: "Wander was highlighted
  // when I started but Adventure was on top" — the highlighted card needs
  // to match the recommended one until the user says otherwise.
  const userTouchedSelectionRef = useRef(false);
  // (Morgan) The pipeline may pick the selected card only ONCE per trip — at
  // first paint. Later passes (the enrichment refresh that folds in detours)
  // must NOT re-pick, or the highlighted card visibly reverts to Fast when the
  // detours arrive. Reset to false whenever a new trip / mode starts.
  const pipelinePickedRef = useRef(false);

  // Auto-sync selected card to the recommended class whenever the route list
  // changes — until the user manually overrides. Uses least-highway as the
  // primary signal (the most useful proxy for "back-roads alternative"); the
  // FOR YOU badge logic further down also factors in live traffic, so the
  // two may briefly disagree in heavy-traffic edge cases but reconverge
  // once the user taps the recommended card. This intentionally avoids
  // referencing `trafficEtas` (which is declared later in the component)
  // to prevent a temporal-dead-zone crash.
  useEffect(() => {
    if (userTouchedSelectionRef.current) return;
    if (scored.length === 0) return;
    // Prefer the route the classifier labeled PLEASANT (the curated/back-roads
    // pick) for the hero/selected card — it's the product's whole point. A
    // faster, blander route can post a lower highway % yet not be the pleasant
    // route; recommending by CLASS first (then least-highway as a tiebreak)
    // keeps a plain route from sitting in the hero slot while the great-roads
    // route is demoted. (Morgan: "we should be advertising pleasant.")
    // Scenic is the deliberate "long way" — an hour-plus detour by design. It
    // must NEVER become the auto-selected hero on the everyday presets, or the
    // background enrichment (which discovers the scenic corridor a few seconds
    // in) silently swaps a sensible short pick for the long route. So: hero the
    // Pleasant pick if there is one, otherwise the Fast route — never Scenic.
    // The only exception is the explicit "scenic" detour tolerance, where the
    // long way IS what the user asked for.
    const preferScenic = profile?.detourTolerance === "scenic";
    const rankCls = (r: typeof scored[number]) => {
      if (r.classification === "pleasant") return 0;
      if (preferScenic) return r.classification === "scenic" ? 1 : 2;
      return r.classification === "fastest" ? 1 : 2;
    };
    const byPref = [...scored].sort((a, b) => {
      const ra = rankCls(a), rb = rankCls(b);
      if (ra !== rb) return ra - rb;
      return (a.features?.highwayPct ?? 100) - (b.features?.highwayPct ?? 100);
    });
    const targetCls = byPref[0]?.classification ?? scored[0].classification;
    if (targetCls && targetCls !== selectedClass) {
      setSelectedClass(targetCls as typeof selectedClass);
    }
  }, [scored]); // eslint-disable-line react-hooks/exhaustive-deps
  // ─── Offline cache ────────────────────────────────────────────────
  // Mirror successful trips to localStorage and restore them on cold-load
  // when the device is offline. The snapshot is small (start/dest labels
  // + scored ClassifiedRoute[]) and capped at 7 days.
  const isOnline = useOnlineStatus();
  // Mobile detection — used to actually unmount header buttons on phone width
  // (Tailwind's `hidden` class wasn't reliably preventing render in this
  // build, and the user was seeing ghost buttons behind Sign in / Balanced).
  const isMobile = useIsMobile();
  const [offlineRestored, setOfflineRestored] = useState(false);
  const [offlineSnapshot, setOfflineSnapshot] = useState<OfflineTripSnapshot | null>(null);
  const [offlinePanelDismissed, setOfflinePanelDismissed] = useState(false);
  useEffect(() => {
    // Cold-load restore: only when we have nothing to show AND we're offline.
    if (offlineRestored) return;
    if (scored.length > 0) return;
    if (isOnline) return;
    const snap = loadOfflineTrip();
    if (!snap || snap.scored.length === 0) return;
    setScored(snap.scored);
    setOfflineRestored(true);
    setOfflineSnapshot(snap);
    setOfflinePanelDismissed(false);
    toast.info("Showing your last saved route", {
      description: `You're offline · ${offlineTripAgeLabel(snap)}.`,
      duration: 5000,
    });
  }, [isOnline, scored.length, offlineRestored]);

  /** Leave cached view: clears the snapshot + scored list so the user can
   *  search live again. The cache itself is preserved (so they can fall back
   *  if they go offline again). */
  const switchToLiveRoutes = () => {
    setScored([]);
    setOfflineRestored(false);
    setOfflineSnapshot(null);
    setOfflinePanelDismissed(false);
    toast.success("Live routing restored", {
      description: "Plan a fresh trip — cached routes are still saved for later.",
      duration: 3500,
    });
  };
  // Warms nominations + road-segment caches when the route set changes, and
  // bumps `segmentRev` whenever the segment cache emits a change event.
  const segmentRev = useSegmentPrefetch(scored);
  /** Pleasant score of the *fastest* raw route in the current set. We hold
   *  it separately because classifyRoutes may collapse "fastest" into the
   *  "pleasant" card, but we still need the original baseline to compute
   *  "+X% more pleasant than fastest" honestly on the cards we surface. */
  const [fastestPleasantBaseline, setFastestPleasantBaseline] = useState<number | undefined>();
  const [selectedClass, setSelectedClass] = useState<"fastest" | "scenic" | "pleasant">("pleasant");
  // PLE-37: how tall the (rare) NoticeBanner is right now, so the full-bleed
  // map and the logo/search header — both absolutely positioned to the top
  // of the screen — can be nudged down by exactly that much instead of
  // sitting under it. 0 whenever there's no notice, i.e. almost always.
  const [noticeBannerHeight, setNoticeBannerHeight] = useState(0);
  // Debug overlay toggle. Visiting with ?debug=1 turns it on AND remembers it
  // (localStorage) so it survives entering a trip, which rewrites the URL and
  // would otherwise drop the query param. ?debug=0 turns it back off.
  const [debugOverlayOn] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      const p = new URLSearchParams(window.location.search).get("debug");
      if (p === "1") { localStorage.setItem("pleasant.debug", "1"); return true; }
      if (p === "0") { localStorage.removeItem("pleasant.debug"); return false; }
      return localStorage.getItem("pleasant.debug") === "1";
    } catch { return false; }
  });
  // (#175) Debug diagnostics — populated by generateRoutes after each
  // classification. Rendered by DebugOverlay above the route screen.
  const [routeDiagnostics, setRouteDiagnostics] = useState<RouteDiagnostics | null>(null);
  /** Which route card has its embedded "Why this route?" panel open. */
  const [whyOpenClass, setWhyOpenClass] = useState<"fastest" | "scenic" | "pleasant" | null>(null);
  /** Header menu popover — controlled so the background scrim can track it. */
  const [menuOpen, setMenuOpen] = useState(false);
  // If routes are still settling, any open "Why this route?" panel explains a
  // route that may be seconds from being replaced — close it (and the button
  // hides via onToggleWhy below) until the lineup is final.
  useEffect(() => {
    if (refining) setWhyOpenClass(null);
  }, [refining]);
  /** Detour info to surface in the AI "Why this route?" explanation.
   *  Set when the system auto-routed via a pleasant waypoint, OR when the
   *  user accepted an AI-proposed detour from chat. */
  const [appliedDetour, setAppliedDetour] = useState<{
    summary: string;
    waypoints: { label: string; reason?: string; coord?: LngLat }[];
  } | undefined>();
  /** Google Place IDs (or curated:* ids) of every "Drive by..." chip the
   *  user has tapped on the current trip. Each one corresponds to a stop
   *  in the route, and stays highlighted green so the user can see what's
   *  already added. Tapping a green chip removes it. Distinct from the
   *  generic appliedDetour above (which can also be set by AI chat). */
  const [activeDriveByIds, setActiveDriveByIds] = useState<string[]>([]);

  // PER-CARD DETOURS (Morgan). A detour added while a card is selected belongs
  // to THAT card. Instead of regenerating every route through global stops
  // (which made cards disappear and dropped the pleasant character), we re-route
  // ONLY the selected card's polyline through its own stops and overlay it. The
  // other cards stay untouched; switching cards instantly shows each card's own
  // detours; nothing disappears. Keyed by classification.
  type DetourOverride = {
    picks: { id: string; name: string; center: LngLat }[];
    coords: LngLat[];
    durationS: number;
    distanceM: number;
  };
  const [detourByClass, setDetourByClass] = useState<
    Partial<Record<"fastest" | "scenic" | "pleasant", DetourOverride>>
  >({});

  // Community
  const [tags, setTags] = useState<CommunityTag[]>([]);
  const [showCommunity, setShowCommunity] = useState(false);
  // When the user clicks a row in the signals list, the map flies to that
  // signal and opens its popup. Cleared via a tiny "tick" pattern below so
  // re-clicking the same row re-triggers the focus.
  const [focusedTagId, setFocusedTagId] = useState<string | null>(null);
  const focusTag = (id: string) => {
    setFocusedTagId(null);
    // Defer one tick so the effect re-runs even when the same id is clicked twice.
    requestAnimationFrame(() => setFocusedTagId(id));
  };
  // When on, route scoring weights community pleasantness much more heavily.
  // Source of truth lives in the user's profile (set during onboarding).
  const prioritizeCommunity = profile?.prioritizeCommunity ?? false;
  const [tapPoint, setTapPoint] = useState<LngLat | undefined>();
  // Reserved for the future "tap to pick a spot" flow that the contribute
  // sheet may trigger. Kept simple now that the standalone TagButton is gone.
  const [pickingOnMap, setPickingOnMap] = useState(false);

  // UI overlays
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [tripEndFeedbackOpen, setTripEndFeedbackOpen] = useState(false);
  // Post-drive recap (#63): captured the moment NavigationView fires
  // onTripEnd with arrived=true. Survives state churn so the recap renders
  // a stable snapshot even if the user starts a new search behind it.
  const [recapOpen, setRecapOpen] = useState(false);
  const [recapSnapshot, setRecapSnapshot] = useState<TripRecapSnapshot | null>(null);
  // Trip journal (#68) — opens over the Settings sheet, shows past drives.
  const [journalOpen, setJournalOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  // The CarPlay card waits until the person has driven (PLE-13) — see
  // utils/carplayCardGate. Seeded from storage so a CarPlay connection on a
  // previous run counts too.
  const [carplayCardEligible, setCarplayCardEligible] = useState(isCarplayCardEligible);

  // ONE-TIME SIGN-IN INVITATION (Morgan, Aug 2). 581 of 701 people who route
  // are signed out — unreachable by email, and their tags can't persist.
  // Morgan first asked for a sign-in WALL; scaled back on purpose to an
  // invitation, because a login screen before the first route is how nav apps
  // earn "just let me use it" one-star reviews.
  //
  //   · fires on the SECOND search, never the first — the first impression
  //     stays entirely about routes;
  //   · waits 2.5s after cards land so it never interrupts reading them;
  //   · once EVER (localStorage) — declining is an answer, and re-asking
  //     converts a no into an uninstall. Tagging still offers sign-in
  //     contextually via requireAuth, so the door stays open.
  //
  // MUST stay below the navOpen/routeGenStatus/destination declarations —
  // an earlier placement above them took down the entire site (TDZ, Aug 2).
  const SIGNIN_NUDGE_KEY = "pleasant.signinNudge.v1";
  const searchesThisInstallRef = useRef(0);
  useEffect(() => {
    if (routeGenStatus !== "ready" || !destination) return;
    searchesThisInstallRef.current += 1;
    if (user) return;
    if (searchesThisInstallRef.current < 2) return;
    try {
      if (window.localStorage.getItem(SIGNIN_NUDGE_KEY)) return;
      window.localStorage.setItem(SIGNIN_NUDGE_KEY, String(Date.now()));
    } catch { return; /* private mode: skip rather than nag every visit */ }
    const t = setTimeout(() => {
      // Re-check at fire time: they may have signed in, or started
      // navigating — never interrupt an active drive with an account ask.
      if (!user && !navOpen) {
        setAuthPrompt({
          reason: "Sign in to get updates as Pleasant improves — and to tag the roads you love so it learns from you.",
          resume: () => {},
        });
      }
    }, 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeGenStatus, destination?.name]);
  // Pleasant Monthly (Morgan: "$1.99 a month … after 4 drives").
  const [paywallOpen, setPaywallOpen] = useState(false);
  const pendingStartFromCarRef = useRef(false);
  /** The single gate every navigation start passes through: free drives
   *  remaining → count it and go; otherwise the paywall (and, from the
   *  car, a spoken pointer to the phone — Apple forbids selling on the
   *  car screen). Fails open on any billing hiccup. */
  const guardedStartNav = async (fromCar: boolean) => {
    // Fate log (rule 17, phantom-restart hunt): every nav start names its
    // trigger. A "started a route by itself" report + this line = the
    // culprit, no guessing. The stack's caller is in the trace above it.
    console.log(
      `[nav] start requested fromCar=${fromCar} dest="${destination?.name ?? destination?.place ?? "?"}"`,
      new Error("trigger trace").stack?.split("\n").slice(1, 4).join(" <- ") ?? "",
    );
    // WHICH ROUTE, EXACTLY (rule 19 — measure before fixing).
    //
    // Aug 10, live CarPlay: the card list said pleasant/valhalla-balanced/236m
    // and scenic/mapbox-pleasant/529m, the car was labelled "Pleasant route",
    // and updateProgress reported 389km in 31,804s — 44 km/h, which is the
    // SCENIC route's numbers. So the thing navigated and the thing labelled
    // disagreed, and every explanation I offered for it that night was a guess.
    //
    // `selectedClass` is a STRING, and selectedRoute re-resolves from it on
    // every render (see the declaration ~line 3333). While enrichment is still
    // reshuffling which route holds a slot — the same run logged
    // `[ratchet] scenic swap seen` and `[scored-write] pleasant none →
    // valhalla-balanced` — what you tapped and what you get can drift apart
    // without anything saying so.
    //
    // This prints the actual object being handed over. One drive settles it.
    {
      const sel = selectedRoute;
      const all = displayedScoredWithDetours ?? [];
      console.log(
        `[nav] handing over: selectedClass=${selectedClass} → ` +
          (sel
            ? `${sel.classification}/${getRouteSource(sel.route)}/` +
              `${Math.round(sel.route.duration / 60)}m/${(sel.route.distance / 1609.34).toFixed(1)}mi/` +
              // [nav-trace] BOUNDARY 2: exact seconds + geometry id so this
              // line joins up with the card-selection and NavigationView
              // traces (id is geometry-only, survives duration rewrites).
              `${routeEtaFragment(sel.route, getRouteSource(sel.route))}`
            : "NOTHING RESOLVED") +
          `  |  cards: ${all
            .map(
              (c) =>
                `${c.classification}/${getRouteSource(c.route)}/${Math.round(c.route.duration / 60)}m`,
            )
            .join(" ")}`,
      );
    }
    try {
      const { mayStartDrive, recordDriveStart } = await import("@/lib/pleasantPlus");
      if (await mayStartDrive()) {
        recordDriveStart();
        setNavOpen(true);
        return;
      }
      pendingStartFromCarRef.current = fromCar;
      setPaywallOpen(true);
      if (fromCar) {
        const { carplaySpeak } = await import("@/native/carplayBridge");
        carplaySpeak(
          "Your four free drives are used. Subscribe on your phone to keep driving pleasant.",
          1.0,
        );
      }
    } catch {
      setNavOpen(true); // fail open — never strand a driver
    }
  };
  const guardedStartNavRef = useRef(guardedStartNav);
  guardedStartNavRef.current = guardedStartNav;
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Community pins (tips + photo pins) on the map are OFF by default
  // (Morgan: "photos tagged is toggled off unless you toggle it on in
  // settings") — they read as clutter to people who haven't opted into the
  // community layer. Persisted; the Settings row below flips it.
  const [showCommunityPins, setShowCommunityPins] = useState<boolean>(() => {
    try { return localStorage.getItem("pleasant.showCommunityPins") === "1"; } catch { return false; }
  });
  // The Settings sheet is now drill-down: null = the main view (just the
  // Detour-tolerance picker + a list of rows), or one of these subpages
  // when the user taps into a row. Real-drive note (#5-adjacent): the
  // first screen should show the one choice that matters most; the rest
  // is "click on once you're in."
  const [settingsSubPage, setSettingsSubPage] =
    useState<"pleasantness" | "voice" | "narration" | "locale" | "units" | "nudge" | null>(null);
  const [signInDismissed, setSignInDismissed] = useState(() => wasSignInSkipped());
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Three-state route panel: minimized (small bar), default (45vh),
  // maximized (full screen). Auto-expands to default when a new trip starts.
  const [panelState, setPanelState] = useState<"min" | "default" | "max">("default");
  const panelMinimized = panelState === "min";

  // (#196) Detour list is collapsed by default. Morgan: "the bottom is
  // too hectic - make it so that you can expand to see the detours." The
  // chip list (Rockefeller State Park, Scenic Hudson, ...) is useful when
  // the user wants to add a stop, but visually overwhelming on every
  // route screen. User taps "Detours" header to expand.
  const [detoursExpanded, setDetoursExpanded] = useState(false);
  // Scroll the Detours panel into view when it opens — on a phone the panel
  // sits below the route cards, so without this a tap on "Detours" looks like
  // "nothing happened" (Morgan).
  const detourPanelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!detoursExpanded) return;
    const id = window.setTimeout(() => {
      detourPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
    return () => window.clearTimeout(id);
  }, [detoursExpanded]);

  // Save-place dialog: either creating from `destination` or editing an
  // existing place. Triggered by the floating pill above the destination pin
  // and by the "Edit" affordance on the saved-places chip row.
  const [savePlaceState, setSavePlaceState] = useState<
    | { mode: "create"; point?: TripPoint }
    | { mode: "edit"; place: SavedPlace }
    | null
  >(null);
  // Bumped whenever places change so the floating pill re-reads the label.
  const [savedPlacesVersion, setSavedPlacesVersion] = useState(0);
  useEffect(() => {
    const onChange = () => setSavedPlacesVersion((v) => v + 1);
    window.addEventListener("saved-places-changed", onChange);
    return () => window.removeEventListener("saved-places-changed", onChange);
  }, []);

  // Auto-locate is handled inside useUserLocation (with permission-aware
  // pre-prompt on iOS). No legacy effect needed here.

  // ── Incoming start point from /explore ──
  // When the user clicks a "Best places to be right now" card, Explore
  // navigates here with state `{ exploreStart, mode }`. We set that place
  // as the start point on the map (no destination), and recenter there.
  const navLocation = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const state = navLocation.state as
      | { exploreStart?: { name: string; place: string; coords: LngLat }; mode?: TravelMode }
      | null;
    if (!state?.exploreStart) return;
    const startPoint: TripPoint = {
      name: state.exploreStart.name,
      place: state.exploreStart.place,
      coords: state.exploreStart.coords,
    };
    if (state.mode) setMode(state.mode);
    setStart(startPoint);
    setDestination(null);
    setStops([]);
    setPanelState("default");
    // Clear router state so a back-forward nav doesn't re-trigger.
    navigate(navLocation.pathname, { replace: true, state: null });
  }, [navLocation, navigate]);

  // The auto-resume-after-reload was retired. Real-drive feedback: "don't
  // keep the last place still there — but remember the last few entries
  // as options." Auto-loading the last trip on cold open felt intrusive;
  // recent destinations are surfaced as a pickable list (recentSearches.ts
  // / TripSearch) instead. The active-trip marker functions are left as
  // harmless no-ops; the offline-snapshot restore (offline-only) is kept.

  /**
   * The current trip as a relative /app link, in the same format the shared
   * trip parser reads. Used to return the user to the trip they were looking
   * at after a detour to sign-in, which otherwise drops them on a blank map.
   */
  // Trip-naming dialog. Replaces window.prompt, which is a native modal and
  // froze the WebView on every save (Aug 30).
  const [nameTripOpen, setNameTripOpen] = useState(false);
  const [nameTripDefault, setNameTripDefault] = useState("");

  const tripReturnPath = (): string =>
    destination
      ? buildTripPath({ destination, start, stops, mode })
      : window.location.pathname;

  // ── Incoming shared trip from a friend's link ──
  // Format: /?start=lng,lat|name&end=lng,lat|name&stops=...&mode=walking|biking|driving
  // Parsed once on mount; URL is then cleaned so back/forward doesn't replay.
  const sharedHydrated = useRef(false);
  useEffect(() => {
    if (sharedHydrated.current) return;
    const sp = new URLSearchParams(window.location.search);
    const sharedEnd = parseTripPointParam(sp.get("end"));
    if (!sharedEnd) return;
    sharedHydrated.current = true;
    const sharedStart = parseTripPointParam(sp.get("start"));
    const sharedStops = parseStopsParam(sp.get("stops"));
    const sharedMode = sp.get("mode");
    if (sharedMode === "walking" || sharedMode === "biking" || sharedMode === "driving") {
      setMode(sharedMode);
    }
    if (sharedStart) setStart(sharedStart);
    setDestination(sharedEnd);
    setStops(sharedStops);
    setPanelState("default");
    toast.success(`Trip loaded: ${sharedEnd.name}`);
    // Clean the URL so the share state doesn't keep re-applying.
    window.history.replaceState({}, "", window.location.pathname);
    // Auto-run the real route pipeline if we have an explicit start so
    // shared trips (and the landing "real example" button) show the three
    // scored route lines on the map immediately. We defer one tick so the
    // setStart/setDestination state is observable to downstream refs.
    // Skip auto-run when ?prefill=1 — caller wants the user to tweak the
    // start/destination inputs before generating routes.
    const prefillOnly = sp.get("prefill") === "1";
    if (sharedStart && !prefillOnly) {
      const effectiveMode: TravelMode =
        sharedMode === "walking" || sharedMode === "biking" || sharedMode === "driving"
          ? sharedMode
          : mode;
      setTimeout(() => {
        runRouteRequest({
          startCoords: sharedStart.coords,
          dest: sharedEnd,
          waypoints: sharedStops.map((s) => s.coords),
          modeOverride: effectiveMode,
        });
      }, 0);
    }
  }, []);


  // (Removed) The profile's mode used to override the selector on boot —
  // which is why the app could open on Walk. Drive is now the fixed
  // opening mode; explicit trip restores and shared links still set
  // their own mode.

  // ─── Offline cache write ──────────────────────────────────────────
  // Persist a snapshot of the current trip whenever the route set changes
  // and we have a destination. Skipped when we just restored from cache
  // (avoids overwriting the snapshot with itself on first paint).
  useEffect(() => {
    if (scored.length === 0) return;
    if (offlineRestored) return;
    if (!destination) return;
    saveOfflineTrip({
      savedAt: new Date().toISOString(),
      startLabel: start?.name || start?.place,
      destLabel: destination?.name || destination?.place,
      startCoords: start?.coords,
      destCoords: destination?.coords,
      mode,
      scored,
    });
    // A trip with routes is on screen — mark it active so a mid-drive app
    // reload can restore it instead of dumping the driver to a blank screen.
    markActiveTrip();
    // If this is the SF→Half Moon Bay example, also persist a tiny preview
    // snapshot so the landing page's comparison cards can show real
    // Pleasant scores, ETAs, distances, and reasons next time.
    if (isExampleTrip(start?.coords, destination?.coords)) {
      maybeSaveExamplePreview(scored);
    }
  }, [scored, start, destination, mode, offlineRestored]);

  // Re-score in place when the community-priority toggle flips OR when the
  // road-segment cache changes (e.g. user just rated a street mid-trip).
  // Cheap: reuses already-fetched features + community values, no network.
  // (`segmentRev` is provided by useSegmentPrefetch above.) Live-traffic
  // re-scoring lives in a separate effect further down (#65) because it
  // depends on `trafficEtas`, which can't be referenced this high in the
  // component.
  useEffect(() => {
    setScored((prev) => {
      if (prev.length === 0) return prev;
      const fastestDur = Math.min(...prev.map((r) => r.route.duration));
      const rescored = prev.map((r) => {
        // Recompute community score so a fresh mid-trip rating (which just
        // updated the road-segment cache) shifts this route immediately.
        const recomputedCommunity = (r as any).communityTags
          ? communityScore(r.route, (r as any).communityTags, (r as any).buffer ?? BUFFER_BY_MODE[mode])
          : r.scores.community;
        // NOTE: trafficDelaySec is intentionally NOT applied here. Live
        // traffic re-scoring lives in a separate effect (#65) below the
        // trafficEtas declaration. This early effect is the segment-
        // recomputation path that fires on rating events.
        const next = scoreRoute({
          route: r.route,
          features: r.features,
          fastestDuration: fastestDur,
          prefs: profile?.loves ?? [],
          community: recomputedCommunity,
          prioritizeCommunity,
          toleranceMultiplier: profile
            ? TOLERANCE_MULTIPLIER[profile.detourTolerance]
            : undefined,
          mode,
        });
        // Re-score effect doesn't re-fetch OSM/POIs, so preserve the
        // segment-based fields from the prior scoring pass.
        return {
          ...next,
          dimensions: r.dimensions ?? next.dimensions,
          evidence: r.evidence ?? next.evidence,
          segmented: r.segmented ?? next.segmented,
          scores: { ...next.scores, profileMatch: r.scores.profileMatch ?? next.scores.profileMatch },
          classification: r.classification,
          communityTags: (r as any).communityTags,
          buffer: (r as any).buffer,
        } as typeof r;
      });
      return rescored;
    });
  }, [prioritizeCommunity, segmentRev]); // eslint-disable-line react-hooks/exhaustive-deps

  // Early-exit conditions are computed here but applied AFTER all hooks below,
  // so the hook order stays stable across renders (React throws "Rendered more
  // hooks than during the previous render" if we early-return before hooks).
  const showQuickSignIn = !!profile && !user && !signInDismissed;

  /** Build a detour-meta payload from the user's current stops so the route
   *  explainer mentions them by name. Returns undefined when there are no
   *  named stops (raw coords with no label aren't worth surfacing). */
  const stopsToDetourMeta = (
    tripStops: TripPoint[],
  ): { summary: string; waypoints: { label: string; reason?: string }[] } | undefined => {
    const named = tripStops.filter((s) => s.name && s.name.trim().length > 0);
    if (named.length === 0) return undefined;
    const labels = named.map((s) => s.name);
    const summary =
      named.length === 1
        ? `Routed past ${labels[0]}.`
        : `Routed past ${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}.`;
    return {
      summary,
      waypoints: named.map((s) => ({ label: s.name, reason: "stop you added" })),
    };
  };

  // ---------- Generate routes (optional waypoints for AI-suggested detours) ----------
  // `modeOverride` lets callers (like the mode-switcher buttons) bypass the
  // stale `mode` closure value — `setMode` is async, so on click the new mode
  // hasn't been committed yet when we kick off generate().
  // "Try a different route" state — bumps each time the user asks for a new
  // pleasant pick, and remembers the waypoint coords we've already tried so
  // we never re-suggest them. Reset whenever a new search is started.
  const [tryAgainAttempt, setTryAgainAttempt] = useState(0);
  const triedWaypointsRef = useRef<LngLat[]>([]);
  /** Polylines we've already shown the user as the "pleasant" pick for the
   *  current trip. Each "Try again" click excludes them so the next pick is
   *  geometrically different. */
  const shownPleasantRef = useRef<LngLat[][]>([]);
  /** Set to true when the user taps "Try Scenic" on the highway-heavy
   *  hint. The effect below picks this up *after* the profile state has
   *  committed (so routeGenCtx has the fresh tolerance), then triggers a
   *  fresh route generation. */
  const pendingToleranceRegenRef = useRef<boolean>(false);
  // Bundle every state slice + ref + setter the route-generation pipeline
  // reads from or writes to into a single context object. The hook below
  // turns this into a stable callback that always reads fresh values.
  const routeGenCtx = useMemo(() => ({
    mode,
    profile: profile!,
    prioritizeCommunity,
    generationIdRef,
    triedWaypointsRef,
    shownPleasantRef,
    setLoading,
    setAppliedDetour,
    setFastestPleasantBaseline,
    setScored,
    // (#172) Guard setSelectedClass against the route-generation pipeline
    // overriding a user tap. generateRoutes calls setSelectedClass("pleasant")
    // after every classifyThreeCards finishes — including the enrichment
    // refresh 3-5 sec after the cheap path. Without this guard, the user
    // taps Direct → blue highlight shows → enrichment finishes → selection
    // resets to Pleasant → Direct loses its highlight. Audit Finding #2.
    // Once the user has touched the cards, generateRoutes can no longer
    // override their choice.
    setSelectedClass: (cls: "fastest" | "scenic" | "pleasant") => {
      if (userTouchedSelectionRef.current) return;
      // Only the FIRST pipeline pick of a trip sticks. The enrichment refresh
      // that adds detours runs setSelectedClass again a few seconds later — we
      // ignore it so the card stays on what it was (Morgan: "when you add the
      // detours, the route card should not revert to Fast, stay what it was").
      if (pipelinePickedRef.current) return;
      pipelinePickedRef.current = true;
      setSelectedClass(cls);
    },
    setTags,
    setStatus: setRouteGenStatus,
    setRouteDiagnostics,
  }), [mode, profile, prioritizeCommunity]);
  const runRouteRequest = useRouteRequest(routeGenCtx);

  /** Thin positional-arg wrapper preserving the original `generate(...)`
   *  call shape used throughout this component. Google polyline (#92) is
   *  threaded in automatically from the current Google route when
   *  available, so Pleasant routes can actively avoid Google's path. */
  const generate = (
    startCoords: LngLat,
    dest: TripPoint,
    waypoints: LngLat[] = [],
    detourMeta?: GenerateRoutesArgs["detourMeta"],
    modeOverride?: TravelMode,
    tryAgainOpts?: GenerateRoutesArgs["tryAgainOpts"],
  ) => {
    // OFFLINE (Morgan): two honest paths, neither of them a spinner.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      // OFFLINE CLEARS THE SPINNER (Aug 27 2026). The comment above states the
      // intent and the code did not keep it: `handleTryAgain` calls
      // `setLoading(true)` for immediate feedback before calling `generate`,
      // and BOTH returns below leave without ever calling `setLoading(false)`.
      // `runRouteGeneration` — whose `finally` is the only thing that clears
      // the flag — is never reached on this branch, so the spinner runs until
      // signal returns AND a later search completes. Clear it here, where the
      // decision not to search is actually made.
      setLoading(false);
      // CACHED-ROUTE REPLAY: same destination as the saved snapshot →
      // show that trip NOW, honestly labeled, and refresh live the
      // moment signal returns. Repeat drives keep their fully-verified
      // Pleasant routes at any signal level.
      const snap = loadOfflineTrip();
      if (
        snap && Array.isArray(snap.destCoords) && snap.scored.length > 0 &&
        haversine(snap.destCoords as LngLat, dest.coords) <= 300
      ) {
        setScored(snap.scored);
        setOfflineSnapshot(snap);
        setOfflineRestored(true);
        setOfflinePanelDismissed(false);
        toast.info("Saved route from your last trip", {
          description: `You're offline · ${offlineTripAgeLabel(snap)} — I'll refresh it when signal returns.`,
          duration: 6000,
        });
        void import("@/lib/net").then(({ onBackOnline }) =>
          onBackOnline(() => {
            void generate(startCoords, dest, waypoints, detourMeta, modeOverride, tryAgainOpts);
          }),
        );
        return;
      }
      // No cached trip to replay → SAVE the request and watch for service
      // (Morgan: "searching constantly until there is service while
      // saving the requested trip... then tell the user").
      toast("No signal — trip saved. Watching for service…");
      // …and say it IN the panel, not only in a toast that disappears.
      // `routeGenStatus` starts at "idle" and nothing else moves it off, so
      // without this the card area renders completely empty: no spinner, no
      // cards, no message. That is the blank screen, arrived at from the
      // other direction.
      setRouteGenStatus(
        "error",
        "No signal — your trip is saved. I'll search as soon as service is back.",
      );
      void import("@/lib/net").then(({ onBackOnline }) =>
        onBackOnline(() => {
          toast("There's service now — searching your route.");
          void import("@/native/carplayBridge").then((m) => {
            if (m.isCarplayConnected()) m.carplayAnnounce("There's service now — searching your route.");
          }).catch(() => {});
          void generate(startCoords, dest, waypoints, detourMeta, modeOverride, tryAgainOpts);
        }),
      );
      return;
    }
    // STALE-BASELINE GUARD (Morgan's Jersey City bug): on a new search the
    // baseline route state still holds the PREVIOUS trip's route for a few
    // seconds while the fresh one fetches. Unchecked, that 10-minute
    // leftover joined the new pool and "won" Pleasant against a 65-minute
    // fastest — for a different destination. Only a baseline that actually
    // ENDS at this trip's destination may join.
    const gEnd = googleRoute?.coordinates?.[googleRoute.coordinates.length - 1];
    const baselineFresh = !!gEnd && haversine(gEnd, dest.coords) <= 250;
    return runRouteRequest({
      startCoords, dest, waypoints, detourMeta, modeOverride, tryAgainOpts,
      googleCoords: baselineFresh ? googleRoute?.coordinates : undefined,
      // Full baseline route so it can JOIN THE POOL as a candidate (Morgan,
      // July 17 — the natural fastest must be able to win Pleasant).
      googleRoute: baselineFresh && googleRoute?.coordinates && googleRoute.durationS
        ? { coordinates: googleRoute.coordinates, durationS: googleRoute.durationS, distanceM: googleRoute.distanceM }
        : undefined,
    });
  };

  // --- Pleasant Loop (Morgan's review sweep) ---
  const [loopPickerOpen, setLoopPickerOpen] = useState(false);
  const loopVariantRef = useRef(0);
  /** The active loop request — lets the fill-the-time check re-inflate
   *  the circle once when the router shortchanges the duration. */
  const loopTargetRef = useRef<{
    minutes: number;
    mode: TravelMode;
    origin: LngLat;
    rescaled: boolean;
    dirtyRetried: boolean;
    /** Proportional-correction bookkeeping (Morgan's 60→111 min overshoot). */
    tries?: number;
    scale?: number;
  } | null>(null);
  const startLoop = (minutes: number) => {
    const origin = start?.coords ?? userLocation;
    if (!origin) {
      toast("Waiting for your location — try again in a second.");
      return;
    }
    setLoopPickerOpen(false);
    loopTargetRef.current = { minutes, mode, origin, rescaled: false, dirtyRetried: false };
    // The loop honors the selected mode (Morgan: "you should be able to
    // select walk bike or drive") — the circle sizes itself to the pace.
    const plan = buildLoopPlan(origin, minutes, loopVariantRef.current++, mode);
    setStart({ name: "Current location", place: "Current location", coords: origin });
    setDestination(plan.dest);
    setStops([]);
    generationIdRef.current += 1;
    // Snap waypoints onto mode-legal roads first (Morgan: the private-road
    // walk loop) — walkers keep trails, bikers keep bike paths, drivers
    // get real streets. Fail-open to the geometric points.
    void (async () => {
      const { snapLoopWaypoints } = await import("@/lib/loopSnap");
      const wps = await snapLoopWaypoints(plan.waypoints, mode);
      void generate(origin, plan.dest, wps, plan.detourMeta, mode);
    })();
  };

  /** Car-side loop entry (ref so the [] CarPlay effect never sees a stale
   *  `generate` closure — same pattern as carSubmitRef). */
  const carLoopRef = useRef<((sc: LngLat, minutes: number) => void) | null>(null);
  carLoopRef.current = (sc, minutes) => {
    loopTargetRef.current = { minutes, mode: "driving", origin: sc, rescaled: false, dirtyRetried: false };
    const plan = buildLoopPlan(sc, minutes, loopVariantRef.current++);
    setMode("driving");
    setStart({ name: "Current location", place: "Current location", coords: sc });
    setDestination(plan.dest);
    setStops([]);
    generationIdRef.current += 1;
    void (async () => {
      const { snapLoopWaypoints } = await import("@/lib/loopSnap");
      const wps = await snapLoopWaypoints(plan.waypoints, "driving");
      void generate(sc, plan.dest, wps, plan.detourMeta, "driving");
    })();
  };

  // --- Friction batch (Morgan: "what else am i missing like this") ---
  /** One-tap go-home, shared by the app-icon quick action and the evening
   *  chip. Ref-wrapped so the [] event effect never closes over stale
   *  state (same pattern as carSubmitRef). */
  const goHomeRef = useRef<(() => void) | null>(null);
  goHomeRef.current = () => {
    const home = findHomePlace();
    if (!home) {
      toast("No Home saved yet — search your address and tap the bookmark to label it Home.");
      return;
    }
    const sc = start?.coords ?? userLocation;
    if (!sc) {
      void requestLocation();
      toast("Getting your location — tap again in a second.");
      return;
    }
    setMode("driving");
    const homeDest: TripPoint = { name: home.label, place: home.place, coords: home.coords };
    setDestination(homeDest);
    setStops([]);
    generationIdRef.current += 1;
    void generate(sc, homeDest, [], undefined, "driving");
  };
  useEffect(() => {
    const onGoHome = () => goHomeRef.current?.();
    window.addEventListener("pleasant:go-home", onGoHome);
    return () => window.removeEventListener("pleasant:go-home", onGoHome);
  }, []);
  const [homeChipDismissed, setHomeChipDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(HOME_CHIP_DISMISS_KEY) === "1"; } catch { return true; }
  });

  // Save-Home nudge: users who never save Home never see any of the
  // one-tap magic. Once, after they've taken 2+ trips without one.
  useEffect(() => {
    try {
      if (localStorage.getItem("pleasant.saveHomeNudge.v1") === "done") return;
      if (findHomePlace()) return;
      if (listTrips().length < 2) return;
      localStorage.setItem("pleasant.saveHomeNudge.v1", "done");
      toast("Save your Home for one-tap trips", {
        description: "Home shows up first in every search, on the car screen, and on the app icon.",
        duration: 10000,
        action: {
          label: "Save Home",
          onClick: () => setSavePlaceState({ mode: "create", point: undefined }),
        },
      });
    } catch { /* storage unavailable — skip quietly */ }
    // One-shot on mount by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SAY WHERE TO (Morgan: "initiate a new drive even when we were not
  // using pleasant before while driving"). When the phone senses driving
  // speed with no trip active, a single glanceable mic pill starts the
  // whole flow: speak → top match → routes from the ANTICIPATED position
  // → auto-start the Pleasant way. No keyboard, no card-reading at 45mph.
  const [movingLikely, setMovingLikely] = useState(false);
  const [sayWhereBusy, setSayWhereBusy] = useState(false);
  const autoStartPendingRef = useRef(false);
  useEffect(() => {
    let alive = true;
    const probe = async () => {
      try {
        const { isMovingNow } = await import("@/native/carplayBridge");
        if (isMovingNow()) { if (alive) setMovingLikely(true); return; }
        // A background probe must never be the thing that asks. It used to
        // run requestUserLocation regardless, which REQUESTS permission on
        // native — putting the iOS location dialog over a fresh install's
        // sign-in screen within a second of launch (PLE-13, measured: the
        // bridge log showed this as the only requestPermissions at launch).
        if ((await readGeolocationPermission()) !== "granted") return;
        const r = await requestUserLocation({
          enableHighAccuracy: false, timeoutMs: 4_000, maximumAgeMs: 30_000,
        });
        if (alive) setMovingLikely(typeof r.speed === "number" && r.speed >= 4);
      } catch { /* keep current state */ }
    };
    void probe();
    const iv = setInterval(probe, 60_000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  const sayWhereTo = async () => {
    if (sayWhereBusy) return;
    setSayWhereBusy(true);
    try {
      const { phoneDictation } = await import("@/native/carplayBridge");
      const text = await phoneDictation();
      if (text === null) { toast("Mic unavailable — check Settings → Pleasant → Microphone."); return; }
      if (!text) { toast("Didn't catch that — try again."); return; }
      const origin = start?.coords ?? userLocation;
      if (!origin) { void requestLocation(); toast("Getting your location — try again in a second."); return; }
      const { geocodeAutocomplete, resolveSuggestionCoords, needsResolve } = await import("@/api/routing");
      const suggs = await geocodeAutocomplete(text, origin);
      const top = suggs[0];
      if (!top) { toast(`Couldn't find "${text}" — try again.`); return; }
      let coords = top.center;
      if (needsResolve(top)) coords = (await resolveSuggestionCoords(top)) ?? top.center;
      if (!coords || (coords[0] === 0 && coords[1] === 0)) { toast(`Couldn't find "${text}" — try again.`); return; }
      autoStartPendingRef.current = true;
      toast(`Heading to ${top.name} — starting the pleasant way.`);
      void handleSearchSubmit(origin, { name: top.name, place: top.place, coords }, [], null, "driving");
    } finally {
      setSayWhereBusy(false);
    }
  };

  const handleSearchChange = (next: { start: TripPoint | null; end: TripPoint | null; stops: TripPoint[] }) => {
    setStart(next.start);
    setDestination(next.end);
    // Reorder stops along the trip so they sit in the position that adds
    // the least extra travel — saves the user from manually dragging.
    const startCoord = next.start?.coords ?? userLocation;
    const orderedStops =
      startCoord && next.end
        ? orderStopsAlongRoute(startCoord, next.end.coords, next.stops)
        : next.stops;
    setStops(orderedStops);
  };

  const handleSearchSubmit = async (
    startCoordsIn: LngLat,
    dest: TripPoint,
    tripStops: TripPoint[],
    startPoint?: TripPoint | null,
    /** CarPlay path: the mode state may not have re-rendered into the
     *  generation context yet, so the car passes "driving" explicitly. */
    modeOverride?: TravelMode,
  ) => {
    // COORDINATE SANITY FIRST (Morgan, Aug 6, #66): an unresolved place is
    // [0,0] — routing to Null Island produced "No drivable route between
    // those points" until location was granted (coincidence of timing, not
    // cause). Refuse to route to a non-place and say the honest thing.
    const { isRoutableCoord } = await import("@/utils/validCoords");
    if (!isRoutableCoord(dest?.coords)) {
      console.log(`[search] REFUSED null-island destination "${dest?.name ?? "?"}"`);
      toast.error("Couldn't pin down that place", {
        description: "Try picking it from the suggestion list, or search a nearby address.",
      });
      return;
    }
    // NEW TRIP → drop the previous trip's drawn route immediately (Morgan: "holds
    // onto a previous starting location too long"). Without this, the old route
    // line — e.g. the prior NY origin — stays on the map the whole time the new
    // trip loads, which is seconds on a cross-country route AND includes the
    // up-to-8s fresh-GPS await below. Clearing here makes it vanish at once.
    setScored([]);
    // SAME-DESTINATION RE-PLAN keeps the chosen route type (Morgan: tapping
    // "Current location" mid-trip should "stop and re-do the CHOSEN route
    // type from the current location" — not bounce the driver back to the
    // Pleasant default). Only a genuinely new destination resets the pick.
    const sameDestination =
      destination?.coords && dest?.coords &&
      haversine(destination.coords, dest.coords) < 100;
    if (!sameDestination) setSelectedClass("pleasant");
    // Same-destination re-plan: mark the pipeline pick as already spent so
    // generateRoutes can't override the preserved selection when the new
    // classification lands. New destination: pipeline may pick once again.
    pipelinePickedRef.current = !!sameDestination;
    setAppliedDetour(undefined);
    setDetourByClass({}); // new trip → drop per-card detour overlays
    // Commit the resolved origin to state so the map + Google comparison route
    // from the SAME place the engine does. `startPoint` is the resolved start
    // place, or null when the origin is the user's GPS — TripSearch tells us
    // which, so we no longer guess from a possibly-stale `start`. (Bug: a typed
    // "Carmel" start that hadn't committed fell back to GPS, so the route shot
    // from the user's current location across the country.)
    const isGpsOrigin = !startPoint;
    // Confession (rule 17): this is the line that decides whether the trip
    // leaves from a typed place or from the phone's GPS — the silent version
    // of this decision is exactly what routed "New Buffalo → Mackinaw" from
    // Westchester (Aug 20).
    console.log(
      `[search] origin=${isGpsOrigin ? "GPS" : `"${startPoint.place || startPoint.name}"`} → "${dest.place || dest.name}"`,
    );
    setStart(startPoint ?? null);
    // When the trip starts from "Current location", refine the cached fix —
    // but DON'T make the user wait. The old code forced a brand-new GPS read
    // (maximumAgeMs: 0, up to 8s), which blocked the whole route from loading
    // on every "from here" trip. For PLANNING, a fix a few seconds old is plenty
    // accurate (navigation re-snaps to live GPS on Start anyway), so accept a
    // recent cached fix instantly and only spend a brief moment if there's none.
    let startCoords = startCoordsIn;
    if (isGpsOrigin) {
      let fresh = await requestUserLocation({
        enableHighAccuracy: true,
        timeoutMs: 2_500,
        maximumAgeMs: 15_000,
      });
      // COLD-START PATIENCE (Morgan, Aug 8, Umbria: leaving the house,
      // "could not find my location" on phone AND car). A cold GPS fix
      // through stone walls takes 10-30s; a 2.5s budget guarantees failure
      // at exactly the moment every drive begins. When the quick attempt
      // and the cached position both come up empty, say so honestly and
      // wait properly before giving up.
      if (!fresh.coords && !isRoutableCoord(startCoordsIn)) {
        toast.info("Getting a GPS fix…", {
          description: "First fix after starting up can take a few seconds.",
        });
        console.log("[search] cold GPS — waiting up to 20s for first fix");
        fresh = await requestUserLocation({
          enableHighAccuracy: true,
          timeoutMs: 20_000,
          maximumAgeMs: 600_000,
        });
      }
      if (fresh.coords) {
        startCoords = fresh.coords;
        // ANTICIPATED ORIGIN (Morgan: "it takes a while to load routes and
        // you are moving — anticipate where they will be on the same road
        // in 30-45 seconds"). In motion, the trip starts from where the
        // car WILL be when the cards land, so the route never begins
        // behind the driver. Stationary = unchanged.
        const { anticipateOrigin } = await import("@/native/carplayBridge");
        startCoords = anticipateOrigin(startCoords, {
          speedMps: fresh.speed ?? null,
          headingDeg: fresh.heading ?? null,
        });
      }
    }
    // Same sanity for the origin — a typed start that failed to resolve, or
    // a GPS fallback that never got a fix, must not launch a transatlantic
    // "route" (#66).
    if (!isRoutableCoord(startCoords)) {
      console.log(`[search] REFUSED null-island origin (gps=${isGpsOrigin})`);
      // The CAR must hear the verdict too (Aug 8: the car sat silently in
      // "finding your routes" while only the phone knew GPS had failed).
      void import("@/native/carplayBridge").then((m) => {
        if (m.isCarplayConnected()) {
          m.carplayAnnounce(
            "I couldn't get a GPS fix. Give the phone a clear view of the sky and try again.",
            1.0,
            "No GPS fix — try again with sky view",
          );
        }
      }).catch(() => {});
      toast.error(
        isGpsOrigin
          ? "Couldn't get your location"
          : "Couldn't pin down that starting place",
        {
          description: isGpsOrigin
            ? "Check location access for Pleasant in Settings, or type a starting address."
            : "Try picking it from the suggestion list.",
        },
      );
      return;
    }
    setDestination(dest);
    const orderedStops = orderStopsAlongRoute(startCoords, dest.coords, tripStops);
    setStops(orderedStops);
    // Keep the panel collapsed so the map stays visible while routes load.
    // The user can swipe/tap up to see the route cards.
    setPanelState("default");
    // (Removed centroid warning — too many false positives on real POIs like
    // "Boston Public Library" that don't include a street number in the label.)
    // Remember every endpoint the user actually routes to (destination + any
    // intermediate stops). Skip the start when it's GPS — there's no address
    // to record. The recents list de-dupes by location.
    recordRecentSearch({ name: dest.name, place: dest.place, coords: dest.coords });
    for (const s of orderedStops) {
      if (s.place) recordRecentSearch({ name: s.name, place: s.place, coords: s.coords });
    }
    if (startPoint && startPoint.place) {
      recordRecentSearch({ name: startPoint.name, place: startPoint.place, coords: startPoint.coords });
    }
    // Fresh trip → reset try-again rotation + memory of tried waypoints + shown lines.
    triedWaypointsRef.current = [];
    shownPleasantRef.current = [];
    setTryAgainAttempt(0);
    generate(startCoords, dest, orderedStops.map((s) => s.coords), stopsToDetourMeta(orderedStops), modeOverride);
  };

  // CarPlay needs to run the LATEST submit (fresh closures over googleRoute,
  // profile, mode) from a listener registered once — a ref bridges the gap.
  const carSubmitRef = useRef<typeof handleSearchSubmit | null>(null);
  carSubmitRef.current = handleSearchSubmit;

  /** "Try a different route" — re-runs route generation with a rotated
   *  pleasantness emphasis and excludes any waypoints we've already tried,
   *  so the user gets a meaningfully different pleasant pick each click. */
  const handleTryAgain = () => {
    if (!destination) {
      toast.info("Pick a destination first.");
      return;
    }
    const sc = start?.coords ?? userLocation;
    if (!sc) {
      toast.info("Waiting for your location — try again in a moment.");
      return;
    }
    if (!profile) {
      toast.info("Finishing setup — try again in a moment.");
      return;
    }
    const nextAttempt = tryAgainAttempt + 1;
    setTryAgainAttempt(nextAttempt);
    // Immediate feedback so the click never feels inert, even if the
    // first network roundtrip takes a beat.
    setLoading(true);
    setPanelState("default");
    toast.message("Looking for a different pleasant route…", {
      id: "try-again-toast",
      duration: 2500,
    });
    void generate(
      sc,
      destination,
      stops.map((s) => s.coords),
      stops.length > 0 ? stopsToDetourMeta(stops) : undefined,
      undefined,
      { attempt: nextAttempt, exclude: [...triedWaypointsRef.current] },
    );
  };

  const clearTrip = () => {
    setStart(null);
    setDestination(null);
    setStops([]);
    setScored([]);
    setSelectedClass("pleasant");
    pipelinePickedRef.current = false;
    setActiveDriveByIds([]);
    setAppliedDetour(undefined);
    setDetourByClass({}); // drop per-card detour overlays
    triedWaypointsRef.current = [];
    shownPleasantRef.current = [];
    setTryAgainAttempt(0);
    // A deliberate "back to a new search" — the trip is over, so a later
    // cold load should NOT resurrect it.
    clearActiveTrip();
  };

  const togglePref = (p: Preference) => {
    if (!profile) return;
    const next = profile.loves.includes(p)
      ? profile.loves.filter((x) => x !== p)
      : [...profile.loves, p];
    const updated = saveProfile({
      loves: next,
      mode: profile.mode,
      prioritizeCommunity: profile.prioritizeCommunity,
      pleasantnessProfile: profile.pleasantnessProfile,
      detourTolerance: profile.detourTolerance,
    });
    setProfile(updated);
  };

  const updateProfileField = <K extends keyof UserProfile>(key: K, value: UserProfile[K]) => {
    if (!profile) return;
    const updated = saveProfile({
      loves: profile.loves,
      mode: profile.mode,
      prioritizeCommunity: profile.prioritizeCommunity,
      pleasantnessProfile: profile.pleasantnessProfile,
      detourTolerance: profile.detourTolerance,
      [key]: value,
    } as Omit<UserProfile, "completedAt">);
    setProfile(updated);
  };

  // The fastest card is no longer surfaced; for "+X min" comparisons we use the
  // route the user actually sees as fastest — the one classified "fastest" (the
  // Fast card) — NOT the raw pool minimum. (Morgan) On a long trip the pool can
  // contain a malformed route with an impossibly short duration (e.g. a broken
  // via-injection); taking the pool min made the Scenic card read "+6h 4m more"
  // against a phantom ~5h fastest on a Paris→Nice trip whose real fast route is
  // 8h 20m. Same value as before on normal trips (the fastest card IS the pool
  // min); only differs when a bogus sub-fastest route is lurking in the pool.
  const fastestDuration = scored.length
    ? (scored.find((r) => r.classification === "fastest")?.route.duration ??
       Math.min(...scored.map((r) => r.route.duration)))
    : undefined;
  // Note: `selectedRoute` is resolved AFTER the Google carrier is built
  // (further down) so the Google slot is selectable like any other card.
  // See the `selectedRoute` declaration below the Google comparison block.
  // Mirrors the routing logic: only auto-add "no-highways" when the user
  // opted into it, instead of forcing it on every driving trip.
  const effectiveLoves: Preference[] = profile?.loves ?? [];

  // (Highlights-toast `useEffect` was moved below the `selectedRoute`
  // declaration so it can reference the resolved selection that now
  // includes the always-on Google Maps slot.)

  // Effective start coords (custom start if set, otherwise GPS).
  const startCoords: LngLat | undefined = start?.coords ?? userLocation;

  // Re-run route generation after the user taps "Try Scenic" on the
  // highway-heavy hint. The button updates the tolerance via
  // updateProfileField; routeGenCtx is memoized on `profile`, so the
  // regenerate has to wait until React commits the new state and the
  // context rebuilds. This effect picks up the flag on the next render
  // and fires generate() with the fresh tolerance. Real-drive note (#49).
  useEffect(() => {
    if (!pendingToleranceRegenRef.current) return;
    if (!startCoords || !destination) return;
    pendingToleranceRegenRef.current = false;
    void generate(
      startCoords,
      destination,
      stops.map((s) => s.coords),
      stopsToDetourMeta(stops),
    );
    // generate / stopsToDetourMeta are stable inline closures over fresh
    // refs/state; we only want this to fire when tolerance committed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.detourTolerance]);

  // Google Maps comparison — fetched once per A→B+mode trip via edge fn.
  // Drives the per-card "vs Google" badge AND the dedicated compare panel
  // in trip details. Falls back gracefully when unavailable (no key, etc.).
  // Loop trips have no real destination (start === end) — the Google
  // baseline for A→A is degenerate, and "Fastest" is a meaningless label
  // for a circle (Morgan: "always pleasant/scenic not fastest").
  /** One long-trip notice per trip — keyed on destination + rounded miles. */
  const longTripNoticeRef = useRef("");
  /** Is a CarPlay screen attached right now? Drives the "Send to car" button.
   *  Polled rather than event-driven: the bridge tracks connection natively and
   *  a 2s poll is cheap next to getting a stale button on the trip panel. */
  const [carAttached, setCarAttached] = useState(false);
  /** Bumped by "Send to car" so the push effect re-runs on demand. */
  const [carSendNonce, setCarSendNonce] = useState(0);
  useEffect(() => {
    const tick = () => setCarAttached(isCarplayConnected());
    tick();
    const id = setInterval(tick, 2000);
    return () => clearInterval(id);
  }, []);
  const isLoopTrip = destination?.name === "Pleasant Loop";
  const {
    route: googleRoute,
    loading: googleLoading,
    status: googleStatus,
    retry: retryGoogle,
  } = useGoogleComparison(startCoords, isLoopTrip ? undefined : destination?.coords, mode);
  const googleHwyPct = googleRoute ? highwayPctOf(googleRoute) : null;

  // Build the always-on Google Maps card carrier from `googleRoute`. We
  // inherit features from the engine's "fastest" entry so the card has
  // reasonable scoring chips to render — but the polyline, distance,
  // duration, and highway% are Google's actual values.
  const _engineFastest = scored.find((r) => r.classification === "fastest");
  // DRIVING ONLY. Google's WALK/BIKE durations use a different pace than Mapbox,
  // so substituting Google as the Direct card on a walk made "the fastest way"
  // read SLOWER than the Pleasant route (Morgan: 4.0mi/90m Direct vs 4.3mi/83m
  // Pleasant — a shorter route showing a longer time). On foot/bike, keep the
  // engine's own (Mapbox) fastest so every card's time is on the same clock.
  const googleCarrier = useMemo(
    () => (googleRoute && mode === "driving" ? buildGoogleCarrier(googleRoute, _engineFastest) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [googleRoute, mode, _engineFastest?.route.coordinates.length],
  );

  // Displayed list = [Pleasant pick, ...other engine routes, Google carrier
  // (if ready)]. The Pleasant pick is our hero so it's always at the top;
  // the Google "Fastest route" slot is the canonical baseline so it's
  // always at the bottom. The engine's "fastest" entry is hidden because
  // the Google slot replaces it.
  //
  // Suppress the Google carrier when any Pleasant variant is flagged
  // sameAsFastest — that flag already means the pleasant pick rode the
  // same physical road as the fastest path, so showing a second Google
  // "Direct" card just duplicates the same polyline twice in the list.
  // Real-world ride note: "when a pleasant route is actually the same as
  // the direct route … we don't need two cards."
  //
  // The only other filter here is GEOMETRY: `differentRoadFromGoogle` below
  // drops a variant whose polyline is essentially Google's own road (≥80%
  // bucket overlap), because that is one road wearing two labels (rule 3).
  //
  // (Comment corrected Sep 24, 2026. It used to describe a second filter that
  // also dropped Pleasant variants landing within ±2 min of Google's ETA. That
  // came out on 2026-05-23 in 481fec5a ("Pleasant route shows whenever it
  // differs from Google"), and the clock has not been consulted here since.)
  const displayedScoredRaw = useMemo(() => {
    // (#129) Three-cards-always: render all three category cards from
    // `scored` (Direct, Pleasant, Scenic). The old logic dropped the
    // "fastest" classification because Google's blue dashed reference
    // line stood in for Direct. Under the new model, Direct is its
    // OWN card (the user can tap it) and the blue dashed reference
    // stays as the comparison overlay. The two coexist for now —
    // future cleanup may consolidate them.
    const direct = scored.find((r) => r.classification === "fastest");
    const pleasant = scored.find((r) => r.classification === "pleasant");
    const scenic = scored.find((r) => r.classification === "scenic");

    // DETOUR BYPASS: when the user has threaded the trip through an explicit
    // waypoint (a drive-by detour / added stop), the Google reference route —
    // fetched for the bare start→end with NO waypoint — is no longer a valid
    // yardstick. The substitution + differentRoadFromGoogle filter below would
    // either rebase the (collapsed) Pleasant/Scenic cards onto Google's
    // waypoint-less geometry, or discard the detour route for overlapping
    // Google too much — both of which silently strip the detour back out, so
    // the polyline never bends even though the engine routed through the point.
    // Real-drive bug: "marker appears, line doesn't bend." Surface the engine's
    // own routes (which DO pass through the waypoint), de-duped by geometry.
    if (stops.length > 0) {
      const engineCards = [pleasant, scenic, direct].filter(
        (r): r is ClassifiedRoute => r != null,
      );
      const seen = new Set<number>();
      const unique = engineCards.filter((c) => {
        const key = Math.round(c.route.distance);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return unique.length ? unique : engineCards.slice(0, 1);
    }

    // SF → HALF MOON BAY DEMO HARDWIRE: Pacific Coast Highway is the Pleasant
    // route here. Promote the coastal (westernmost) route to the Pleasant pick
    // and label the inland one(s) "Alternate route" — regardless of the generic
    // scorer. Scoped to this one example trip; normal trips are untouched.
    if (isHmbDemoTrip(startCoords, destination?.coords)) {
      const nonFastest = scored.filter(
        (r) => r.classification !== "fastest" && r.route.coordinates.length > 0,
      );
      if (nonFastest.length > 0) {
        const meanLng = (r: ClassifiedRoute) =>
          r.route.coordinates.reduce((s, c) => s + c[0], 0) / r.route.coordinates.length;
        const coastal = nonFastest.reduce((a, b) => (meanLng(b) < meanLng(a) ? b : a));
        const promoted: ClassifiedRoute = {
          ...coastal,
          classification: "pleasant",
          variantLabel: undefined,
          variantSub: undefined,
          sameAsClass: undefined,
          sameAsFastest: false,
        };
        const others: ClassifiedRoute[] = nonFastest
          .filter((r) => r !== coastal)
          .map((r) => ({
            ...r,
            classification: "scenic" as const,
            variantLabel: "Alternate route",
            variantSub: undefined,
            sameAsClass: undefined,
          }));
        const fastestCard = googleCarrier
          ? { ...googleCarrier, classification: "fastest" as const }
          : direct;
        return [promoted, ...others, ...(fastestCard ? [fastestCard] : [])];
      }
    }

    // REVERTED #184 — removal caused app crashes. Restoring the
    // googleCarrier substitution as a safety. We'll re-attempt the
    // architectural cleanup more carefully later.
    const directDisplay =
      googleCarrier
        ? { ...googleCarrier, classification: "fastest" as const }
        : direct;
    // (#157) When a Pleasant or Adventure card carries sameAsClass, it
    // has collapsed onto another slot in classifyThreeCards. The card's
    // stored `route` still points at the engine's loopy alternative
    // (Bedford Rd often isn't in Mapbox's pool for this trip). The
    // displayed Direct (above) is Google's clean Bedford Rd geometry.
    // Re-base each collapsed card on the displayed target's route data
    // so the "Same as the Fastest/Pleasant route" copy matches the
    // polyline AND the stats. Without this the cards say "Same as the
    // Fastest" but show 6.0mi/19min loopy numbers — incoherent.
    const substituteRoute = (
      card: ClassifiedRoute | undefined,
      target: ClassifiedRoute | undefined,
    ): ClassifiedRoute | undefined => {
      if (!card || !target) return card;
      return {
        ...target,
        classification: card.classification,
        sameAsFastest: card.sameAsFastest,
        sameAsClass: card.sameAsClass,
        sameAsFastestReason: card.sameAsFastestReason,
        scenicSearching: card.scenicSearching,
      };
    };
    const pleasantDisplay = pleasant?.sameAsClass === "fastest"
      ? substituteRoute(pleasant, directDisplay)
      : pleasant;
    const scenicDisplay = scenic?.sameAsClass === "fastest"
      ? substituteRoute(scenic, directDisplay)
      : scenic?.sameAsClass === "pleasant"
        ? substituteRoute(scenic, pleasantDisplay)
        : scenic;
    if (directDisplay || pleasantDisplay || scenicDisplay) {
      // (#159) Hide cards that are pure duplicates. A card with
      // sameAsClass set is, by construction, pointing at another card's
      // route — surfacing both adds noise without information. Morgan:
      // "i also don't want 3 cards if 2 are the same route."
      //
      // Exception: when scenicSearching is true (cheap path, anchors
      // still loading), keep the Adventure card so the user knows we're
      // looking. Once enrichment finishes and Adventure either finds
      // something real or honestly collapses, scenicSearching clears
      // and the dedup rule takes effect.
      const cards = [directDisplay, pleasantDisplay, scenicDisplay].filter(
        (r): r is ClassifiedRoute => r != null,
      );
      return cards.filter((c) => !c.sameAsClass || c.scenicSearching === true);
    }
    // (#187) Null-guard profile. This useMemo runs on every render —
    // including the first render when the user is on Onboarding and
    // profile is still null. Without this guard, `profile.detourTolerance`
    // throws "null is not an object" and the SentryErrorBoundary catches
    // it before the Onboarding screen can render. Bug was latent forever
    // because returning users always have a profile; only fresh installs
    // hit this. When profile is null there's nothing meaningful to
    // display, so just return an empty list.
    if (!profile) return [];
    // Fallback to old logic if classifyThreeCards isn't running yet.
    if (profile.detourTolerance === "fast") {
      if (googleCarrier) return [googleCarrier];
      const engineFastest = scored.find((r) => r.classification === "fastest");
      return engineFastest ? [engineFastest] : scored.slice(0, 1);
    }
    const rest = scored.filter((r) => r.classification !== "fastest");
    // Google's polyline — the yardstick for "does this route run a
    // genuinely DIFFERENT road than Google would give?" Test is GEOMETRY,
    // not time. A nicer road that costs no extra minutes is the win.
    const googleBucketsCarrier = googleCarrier
      ? geometryBuckets(googleCarrier.route.coordinates)
      : null;
    const differentRoadFromGoogle = (r: ClassifiedRoute): boolean => {
      if (!googleBucketsCarrier) return true; // no Google polyline → keep
      const overlap = jaccard(
        geometryBuckets(r.route.coordinates),
        googleBucketsCarrier,
      );
      return overlap < 0.80;
    };
    const filtered = rest.filter(differentRoadFromGoogle);

    // EXCEPTION — when the engine has NOTHING different from Google to
    // offer, surface the engine's fastest route as the lone card,
    // explicitly framed in RouteCard as "the most direct way — no calmer
    // option on this trip" (real-drive direction: "unless there is NO
    // other option, and we make that explicit"). Google's polyline still
    // appears as the blue reference line on the map, just not as a card
    // you can pick.
    if (filtered.length === 0) {
      const engineFastest = scored.find((r) => r.classification === "fastest");
      return engineFastest ? [engineFastest] : [];
    }

    // Otherwise: surface the engine's pleasant + scenic routes, pleasant
    // pick first. Google is NEVER a card — only the blue reference line
    // on the map and the per-card "vs Google" comparison badges.
    // Real-drive direction: "we want to be able to see the google route
    // in the blue optional line — not as a route option."
    const pleasantIdx = filtered.findIndex((r) => r.classification === "pleasant");
    return pleasantIdx > 0
      ? [filtered[pleasantIdx], ...filtered.slice(0, pleasantIdx), ...filtered.slice(pleasantIdx + 1)]
      : filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scored, googleCarrier, stops, startCoords, destination]);

  // (Morgan, naming) Re-label the surfaced routes so the cards read
  // Fastest / Pleasant / Scenic — never "Alternate". The fastest route keeps
  // its identity. Among the remaining (non-fastest) routes we name EXACTLY ONE
  // "Pleasant" — the genuinely nicest one (highest pleasant score) that's still
  // within the Pleasant time budget (+max(20 min, 20%)). Every other non-fastest
  // route becomes "Scenic". Picking a single Pleasant by score (not by raw
  // time) fixes two bugs at once: short trips no longer collapse two same-time
  // routes into "Alternate 1/2", and the Pleasant label lands on the route
  // that actually avoids the highway rather than its fast twin.
  const displayedScored = useMemo(() => {
    // LOOP TRIPS (Morgan): a loop has no destination, so "Fastest" is a
    // meaningless card — only Pleasant/Scenic may show. If the pipeline
    // only produced the fastest threading of the vias, that IS the
    // pleasant loop — relabel it rather than show nothing.
    const loopFilter = (list: typeof displayedScoredRaw) => {
      if (!isLoopTrip) return list;
      // LOOP HYGIENE (Morgan: "destinations along the way that dont make
      // sense") — the loop's shaping waypoints are pass-throughs, not
      // stops. Strip every intermediate "You have arrived" so directions,
      // voice, and the car read as ONE continuous drive; only the true
      // homecoming keeps its arrival.
      const dropMidArrivals = (c: (typeof list)[number]) => {
        const steps = c.route.steps ?? [];
        const lastArrive = steps.reduce(
          (acc, s, i) => (s.maneuverType === "arrive" || /arrived/i.test(s.instruction ?? "") ? i : acc),
          -1,
        );
        // THE REAL SOURCE OF THE STOP BADGES (Morgan, four screenshots):
        // `stopStepIndices` marks leg boundaries — the text strip never
        // touched it, so "72 steps · 3 stops" survived every fix. A loop
        // has NO stops by definition: clear it unconditionally.
        const cleanedRoute = { ...c.route, stopStepIndices: [] as number[] };
        if (lastArrive < 0) return { ...c, route: cleanedRoute };
        const cleaned = steps.filter((s, i) =>
          i === lastArrive || !(s.maneuverType === "arrive" || /arrived/i.test(s.instruction ?? "")),
        );
        return { ...c, route: { ...cleanedRoute, steps: cleaned } };
      };
      // ZERO BUSY ROADS (Morgan: "for loop make it strictly NO highways —
      // zero"): motorways are already excluded at request level; here any
      // loop candidate touching a marked avoid road (the 9A class) loses
      // to any candidate that doesn't. Only if EVERY candidate touches
      // one does the least-dirty survive — a loop must still exist.
      const cleanest = (cards: typeof list) => {
        // Busy-road penalty for a LOOP candidate, tag-independent
        // (Morgan's ½-hour loop rode the 9A expressway via a ramp):
        // avoid-tag contact + any real motorway meters + EVERY ramp/exit
        // step — a circle never needs limited-access, so the loop
        // standard is zero ramps, not two.
        const busy = (c: (typeof list)[number]) => {
          let pen = avoidDrivePenalty(c.route.coordinates);
          // Motorway AND trunk from step classes (9A/117 are trunk).
          let m = c.route.motorwayMeters ?? 0;
          let trunkM = 0;
          for (const s of c.route.steps ?? []) {
            const cl = s.classes ?? [];
            if (cl.includes("motorway") || cl.includes("trunk")) trunkM += s.distance ?? 0;
          }
          m = Math.max(m, trunkM);
          const epic = c.route.epicHighwayMeters ?? 0;
          if (m > 400 && epic < m * 0.7) pen += 100;
          const ramps = (c.route.steps ?? []).filter((s) => {
            const t = (s.instruction ?? "").toLowerCase();
            if (t.includes("roundabout") || t.includes("rotary")) return false;
            return t.includes(" exit") || t.startsWith("merge onto") || t.includes("ramp");
          }).length;
          if (ramps >= 1 && epic <= 0) pen += 50 * ramps;
          return pen;
        };
        // CLOSED LOOPS ONLY (Morgan: "make sure loops are closed loops").
        // A real circle barely retraces itself; the scribbles that come
        // back are out-and-backs wearing a loop's name. Overlap costs
        // heavily so a true circle always outranks a retrace.
        const retraceOf = (c: (typeof list)[number]) => {
          const co = c.route.coordinates ?? [];
          if (co.length < 8) return 0;
          // Sample-grid overlap: how many sampled points revisit a cell
          // already visited earlier in the drive.
          const seen = new Set<string>();
          let repeats = 0, n = 0;
          const stride = Math.max(1, Math.floor(co.length / 300));
          for (let i = 0; i < co.length; i += stride) {
            const k = `${co[i][0].toFixed(3)}:${co[i][1].toFixed(3)}`;
            n++;
            if (seen.has(k)) repeats++;
            else seen.add(k);
          }
          return n > 0 ? repeats / n : 0;
        };
        const scored = cards.map((c) => ({
          c,
          pen: busy(c) + Math.round(retraceOf(c) * 300),
        }));
        const clean = scored.filter((x) => x.pen === 0);
        return (clean.length > 0 ? clean : scored.sort((a, b) => a.pen - b.pen))
          .map((x) => x.c);
      };
      const nf = cleanest(list.filter((c) => c.classification !== "fastest").map(dropMidArrivals));
      if (nf.length > 0) return nf;
      return cleanest(list.slice(0, 1).map(dropMidArrivals)).map((c) => ({
        ...c,
        classification: "pleasant" as ClassifiedRoute["classification"],
      }));
    };
    if (!fastestDuration) return loopFilter(displayedScoredRaw);
    const budget = pleasantBudgetSeconds(fastestDuration);
    // SANITY CAP: a route more than 2× the fastest time is a broken wander, not
    // a real Pleasant/Scenic option — drop it from the cards entirely. This is
    // the guard against the SF→HMB "2h37m for a 49-min trip" monster. A genuine
    // long scenic road trip still fits (it's a modest multiple of an already-
    // long fastest time); only absurd loops exceed 2×.
    // LOOPS ARE EXEMPT. On a loop the "fastest" reference is the degenerate
    // origin→origin hop the router returns — around a minute — so 2× of it is
    // ~two minutes and this filter deletes every real 30/60/90-minute circle,
    // leaving the stub to be relabelled Pleasant. The comment above ("only
    // absurd loops exceed 2×") was written while loops were parked; a
    // requested loop exceeds it by design.
    // WATER ROUTES ARE EXEMPT TO 3× (Morgan, Aug 2 — the eighth and, by
    // count, final mechanism). This display-layer cap silently deleted the
    // Lake Michigan shoreline (2.28×) AFTER scoring had chosen it, which is
    // why the diag line said scenic:coastal-pch while the rendered card said
    // valhalla — engine and screen disagreeing across this exact line. Same
    // 3× tier as scoring.ts; the CLAUDE.md rule-1 amendment covers both.
    const isWaterRoute = (c: typeof displayedScoredRaw[number]) => {
      const src = getRouteSource(c.route);
      return src === "coastal-pch" || src === "scenic-corridor";
    };
    // THE BACKSTOP IS 3× FOR SCENIC (rule 1, verbatim: "Anonymous engine
    // candidates keep scenicCap and the display 3× backstop"). Only water
    // sources were getting it, so an ordinary engine-found long way was held to
    // 2× — a stricter bar than the rule sets, applied by the display alone.
    //
    // Aug 9, current location → Nice: `[sane] dropped scenic/mapbox-pleasant —
    // 916m exceeds 2× the fastest (cap 752m)` while the engine's own line in the
    // same run read `cap3x=1128m`. 916 sits comfortably inside the real
    // backstop; it was only ever failing a limit the display invented. That was
    // the last thing standing between this trip and a second card.
    //
    // Rule 1's spirit is the point: Scenic may be as long as it needs to be.
    // The card shows the honest "+X hrs" and the driver decides. A 3× ceiling
    // still catches the genuinely absurd.
    const saneMultiplier = (c: typeof displayedScoredRaw[number]) =>
      isWaterRoute(c) || c.classification === "scenic" ? 3 : 2;
    const sane = displayedScoredRaw.filter(
      (c) =>
        isLoopTrip ||
        c.classification === "fastest" ||
        c.route.duration <= fastestDuration * saneMultiplier(c),
    );
    // NO SILENT VERDICTS (rule 17). `[slots] raw=2 sane=1` told us a card died
    // here and refused to say which or why — on Umbria → Nice that one line was
    // the difference between two cards and one, and nothing in the log could
    // explain it. Every other decider in this codebase confesses; this one is
    // the display's last gate before the user, so it matters most.
    if (sane.length < displayedScoredRaw.length) {
      const fastestMin = Math.round(fastestDuration / 60);
      for (const c of displayedScoredRaw) {
        if (sane.includes(c)) continue;
        const mult = saneMultiplier(c);
        // eslint-disable-next-line no-console
        console.log(
          `[sane] dropped ${c.classification}/${getRouteSource(c.route)} — ` +
            `${Math.round(c.route.duration / 60)}m exceeds ${mult}× the fastest ` +
            `(${fastestMin}m → cap ${fastestMin * mult}m)`,
        );
      }
    }
    // FAST MEANS FASTEST ON SCREEN TOO (Morgan, Aug 23 — Asheville).
    // The anchor is chosen by duration across the final card set rather than
    // by a `fastest` label decided before the display injected its own direct
    // route. Everything else picks from what is left, exactly as before.
    const displayFastAnchor = pickDisplayFastAnchor(sane);
    {
      const labelled = sane.find((c) => c.classification === "fastest");
      if (displayFastAnchor && labelled && labelled !== displayFastAnchor) {
        // eslint-disable-next-line no-console
        console.log(
          `[fast-display] the card labelled fastest (${getRouteSource(labelled.route) ?? "engine"}/` +
          `${Math.round(labelled.route.duration / 60)}m) is slower than ` +
          `${getRouteSource(displayFastAnchor.route) ?? "engine"}/` +
          `${Math.round(displayFastAnchor.route.duration / 60)}m — the quicker route takes the Fast card.`,
        );
      }
    }
    const nonFastest = sane.filter((c) => c !== displayFastAnchor);
    // A deliberately-discovered "long way" (tagged scenic-corridor — the
    // coastline / along-corridor passes) is ALWAYS the Scenic slot, never
    // Pleasant. It's an hour-plus detour by design, so it must never compete
    // for the Pleasant pick (which would mislabel it "Alternate route").
    const isCorridor = (c: typeof displayedScoredRaw[number]) =>
      getRouteSource(c.route) === "scenic-corridor";
    // The single Pleasant pick: nicest route within the time budget. If
    // nothing is within budget (e.g. only a long cross-country option exists),
    // there's no Pleasant — that route is honestly Scenic.
    // Pleasant eligibility is BUDGET-based, not tag-based. A discovered
    // scenic-corridor route that still fits within the Pleasant time budget
    // (e.g. River Road up the Hudson — calmer than Direct, only modestly
    // longer) IS the Pleasant pick, not a "long way". Only a corridor that
    // blows the budget (the Maine coast at +90 min) stays Scenic-only. Without
    // this, a reasonable calm route gets shoved into Scenic and — if the other
    // alternatives collapsed onto Direct — the user is left with no short
    // Pleasant option at all.
    // A route that rides real HIGHWAYS is NEVER eligible for the Pleasant slot
    // (Morgan: "I do not want a highway route through White Plains labeled
    // pleasant"). We gate on the TRUE limited-access share (motorways/parkways
    // like I-287 or the Sprain Brook Pkwy) — NOT the broad `highwayPct`, which
    // also counts rural primary roads (US-9 up the Hudson) that are genuinely
    // pleasant. So an interstate slog is barred while a back-country primary
    // road still qualifies. If nothing qualifies, there's simply no Pleasant
    // card and the trip shows Direct + Scenic — the correct, acceptable outcome.
    // Gate on TRUE motorway share only. Pleasant candidates are the non-fastest
    // ENGINE routes (Mapbox/Valhalla), which DO populate `motorwayMeters`, so a
    // route riding real interstates/parkways (I-287, Sprain Brook) is barred —
    // while a primary-road corridor (NY-117, US-9) is not. We deliberately do
    // NOT gate on `highwayPct` here: it counts primary roads, which would wrongly
    // exclude exactly the calm corridors we want as Pleasant. (The I-95 fastest
    // route is never a Pleasant candidate anyway — it's the Direct slot.)
    const motorwayPctOf = (c: typeof displayedScoredRaw[number]) =>
      motorwayPct(c.route);
    // The RECOMMENDED card can never be a highway route (Morgan: "it even
    // says mostly on US 9 — we want the pleasant route to be recommended
    // and to not have highways"). Counts motorway AND trunk meters from
    // step classes (US-9's divided stretch is trunk, invisible to
    // motorwayPct) plus the ramp signature — same test the Scenic slot uses.
    // RULE 2 now lives in @/utils/pleasantSlot as a pure function, with tests
    // in pleasantSlotHighwayRule.test.ts. It used to sit inline here at 15%,
    // where no test could reach it — which is how a threshold tuned on a
    // Westchester errand quietly deleted the Pleasant route on every
    // long-distance trip for weeks.
    // Exactly the shipped 13 July bar (15%). On a long trip the mixed route
    // belongs in Scenic, which is where the shipped build put it — see
    // scenicHighwayOK below.
    const isHighwayHeavy = (c: typeof displayedScoredRaw[number]) =>
      isHighwayHeavyRoute(c.route);
    let pleasantPick: typeof displayedScoredRaw[number] | null = null;
    // NAME THE BACK ROADS (Morgan, Aug 1: "we don't want to say no motorways,
    // but name the backroads routes as pleasant and scenic").
    //
    // Google's candidate is built with `motorwayMeters: 0` and an empty
    // `roadClassMeters` — it carries no road data at all, because Google
    // returns no step classes. Every highway rule here measures motorway share
    // from those fields, so Google's actual fastest route (I-287, Sprain
    // Brook, I-95) reads as 0% motorway and walks straight through the gate
    // built to keep highway routes out of Pleasant. Her Hackley log caught it
    // red-handed: `pleasant/src=google-direct/13m/4.5mi`.
    //
    // A route cannot be NAMED pleasant or scenic on data nobody measured. This
    // isn't a motorway ban — a measured route with plenty of motorway can still
    // compete. It only says: to wear the name, be measurable. Google keeps the
    // Direct slot, which is what it honestly is, and the measured back-roads
    // routes take the other two.
    const isUnmeasured = (c: typeof displayedScoredRaw[number]) =>
      Object.keys(c.route.roadClassMeters ?? {}).length === 0 &&
      !(c.route.steps ?? []).some((s) => (s.classes ?? []).length > 0);
    // CONFESSION (rule 17 — Morgan, Aug 23). This loop drops every Pleasant
    // candidate silently, and on the eight-corridor validation it returned null
    // on six of eight long routes while the sane list plainly held a
    // pleasant-classified route. Diagnostic only; no filter changes here.
    const __pleasantReasons: string[] = [];
    // SEE THE CANDIDATES THE ENGINE RANKED (Morgan, Aug 23).
    //
    // `nonFastest` is the seated cards minus the Fast anchor — two routes on a
    // long trip, and by construction the wrong two: the direct route (a highway
    // route at 40+ miles, correctly rejected) and the Scenic long way (over the
    // Pleasant budget, correctly rejected). Meanwhile the engine had ranked 4-5
    // calm, in-budget candidates and simply didn't hand them over.
    //
    // Nothing about the rules changes. Every candidate below still faces the
    // same time cap, the same rule-20 measurement bar and the same
    // isHighwayHeavy test, in the same order, and the winner is still the
    // highest pleasant score. This only widens what gets asked.
    //
    // STALE POOLS ARE WORSE THAN NO POOL (rule 18 — route generation races).
    // The snapshot carries the cards its own run returned; if none of them is
    // in what we are displaying, the pool belongs to a different search and is
    // ignored.
    const __snapshot = pleasantPoolSnapshot();
    const __merge = mergeEnginePoolIntoPleasantCandidates(nonFastest, sane, __snapshot);
    const __poolIsForThisRender = __merge.poolUsed;
    const __poolExtras = __merge.extras as typeof displayedScoredRaw;
    const __pleasantCandidates = __merge.candidates as typeof displayedScoredRaw;
    for (const c of __pleasantCandidates) {
      const who = `${getRouteSource(c.route) ?? "engine"}/${Math.round(c.route.duration / 60)}m`;
      const mPct = motorwayPct(c.route);
      const offMi = ((c.route.distance ?? 0) * (1 - Math.min(100, Math.max(0, mPct)) / 100)) / 1609.34;
      const facts =
        `hwy=${mPct.toFixed(0)}% offMotorway=${offMi.toFixed(1)}mi ` +
        `dur=${Math.round(c.route.duration / 60)}m cap=${Math.round((fastestDuration + budget) / 60)}m ` +
        `pleasantScore=${c.scores?.pleasant ?? "?"}`;
      if (c.route.duration > fastestDuration + budget) {
        __pleasantReasons.push(`${who} REJECTED over-budget — ${facts}`);
        continue;
      }
      if (isUnmeasured(c)) {
        __pleasantReasons.push(`${who} REJECTED unmeasured (rule 20) — ${facts}`);
        continue;
      }
      if (isHighwayHeavy(c)) {
        __pleasantReasons.push(`${who} REJECTED isHighwayHeavy — ${facts}`);
        continue;
      }
      __pleasantReasons.push(`${who} PASSED all filters — ${facts}`);
      if (!pleasantPick || (c.scores?.pleasant ?? 0) > (pleasantPick.scores?.pleasant ?? 0)) {
        pleasantPick = c;
      }
    }
    if (__pleasantReasons.length > 0) {
      // eslint-disable-next-line no-console
      console.log(
        `[pleasant-pick] winner=${pleasantPick ? `${getRouteSource(pleasantPick.route) ?? "engine"}/${Math.round(pleasantPick.route.duration / 60)}m` : "NONE"} ` +
        `from ${__pleasantCandidates.length} candidates ` +
        `(${nonFastest.length} seated + ${__poolExtras.length} from the engine's ranked pool` +
        `${__snapshot && !__poolIsForThisRender ? ", pool ignored as stale" : ""}) || ` +
        __pleasantReasons.join("  ||  "),
      );
    }
    // Pick exactly ONE Scenic too — the most scenic of the remaining routes
    // (highest pleasant score, which favors coastline/parks/greenery). Then
    // return EXACTLY one route per slot (Direct / Pleasant / Scenic) and drop
    // any extras. Duplicate classifications were breaking two things at once:
    // the "Alternate" label, and — worse — the map polyline, since MapView
    // draws one route per classification (routes.find), so a duplicate's line
    // (e.g. the coastal route as a 2nd "scenic") never got drawn.
    // SCENIC = NO HIGHWAYS unless designated epic (Morgan: "this is a NO —
    // no highways on scenic route unless designated epic", same
    // calibration as loops). Motorway-class is hard-banned from the
    // Scenic slot; the single exception is a route whose motorway miles
    // ARE the attraction (PCH-class epic covers ≥70% of them). Up to
    // ~400m of ramp/underpass doesn't count as "taking the highway".
    // Busy arterials aren't motorways — those fall to the avoid tags.
    // SCENIC EXCLUDES MOTORWAY MILES (Morgan, the one rule): a Scenic
    // card may not ride limited-access highways. ~400m allowance so a
    // ramp/overpass crossing does not disqualify an otherwise perfect
    // road; epic highways (PCH-class) are the exception since the
    // highway IS the attraction there.
    // SCENIC: PROPORTIONAL, not absolute (Morgan). A Scenic route may
    // touch a highway — it may not BE a highway route. Under a third of
    // the drive on motorway is fine; a third or more is not. Epic
    // highways (PCH-class) are the attraction, so they're exempt.
    const scenicHighwayOK = (c: typeof displayedScoredRaw[number]) => {
      // SHIPPED_JULY_14: the App Store build has NO highway gate on the Scenic
      // slot (this arrived 30 Jul, b80020d). That is why it could surface
      // "Through Stockbridge, MA" for Toronto and the reservoir route for
      // Hackley — a mixed route was refused by Pleasant at 15% and welcomed
      // by Scenic. With this gate on, it is refused by both and vanishes.
      if (SHIPPED_JULY_14_ROUTING) return true;
      // Highway means EXPRESSWAY-GRADE, which includes parkways: the
      // Taconic is tagged trunk, not motorway, so a motorway-only count
      // read it as 0% highway and put "Runs along Taconic State Parkway"
      // on a Scenic card. Count motorway + trunk meters from the route's
      // own steps, fall back to motorwayMeters when steps lack classes.
      let limited = 0;
      for (const s of c.route.steps ?? []) {
        const cl = s.classes ?? [];
        if (cl.includes("motorway") || cl.includes("trunk")) limited += s.distance ?? 0;
      }
      limited = Math.max(limited, c.route.motorwayMeters ?? 0);
      const share = limited / Math.max(1, c.route.distance || 1);
      // TRIP-SCALED (Morgan, July 31). Her 30 July rule — "no highways on
      // scenic unless epic" — was about a half-hour drive, and it holds there.
      // Applied to New York → Boston it deleted the one route worth offering:
      // 215.8 mi via CT 17 and MA 16, which uses I-84 through the dull middle.
      // The shipped 13 July build had NO Scenic highway gate at all, which is
      // why it could surface that route. So the bar relaxes with distance —
      // strict under 45 min, unbarred past 2.5 hours — and short trips keep
      // the 30 July behaviour exactly.
      if (share * 100 < scenicMotorwayBar(fastestDuration)) return true;
      return (c.route.epicHighwayMeters ?? 0) >= limited * 0.7;
    };
    let scenicPick: typeof displayedScoredRaw[number] | null = null;
    // THE COAST WINS HERE TOO (Morgan, Aug 2 — the ninth and final selector).
    // This memo re-derives the Scenic slot for DISPLAY, and its corridor
    // shortcut only knew the scenic-corridor tag: the diag line said
    // scenic:coastal-pch while the rendered card said valhalla, and this
    // block was the remaining disagreement. Coast first, unconditionally —
    // no scenicHighwayOK gate: on the shore road the highway share IS the
    // attraction (rule 2's epic exemption), and the 3× sanity filter above
    // has already vouched for it.
    const coastalCard = nonFastest.find(
      (c) => getRouteSource(c.route) === "coastal-pch" && c !== pleasantPick,
    ) ?? null;
    // A discovered long-way wins the Scenic slot outright — UNLESS it was just
    // taken as the (within-budget) Pleasant pick, in which case there's no
    // separate long way and Scenic falls to the next most-scenic alternative.
    const corridorPick = nonFastest.find(
      (c) => isCorridor(c) && c !== pleasantPick && scenicHighwayOK(c),
    ) ?? null;
    // A CURATED ROAD THE ENGINE ALREADY CHOSE WINS THE SLOT (Morgan, Aug 8,
    // 1am — the tenth selector, and the reason six engine fixes never reached
    // the screen). The [diag] line on San Jose → Flagstaff said the engine had
    // it right:
    //     fastest:mapbox-direct/672m | pleasant:valhalla-balanced/751m/hwy86
    //     scenic:scenic-road/1302m/hwy27          ← Route 66, genuinely calm
    // and the rendered cards said fastest + scenic/valhalla-balanced. This
    // block re-derives the Scenic slot for DISPLAY and only recognises
    // coastal-pch and scenic-corridor as deliberate, so a curated scenic-road
    // fell through to the scoring loop below, lost, and the middle route got
    // relabelled Scenic — taking the Pleasant card with it.
    //
    // Same precedence the engine now uses (rule 16): a road a HUMAN curated
    // outranks anything assembled. Coast still first.
    const engineCuratedScenic = nonFastest.find(
      (c) =>
        c.classification === "scenic" &&
        c !== pleasantPick &&
        getRouteSource(c.route) === "scenic-road",
    ) ?? null;
    if (coastalCard) {
      scenicPick = coastalCard;
    } else if (engineCuratedScenic) {
      scenicPick = engineCuratedScenic;
    } else if (corridorPick) {
      scenicPick = corridorPick;
    } else {
      for (const c of nonFastest) {
        if (c === pleasantPick) continue;
        if (isUnmeasured(c)) continue;     // unmeasured → can't be named Scenic
        // A DIRECT ENGINE ROUTE IS NEVER SCENIC (Morgan, Aug 9 — Back Bay →
        // New City): [cards] fastest/201m/204.9mi | scenic/src=mapbox-direct/
        // 198m/208.2mi. The Scenic card was one of Mapbox's own direct
        // alternates, and it was FASTER than the card labelled Fastest. These
        // land in nonFastest as ordinary candidates and this loop only ranks by
        // pleasant score, so a second direct route can win the slot and be
        // announced as the scenic way. Rule 16 again: the source says what it
        // is. Better to show two honest cards than a third that lies.
        const src = getRouteSource(c.route);
        if (src === "mapbox-direct" || src === "google-direct") continue;
        if (!scenicHighwayOK(c)) continue; // highway scenic: only when epic
        if (!scenicPick || (c.scores?.pleasant ?? 0) > (scenicPick.scores?.pleasant ?? 0)) {
          scenicPick = c;
        }
      }
    }
    // The Direct/fastest anchor must ALWAYS be present. If the input somehow
    // arrived without a "fastest" entry (collapse/dedup upstream), fall back to
    // the shortest-duration route as Direct — never let the result reduce to a
    // scenic-only card (the "reverted to just one long scenic option" bug).
    let fastestRoute = displayFastAnchor;
    /** Rule 3's one question: is this the same road? Shared by the duplicate
     *  check below and by `identical` further down, so the two can never
     *  disagree again. */
    const identicalRoad = (
      a: typeof displayedScoredRaw[number],
      b: typeof displayedScoredRaw[number],
    ) => routesAreSameRoad(
      { coordinates: a.route.coordinates, distance: a.route.distance },
      { coordinates: b.route.coordinates, distance: b.route.distance },
    );
    // ALTERNATIVE ROUTE (Morgan, Aug 9): "we can make the fast route fast &
    // pleasant and have a scenic route and have an alternate route as well."
    //
    // When the Pleasant slot resolves to the fastest road, the third slot goes
    // empty and the driver sees two cards. It should hold a second long way
    // instead — her rule: "it has to be within a few points of the other
    // scenic card — basically an alternative to scenic but not pleasant." So
    // it is judged on the SCENIC score, not the pleasant one, and it competes
    // with the Scenic card rather than replacing the calm middle option.
    //
    // It has to earn the slot or the Aug 2 rule is back with a new name: every
    // honesty gate the Scenic card passes applies here too — no direct engine
    // route (rule 16), the trip-scaled highway bar, the 60% main-roads bar that
    // drops a dishonest Scenic, and a different road by rule 3 from BOTH the
    // Scenic card and the fastest one. If nothing clears all of that, two honest
    // cards it is.
    const alt = pickAlternativeCard(nonFastest, {
      scenicPick,
      pleasantPick,
      fastestRoute,
      isUnmeasured,
      sourceOf: (c) => getRouteSource(c.route),
      scenicHighwayOK,
      sameRoad: identicalRoad,
    });
    const alternativePick = alt.pick;
    // Confession either way (rule 17) — when no alternative appears, the log
    // says who was close and by how much, so the gap is tuned from real trips
    // rather than from a number picked in the abstract. The stand-down on an
    // unmeasured Scenic score says so in its own words: the real repair there
    // is finishing enrichment (the READY-at-28s problem), not this card.
    if (alt.standDown === "scenic-unmeasured") {
      // eslint-disable-next-line no-console
      console.log(
        "[alt] scenic card has no scenic score yet (enrichment incomplete) — no alternative offered",
      );
    } else if (alt.standDown === null) {
      // eslint-disable-next-line no-console
      console.log(
        `[alt] scenic=${alt.scenicScore} gap<=${ALTERNATIVE_SCENIC_POINT_GAP} ` +
        `considered=[${alt.considered.join(" ")}] → ${alternativePick ? getRouteSource(alternativePick.route) : "none"}`,
      );
    }
    // A scenic pick that turned out to BE the fastest fallback isn't a separate
    // option — don't show it twice. Compared by IDENTITY, not by reference:
    // Briarcliff → Rhinebeck offered valhalla-balanced at 69.7 mi / 83 min
    // against a 70.5 mi / 79 min Direct. A different object, the same road, and
    // four minutes. Reference equality missed it, so it became a Scenic card,
    // and the "same route → the fast card drops" rule below then deleted
    // Fastest — leaving the driver one card, labelled Scenic, that was in fact
    // the fastest route. Calling that Scenic is a lie; the duplicate goes and
    // the honest Fast card stays.
    // NOTE (Aug 23): the [dedupe-trace] block lived here. It answered its
    // question — the display's routesAreSameRoad was right and the engine's
    // bucket-jaccard was wrong on cross-provider twins — and was removed before
    // shipping because it is not cheap: sameRoadDiagnostics walks 60 samples in
    // BOTH directions against the full polyline, for every slot pair, on every
    // render. On a long trip that is millions of distance computations per
    // search for a line nobody reads. `git log` has it if it is ever needed
    // again. The cheap confessions ([fast-display], [pleasant-pick]) stay:
    // those name deciders that can drop a card, which rule 17 requires.
    if (scenicPick && (scenicPick === fastestRoute ||
      (fastestRoute && identicalRoad(scenicPick, fastestRoute)))) {
      scenicPick = null;
    }
    const out: typeof displayedScoredRaw = [];
    // IDENTICAL ROUTE → THE FAST CARD DROPS (Morgan's rule, stated
    // plainly: "if it is the exact same route, the fast route drops").
    // Identical means identical — same distance, same duration, same
    // path. Not "similar", no invented percentage.
    const identical = (a: typeof displayedScoredRaw[number], b: typeof displayedScoredRaw[number]) => {
      // ONE IDENTITY TEST, NOT TWO (Morgan, Briarcliff → Rhinebeck, Aug 9).
      // This one demanded EXACT equality — same rounded metres, same rounded
      // seconds, three matching points. The twin merge further down uses
      // routesAreSameRoad, which is scale-aware. They disagreed, and the
      // disagreement showed the driver ONE card:
      //   [cards] fastest/79m/70.5mi | scenic/valhalla-balanced/83m/69.7mi
      //   [cards] merged identical road: fastest into scenic (Δdur 195s)
      //   PLEASANT CARDS: scenic
      // 69.7 mi against 70.5 mi, four minutes apart: the same road. This test
      // called them different, so it became a Scenic CARD; the merge then
      // correctly called them the same and swallowed Fastest into it. The
      // result was the fastest route wearing a Scenic label, alone.
      //
      // Rule 3 wants one answer to "is this the same road", so both callers
      // now ask the same function.
      if (identicalRoad(a, b)) return true;
      if (Math.round(a.route.distance) !== Math.round(b.route.distance)) return false;
      if (Math.round(a.route.duration) !== Math.round(b.route.duration)) return false;
      const ac = a.route.coordinates ?? [], bc = b.route.coordinates ?? [];
      if (ac.length < 2 || bc.length < 2) return false;
      const same = (p: LngLat, q: LngLat) =>
        Math.abs(p[0] - q[0]) < 1e-5 && Math.abs(p[1] - q[1]) < 1e-5;
      const mid = (co: LngLat[]) => co[Math.floor(co.length / 2)];
      return same(ac[0], bc[0]) && same(ac[ac.length - 1], bc[bc.length - 1]) && same(mid(ac), mid(bc));
    };
    // CONFESSION FIRST (rule 17 — Morgan, Aug 23). These two lines delete the
    // Fast card and they have never said so. `[slots]` prints AFTER them, so
    // the log has been showing `fastest=none` beside a sane list that plainly
    // contains a fastest entry, with nothing naming the deleter. Diagnostic
    // only — no behaviour change here.
    /** Set when the Pleasant slot resolved to the fastest route itself, so the
     *  one surviving card can say "Fast & Pleasant" instead of losing the fact.
     *  Several paths reach that state; all are handled below. Declared here
     *  because the same-road check immediately following is now one of them. */
    let inheritsPleasant = false;
    // KEEP FAST, NOT PLEASANT (Morgan, Aug 23 — Pleasantville, verbatim
    // invariant: "if a valid direct/fastest route exists, the user should never
    // end up with no Fast card").
    //
    // This line used to delete the FAST card whenever it was the same road as
    // the Pleasant pick, and it always chose that direction — never nulling
    // Pleasant. Traced on production Pleasantville:
    //
    //   [fast-dropped] fast=mapbox-direct/11m/4.4mi pleasant=mapbox-direct/11m/4.4mi
    //                  identicalRoad=true fastMeasured=true pleasantMeasured=true
    //
    // Literally the same route, both measured, and the Fast card is the one
    // that dies. Nothing about scenery or rule 20 is involved — I checked,
    // because that was my first guess and it was wrong.
    //
    // One card must go: they are one road, and rule 3 says one road is one
    // card. The question is only WHICH LABEL that card keeps, and the answer
    // already exists in this file — `inheritsPleasant`, the "Fast & Pleasant"
    // presentation Morgan approved on Aug 9 ("this is fine but it should say
    // fastest is also pleasant"). It fired only when the two were the same
    // OBJECT; the same ROAD as two objects fell through to the deletion. Now
    // both routes to that state go through it: Fast keeps its slot, and the
    // fact that it is also the pleasant one is kept rather than deleted.
    if (fastestRoute && pleasantPick && identical(fastestRoute, pleasantPick)) {
      // eslint-disable-next-line no-console
      console.log('[cards] fastest and pleasant are the same road → fastest keeps the card as "Fast & Pleasant"');
      pleasantPick = null;
      inheritsPleasant = true;
    }
    // NOT TOUCHED: the scenic twin of this line still deletes Fast. It has the
    // same shape and, by the invariant above, the same defect — but it has not
    // been reproduced on a real trip, and Morgan asked for the smallest fix.
    // It now confesses, so the next run that hits it will say so.
    if (fastestRoute && scenicPick && identical(fastestRoute, scenicPick)) { fastestRoute = null; }
    if (pleasantPick && scenicPick && identical(pleasantPick, scenicPick)) scenicPick = null;

    // SHOW THE SLOTS BEFORE ANYONE ARGUES ABOUT THEM (Morgan, Aug 9). Two
    // attempts at "the fastest card should say Fast & Pleasant" both missed,
    // because both guessed at which value carried the pleasant identity — the
    // display's pleasantPick (null here, isHighwayHeavy bars it) and then the
    // engine's classification on the fastest route (evidently not "pleasant").
    // Guessing twice is one time too many: print what the slots ACTUALLY hold,
    // and pick the rule from the answer. Diagnostic only — no behaviour here.
    {
      const describe = (c: typeof displayedScoredRaw[number] | null) =>
        c ? `${c.classification}/${getRouteSource(c.route)}/${Math.round(c.route.duration / 60)}m` +
            `/scenic=${c.scores?.scenic ?? "?"}/pleasant=${c.scores?.pleasant ?? "?"}` +
            `${c.sameAsFastest ? "/sameAsFastest" : ""}${c.sameAsClass ? `/sameAsClass=${c.sameAsClass}` : ""}`
          : "none";
      // eslint-disable-next-line no-console
      console.log(
        `[slots] raw=${displayedScoredRaw.length} sane=${sane.length} ` +
        `[${sane.map((c) => `${c.classification}/${getRouteSource(c.route)}`).join(" ")}] ` +
        `→ fastest=${describe(fastestRoute)} · pleasant=${describe(pleasantPick)} · scenic=${describe(scenicPick)}`,
      );
    }
    if (fastestRoute) {
      // THE ENGINE'S VERDICT COUNTS (Morgan, Aug 9: "it just looks like fast
      // not pleasant and fast still on briarcliff to rhinebeck").
      //
      // The branch below only fires when the DISPLAY's own pleasantPick lands
      // on the fastest route — and on this trip pleasantPick is null, because
      // the Pleasant loop bars a 100%-highway route through isHighwayHeavy
      // before it can be picked. So nothing inherited anything and the card
      // stayed plain "Fast", exactly as she saw.
      //
      // But the engine had already answered the question:
      //
      //   [perf] reclassified cards: pleasant
      //
      // One card, classified pleasant, which then fell into the Direct slot
      // through the min-duration fallback above. When the engine names this
      // route the pleasant one, the card says so — the display re-deriving its
      // own opinion is what has been swallowing that fact.
      if (fastestRoute.classification === "pleasant") {
        // eslint-disable-next-line no-console
        console.log('[cards] engine classified the fastest route "pleasant" → Fast & Pleasant');
        inheritsPleasant = true;
      }
      out.push({ ...fastestRoute, classification: "fastest" as ClassifiedRoute["classification"] });
    }
    if (pleasantPick && pleasantPick !== fastestRoute) {
      out.push({ ...pleasantPick, classification: "pleasant" as ClassifiedRoute["classification"], variantLabel: undefined, variantSub: undefined });
    } else if (pleasantPick) {
      // THIS is where Briarcliff → Rhinebeck actually lost its Pleasant card,
      // and it took a confession log to find out. The first attempt at Morgan's
      // "it should say fastest is also pleasant" instrumented the hollow filter
      // below — and on the very next run that log never printed, which proved
      // the card never got that far. It dies here instead, on a bare reference
      // check that pushes nothing and says nothing:
      //
      //   [pleasant] top: mapbox-direct/78m/hwy100%/p76   <- the fastest route
      //   [perf] reclassified cards: pleasant             <- engine: it IS pleasant
      //   [cards] fastest/77m | scenic/scenic-road/143m   <- and then it isn't
      //
      // The engine had already decided this route was the pleasant one. The
      // display agreed so completely that it collapsed them into a single
      // object — and then threw away the label rather than the duplicate.
      // Rule 17: a decider that drops something confesses.
      // eslint-disable-next-line no-console
      console.log('[cards] pleasant slot IS the fastest route → fastest inherits "Fast & Pleasant"');
      inheritsPleasant = true;
    }
    // The freed slot takes the alternative long way, if one earned it. Pushed
    // in the pleasant slot on purpose: round 1 reuses the existing card
    // plumbing (detours, CarPlay, nav) and only changes the NAME, exactly as
    // the twin-merge does for "Fast & Pleasant". A real fourth classification
    // touches every consumer of ClassifiedRoute and can wait until this has
    // been driven.
    // Morgan, Aug 9: "dont do that UNLESS there is a pleasant and fastest
    // route." The alternative fills a slot vacated by a real merge — a fastest
    // card on screen AND a pleasant identity that folded into it. If either is
    // missing there was no third slot to free, and adding a card would be
    // inventing an option rather than recovering one.
    if (
      inheritsPleasant &&
      fastestRoute &&
      alternativePick &&
      !identicalRoad(alternativePick, fastestRoute)
    ) {
      // eslint-disable-next-line no-console
      console.log(`[cards] alternative route fills the freed slot — ${getRouteSource(alternativePick.route)}`);
      out.push({
        ...alternativePick,
        classification: "pleasant" as ClassifiedRoute["classification"],
        variantLabel: "Alternative route",
        variantSub: undefined,
      });
    }
    if (scenicPick) {
      out.push({ ...scenicPick, classification: "scenic" as ClassifiedRoute["classification"], variantLabel: undefined, variantSub: undefined });
    }
    // NO HOLLOW PLEASANT CARD (Morgan, Aug 2: "no pleasant route if it's just
    // fastest and scenic"). When a real Scenic exists and the Pleasant slot
    // holds nothing distinct — collapsed onto the fastest, or within two
    // minutes of it — two honest cards beat three where one is a costume.
    // A genuinely distinct Pleasant (the back-roads middle option) stays.
    // THE DROP IS NOT SILENT, AND IT HANDS OVER ITS NAME (Morgan, Aug 9 —
    // Briarcliff → Rhinebeck: "this is fine but it should say fastest is also
    // pleasant"). The rule above stays: no third card in costume. But losing
    // the card should not lose the FACT. When the Pleasant slot collapsed onto
    // the fastest route, the honest presentation is one card that says so —
    // "Fast & Pleasant" — not two cards with the information deleted.
    //
    // Whether it really collapsed is a question the code can answer rather
    // than guess (Morgan: "can't we program it to make that judgement call").
    // The first two arms below already assert identity. The third only says
    // the durations are close, which is NOT the same claim — a genuinely
    // different back road can finish within two minutes of the highway. So we
    // ask rule 3's one question, routesAreSameRoad, and only inherit the label
    // when the answer is yes.
    //
    // (Comment corrected Sep 24, 2026. It used to end "a near-tie on a different
    // road is still dropped (that is the Aug 2 rule)" — untrue since 6ad6232a on
    // Aug 22 took the clock out of the filter below. A near-tie on a DIFFERENT
    // road is now KEPT and logged, because it is the free win. Only road
    // identity decides anything here.)
    const hasScenicCard = out.some((c) => c.classification === "scenic");
    const fastestCard = out.find((c) => c.classification === "fastest");
    const finalCards = hasScenicCard
      ? out.filter((c) => {
          if (c.classification !== "pleasant") return true;
          // A NEAR-TIE ON A DIFFERENT ROAD IS THE BEST OUTCOME THIS APP HAS
          // (Morgan, Aug 21: "what is this 2 min rule? insane - i never said
          // that"; "it's basicaly almost always pciking the fast route -
          // which is wrong").
          //
          // The old rule dropped ANY pleasant card finishing within 2 minutes
          // of the fastest, and its own log line admitted what it was doing:
          //   "within 2min of fastest, sameRoad=false → no relabel (different road)"
          // — i.e. a genuinely different back road, no slower than the
          // highway, deleted from the results. That is the free win. It is
          // the single best thing we can hand a driver: a nicer way at no
          // cost. We were throwing away exactly those and keeping the ones
          // that cost minutes, which is why Pleasant kept collapsing into the
          // fast route.
          //
          // The ONLY honest reason to drop a pleasant card is that it is not
          // a second route at all — the same road wearing a second label. So
          // the test is road identity, and the clock is not consulted.
          const assertedSame = c.sameAsFastest === true || c.sameAsClass === "fastest";
          const sameRoad =
            assertedSame ||
            (!!fastestCard &&
              routesAreSameRoad(
                { coordinates: fastestCard.route.coordinates, distance: fastestCard.route.distance },
                { coordinates: c.route.coordinates, distance: c.route.distance },
              ));
          if (!sameRoad) {
            // eslint-disable-next-line no-console
            console.log(
              `[cards] KEPT pleasant on a different road (Δ${Math.round(
                (c.route.duration - fastestDuration) / 60,
              )} min vs fastest) — the old rule dropped this`,
            );
            return true;
          }
          inheritsPleasant = true;
          // eslint-disable-next-line no-console
          console.log(
            `[cards] merged pleasant into fastest — same road (${assertedSame ? "asserted" : "geometry"})` +
            ` → fastest inherits 'Fast & Pleasant'`,
          );
          return false;
        })
      : out;
    if (inheritsPleasant) {
      const i = finalCards.findIndex((c) => c.classification === "fastest");
      if (i >= 0) {
        finalCards[i] = { ...finalCards[i], sameAsFastest: true as const, variantLabel: "Fast & Pleasant" };
      }
    }
    // eslint-disable-next-line no-console
    console.log("[cards] " + finalCards.map((c) =>
      `${c.classification}/src=${getRouteSource(c.route)}/${Math.round(c.route.duration / 60)}m/${(c.route.distance / 1609).toFixed(1)}mi${c.variantLabel ? `/label=${c.variantLabel}` : ""}`,
    ).join("  |  "));
    return loopFilter(finalCards.length > 0 ? finalCards : sane);
  }, [displayedScoredRaw, fastestDuration, isLoopTrip]);

  /** Render-fresh mirror for the [] CarPlay effect (directions answers). */
  const displayedScoredRef = useRef<typeof displayedScored | null>(null);
  displayedScoredRef.current = displayedScored;

  // Overlay per-card detours (detourByClass) onto the displayed cards — swap
  // only the affected card's polyline + ETA, leaving the other cards intact.
  const displayedScoredWithDetours = useMemo(() => {
    let list = displayedScored;
    if (Object.keys(detourByClass).length > 0) {
      list = list.map((c) => {
        const ov = detourByClass[c.classification as "fastest" | "scenic" | "pleasant"];
        if (!ov) return c;
        return { ...c, route: { ...c.route, coordinates: ov.coords, duration: ov.durationS, distance: ov.distanceM } };
      });
    }
    // SAME ROAD, ONE CARD (Morgan, Aug 8, Avigliano: "it is acting like
    // these are two different routes ... they are not"). Two engines can
    // route the identical street and disagree on the ETA; rule 3 says an
    // identical route shows once. Geometry decides (routeTwins.ts); the
    // kept card takes the MINIMUM duration of the merged pair — same
    // road, best estimate — and wears both names.
    if (list.length > 1) {
      const rank = (cls: string) => (cls === "pleasant" ? 0 : cls === "scenic" ? 1 : 2);
      const kept: typeof list = [];
      for (const c of [...list].sort((a, b) => rank(a.classification) - rank(b.classification))) {
        const twin = kept.find((k) => routesAreSameRoad(k.route, c.route));
        if (!twin) { kept.push(c); continue; }
        console.log(`[cards] merged identical road: ${c.classification} into ${twin.classification} (Δdur ${Math.round(Math.abs(twin.route.duration - c.route.duration))}s)`);
        const i = kept.indexOf(twin);
        kept[i] = {
          ...twin,
          route: { ...twin.route, duration: Math.min(twin.route.duration, c.route.duration) },
          ...(c.classification === "fastest" || twin.classification === "fastest"
            ? {
                sameAsFastest: true as const,
                variantLabel: twin.classification === "pleasant" || c.classification === "pleasant"
                  ? "Fast & Pleasant"
                  : "Fast & Scenic",
              }
            : {}),
        };
      }
      // Restore the original display order (pleasant/scenic/fastest slots).
      list = list.filter((c) => kept.some((k) => k.classification === c.classification))
        .map((c) => kept.find((k) => k.classification === c.classification)!);
    }
    return list;
  }, [displayedScored, detourByClass]);

  // Re-route the given card through its detour picks and overlay the result
  // (no global regen → other cards untouched, nothing disappears).
  const applyDetourPicks = async (
    cls: "fastest" | "scenic" | "pleasant",
    nextPicks: { id: string; name: string; center: LngLat }[],
  ) => {
    if (!startCoords || !destination) return;
    if (nextPicks.length === 0) {
      setDetourByClass((prev) => { const n = { ...prev }; delete n[cls]; return n; });
      return;
    }
    const ordered = orderStopsAlongRoute(
      startCoords, destination.coords,
      nextPicks.map((p) => ({ name: p.name, place: p.name, coords: p.center })),
    );
    try {
      const routes = await fetchRoutes(startCoords, destination.coords, mode, ordered.map((s) => s.coords), []);
      const r = routes[0];
      if (!r) return;
      setDetourByClass((prev) => ({
        ...prev,
        [cls]: { picks: nextPicks, coords: r.coordinates, durationS: r.duration, distanceM: r.distance },
      }));
    } catch { /* keep prior state on failure */ }
  };
  // AI-proposed detours from the "Why this route?" chat ride the SAME overlay
  // path as hand-picked "drive by" stops - no global regen, other cards
  // untouched. appliedDetour records what was honored so the next explanation
  // can say so by name.
  const applyAiDetour = (
    waypoints: LngLat[],
    summary: string,
    named: { label: string; reason?: string }[],
  ) => {
    const picks = waypoints.map((c, i) => ({
      id: `ai:${named[i]?.label ?? i}:${c[0].toFixed(4)},${c[1].toFixed(4)}`,
      name: named[i]?.label ?? "Suggested stop",
      center: c,
    }));
    setAppliedDetour({
      summary,
      waypoints: named.map((n, i) => ({ ...n, coord: waypoints[i] })),
    });
    const existing = detourByClass[selectedClass]?.picks ?? [];
    const seen = new Set(existing.map((p) => p.id));
    void applyDetourPicks(selectedClass, [...existing, ...picks.filter((p) => !seen.has(p.id))]);
  };
  // Toggle one "drive by" place on the SELECTED card.
  const toggleDetourForSelected = (place: { id: string; name: string; center: LngLat }) => {
    const cls = selectedClass;
    const existing = detourByClass[cls]?.picks ?? [];
    const isOn = existing.some((p) => p.id === place.id);
    void applyDetourPicks(cls, isOn ? existing.filter((p) => p.id !== place.id) : [...existing, place]);
  };
  // Add several places at once to the SELECTED card.
  const addDetoursForSelected = (places: { id: string; name: string; center: LngLat }[]) => {
    const cls = selectedClass;
    const existing = detourByClass[cls]?.picks ?? [];
    const seen = new Set(existing.map((p) => p.id));
    void applyDetourPicks(cls, [...existing, ...places.filter((p) => !seen.has(p.id))]);
  };

  // Demo hardwire (#9): for the SF → Half Moon Bay example trip, the coastal
  // Route 1 — the westernmost path, hugging the Pacific — should be the
  // highlighted pick, not the inland alternate. We detect the example by its
  // endpoints and select the route whose geometry runs furthest west. Only
  // applies until the user taps a card themselves.
  useEffect(() => {
    if (userTouchedSelectionRef.current) return;
    if (!startCoords || !destination?.coords) return;
    const SF: LngLat = [-122.3933, 37.7955];
    const HMB: LngLat = [-122.4286, 37.4636];
    const near = (a: LngLat, b: LngLat) => haversine(a, b) <= 3000;
    if (!(near(startCoords, SF) && near(destination.coords, HMB))) return;
    if (displayedScored.length === 0) return;
    let best = displayedScored[0];
    let bestLng = Infinity;
    for (const r of displayedScored) {
      const c = r.route.coordinates;
      if (!c.length) continue;
      const meanLng = c.reduce((s, p) => s + p[0], 0) / c.length;
      if (meanLng < bestLng) { bestLng = meanLng; best = r; }
    }
    setSelectedClass(best.classification);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedScored, startCoords, destination]);

  // Traffic ETAs for every displayed card — including the Google carrier
  // so the Fastest card shows a real traffic-adjusted time rather than
  // Google's raw optimistic ETA next to traffic-adjusted Pleasant cards.
  // Real-world ride note: "the fastest route still lists the longest time."
  // That mismatch came from Fastest not getting a traffic adjustment.
  const trafficEtas = useTrafficEtas(displayedScored, mode);

  // FILL THE TIME (Morgan: "loops to fill the amount of time requested"):
  // when the routed loop comes back at under ~55% of the asked-for
  // minutes, the router chorded the circle — re-inflate once (1.6×) and
  // regenerate. One retry only; the second answer stands.
  useEffect(() => {
    if (!isLoopTrip || loading || displayedScored.length === 0) return;
    const t = loopTargetRef.current;
    if (!t) return;
    const regen = (scale: number) => {
      const plan = buildLoopPlan(t.origin, t.minutes, loopVariantRef.current++, t.mode, scale);
      void (async () => {
        const { snapLoopWaypoints } = await import("@/lib/loopSnap");
        const wps = await snapLoopWaypoints(plan.waypoints, t.mode);
        generationIdRef.current += 1;
        void generate(t.origin, plan.dest, wps, plan.detourMeta, t.mode);
      })();
    };
    // DIRTY-LOOP RETRY (Morgan's 9A-ramp loop): if the best surviving
    // candidate still rides limited-access, re-roll the circle ONCE with
    // fresh anchors — the next variant usually orbits a different valley.
    if (!t.dirtyRetried) {
      const dirty = displayedScored.every((c) => {
        let m = c.route.motorwayMeters ?? 0;
        for (const s of c.route.steps ?? []) {
          const cl = s.classes ?? [];
          if (cl.includes("motorway") || cl.includes("trunk")) m += s.distance ?? 0;
        }
        const ramp = (c.route.steps ?? []).some((s) => {
          const txt = (s.instruction ?? "").toLowerCase();
          return !txt.includes("roundabout") && (txt.includes(" exit") || txt.includes("ramp"));
        });
        return m > 400 || ramp;
      });
      if (dirty) {
        t.dirtyRetried = true;
        regen(1);
        return;
      }
    }
    // MATCH THE TIME, BOTH WAYS (Morgan: "asked for a 60 minute loop and
    // got 1 hour 51" — my old blind 1.6× inflation overshot as badly as
    // the original undershoot). Correct PROPORTIONALLY toward the target:
    // scale by requested/actual, damped, and accept anything within
    // 25% of the ask. Up to 2 corrections, then the answer stands.
    if (t.rescaled !== true) {
      const bestMins = Math.min(...displayedScored.map((c) => c.route.duration)) / 60;
      const ratio = t.minutes / Math.max(1, bestMins);
      if (ratio < 0.75 || ratio > 1.25) {
        t.tries = (t.tries ?? 0) + 1;
        if (t.tries >= 2) { t.rescaled = true; return; }
        // Damp the correction so we converge instead of oscillating.
        const scale = Math.max(0.35, Math.min(2.2, (t.scale ?? 1) * (0.5 + ratio * 0.5)));
        t.scale = scale;
        regen(scale);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoopTrip, loading, displayedScored]);

  // Auto-start for Say-where-to trips: the moment usable cards exist,
  // select Pleasant and GO — a moving driver shouldn't read a card list.
  useEffect(() => {
    if (!autoStartPendingRef.current) return;
    if (navOpen || !destination || displayedScored.length === 0) return;
    autoStartPendingRef.current = false;
    const cls = displayedScored.some((c) => c.classification === "pleasant")
      ? "pleasant" as const
      : displayedScored[0].classification;
    setSelectedClass(cls);
    // Give the selection one tick to settle into selectedRoute, then GO.
    setTimeout(() => { void guardedStartNavRef.current(false); }, 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedScored, navOpen, destination]);

  // (Morgan) Pleasant and Scenic were showing the SAME time even though they're
  // different-length routes. Cause: fetchTrafficEta re-times a route by sampling
  // a few waypoints (Mapbox caps them) and asking driving-traffic for an ETA. On
  // a long scenic route those waypoints are hundreds of miles apart, so Mapbox
  // returns the FASTEST path between them — a much SHORTER distance and ~highway
  // time — which then overrode the card's real duration, collapsing every card to
  // the same number. Reject any traffic ETA whose distance doesn't ~match the
  // route it's supposed to be timing; those cards fall back to the route's own
  // (accurate) duration, so the times differ correctly again.
  const validTrafficEtas: typeof trafficEtas = useMemo(() => {
    const out: typeof trafficEtas = {};
    // (Morgan: "something is very wrong" — a 10.4 mi walk shown as 1h6m.) The
    // traffic ETA always uses the DRIVING-traffic profile, so applying it to a
    // walk/bike route shows a DRIVING time for a walk (9+ mph). Only use the
    // traffic override when driving; walking/biking keep the route's own duration.
    if (mode !== "driving") return out;
    for (const r of displayedScored) {
      const eta = trafficEtas[r.classification];
      const d = r.route.distance;
      const mismatch =
        !!eta && d > 0 && Math.abs(eta.distanceM - d) / d > 0.15;
      out[r.classification] = eta && !mismatch ? eta : null;
    }
    // ALL CARDS ON ONE CLOCK, OR NONE (Morgan, Aug 9: "there are fastest routes
    // that are listed as longer than pleasant route").
    //
    // The per-route rejection above is right on its own terms, but it is not
    // evenly distributed: a long Scenic route's re-time samples waypoints
    // hundreds of miles apart, Mapbox answers with the fastest path between
    // them, the distance check correctly throws it out — while the short direct
    // route sails through. So the Direct card carried live traffic and the
    // Scenic card did not, and the two were printed side by side as if they
    // meant the same thing:
    //     [pleasant] top: mapbox-direct/375m      ← engine
    //     [cards]    fastest/428m/386.4mi         ← +53 min of traffic
    //     [cards]    scenic/434m/471.6mi          ← no traffic at all
    // 428 vs 434 is a coin flip, and on a worse traffic day the card titled
    // fastest loses to one it is genuinely quicker than.
    //
    // The user is CHOOSING BETWEEN these numbers, so comparability beats
    // per-card precision. A card that is individually more accurate but
    // incomparable to the one beside it is worse than two honest engine times.
    const cards = displayedScored;
    const allTimed =
      cards.length > 0 && cards.every((r) => out[r.classification] != null);
    if (!allTimed) {
      const timed = cards.filter((r) => out[r.classification] != null).length;
      if (timed > 0) {
        // eslint-disable-next-line no-console
        console.log(
          `[traffic] ${timed}/${cards.length} cards had a usable live ETA — ` +
            `dropping all of them so every card is on the engine's clock ` +
            `(mixed clocks made Direct look slower than Scenic)`,
        );
      }
      for (const r of cards) out[r.classification] = null;
    }
    return out;
  }, [trafficEtas, displayedScored]);

  // Live-traffic re-score (#65): when Mapbox returns updated traffic ETAs,
  // re-score each surfaced route with its actual current delay. The
  // Pleasant score that renders on the card now reflects "right now,
  // this route is moving at X" rather than just "this route's free-flow
  // duration." Routes with significant delay also get an evidence chip
  // ("4 min of live traffic on this route") so the user knows why the
  // score dropped, not a silent number change.
  //
  // Lightweight: only re-runs when trafficEtas changes object identity,
  // and reuses the cached features / community / dimensions. No network.
  useEffect(() => {
    setScored((prev) => {
      if (prev.length === 0) return prev;
      const fastestDur = Math.min(...prev.map((r) => r.route.duration));
      let anyChanged = false;
      const rescored = prev.map((r) => {
        const trafficEta = trafficEtas[r.classification];
        const trafficDelaySec = trafficEta
          ? Math.max(0, trafficEta.durationS - r.route.duration)
          : undefined;
        // Segment-level traffic legs (#66) — prefer this over the
        // whole-route delay when available, so the recommender can
        // penalize routes by WHERE the congestion is.
        const trafficLegs = trafficEta?.legs && trafficEta.legs.length > 0
          ? trafficEta.legs
          : undefined;
        const recomputedCommunity = (r as any).communityTags
          ? communityScore(r.route, (r as any).communityTags, (r as any).buffer ?? BUFFER_BY_MODE[mode])
          : r.scores.community;
        const next = scoreRoute({
          route: r.route,
          features: r.features,
          fastestDuration: fastestDur,
          prefs: profile?.loves ?? [],
          community: recomputedCommunity,
          prioritizeCommunity,
          toleranceMultiplier: profile
            ? TOLERANCE_MULTIPLIER[profile.detourTolerance]
            : undefined,
          mode,
          trafficDelaySec,
          trafficLegs,
        });
        if (Math.abs((next.scores.pleasant ?? 0) - (r.scores.pleasant ?? 0)) > 0.5) {
          anyChanged = true;
        }
        return {
          ...next,
          dimensions: r.dimensions ?? next.dimensions,
          evidence: next.evidence ?? r.evidence,
          segmented: r.segmented ?? next.segmented,
          scores: { ...next.scores, profileMatch: r.scores.profileMatch ?? next.scores.profileMatch },
          classification: r.classification,
          communityTags: (r as any).communityTags,
          buffer: (r as any).buffer,
        } as typeof r;
      });
      // Only update state if something actually changed — avoids a
      // re-render loop when traffic ETAs settle.
      return anyChanged ? rescored : prev;
    });
    // We deliberately re-derive everything from `trafficEtas`. The other
    // deps (profile, mode, etc.) are captured at effect run time; the
    // segment cache + community-toggle path has its own effect higher up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trafficEtas]);


  // --- CarPlay: the car chooses too (Morgan's spec, Stage 2) ---
  // The car screen picks a destination → we set the trip exactly like a
  // shared link would → the engine computes as normal (the phone mirrors
  // it live) → when cards are ready we hand them to the dashboard as
  // route choices. One brain, two screens.
  useEffect(() => {
    const onSetDest = (e: Event) => {
      const d = (e as CustomEvent).detail as { name: string; place: string; coords: LngLat };
      if (!d?.coords) return;
      // A request from the CAR is always a drive — Morgan's test had the
      // phone on Walk and the car sat waiting for walking routes.
      setMode("driving");
      setStart(null);
      setStops([]);
      setDestination({ coords: d.coords, name: d.name, place: d.place });
      setPanelState("default");
      // THE FIX for fastest-only cards (Morgan: "are we using the same
      // routing systems as the app?"): setting destination state alone
      // never ran the routing engine — only the phone's search submit
      // does, via handleSearchSubmit → generate(). The car was left with
      // just the standalone fastest-baseline fetch, so Pleasant/Scenic
      // never existed. Now the car's pick presses the same "go" the
      // phone's search bar does — one brain, one pipeline, three cards.
      void (async () => {
        // ORIGIN FIX ORDER (Morgan's pizza-place hang): the PUMP first —
        // its native fixes flow with the phone locked in a pocket, while
        // webview geolocation silently never answers in the background
        // (which left the car on "Finding your pleasant routes…" forever).
        const { carplayLastFix, anticipateOrigin } = await import("@/native/carplayBridge");
        let sc = carplayLastFix();
        if (!sc) {
          const fresh = await requestUserLocation({
            enableHighAccuracy: true,
            timeoutMs: 2_500,
            maximumAgeMs: 15_000,
          }).catch(() => null);
          sc = fresh?.coords ?? null;
        }
        if (!sc) {
          // SAY IT (Morgan: "gets stuck there") — a silent bail left the
          // car on "Searching…" forever. Now the car explains and resets.
          const { carplaySpeak } = await import("@/native/carplayBridge");
          carplaySpeak("I couldn't get a location fix — give it a few seconds and try again.", 1.0);
          return;
        }
        // In motion: start the routes from where the car WILL be (Morgan).
        sc = anticipateOrigin(sc);
        void carSubmitRef.current?.(
          sc,
          { coords: d.coords, name: d.name, place: d.place },
          [],
          null,
          "driving",
        );
      })();
    };
    const onSelectStart = (e: Event) => {
      const cls = (((e as CustomEvent).detail?.classification as string) ?? "pleasant");
      if (cls === "fastest" || cls === "pleasant" || cls === "scenic") {
        setSelectedClass(cls);
      }
      void guardedStartNavRef.current(true);
    };
    // Siri: "Navigate with Pleasant" → spoken text → geocode near the
    // driver → the SAME destination flow as a car pick (full engine).
    const onSiriNavigate = (e: Event) => {
      const q = String((e as CustomEvent).detail?.query ?? "").trim();
      if (!q) return;
      void (async () => {
        try {
          const [{ geocodeAutocomplete, resolveSuggestionCoords }, fix] = await Promise.all([
            import("@/api/routing"),
            requestUserLocation({
              enableHighAccuracy: true,
              timeoutMs: 2_500,
              maximumAgeMs: 15_000,
            }).catch(() => null),
          ]);
          const suggestions = await geocodeAutocomplete(q, fix?.coords ?? undefined);
          const top = suggestions[0];
          if (!top) {
            toast.info(`Couldn't find "${q}" — try the search bar.`);
            return;
          }
          const coords =
            top.center ?? (await resolveSuggestionCoords(top as never).catch(() => null));
          if (!coords) return;
          window.dispatchEvent(new CustomEvent("pleasant:car-set-destination", {
            detail: { name: top.name, place: top.place, coords },
          }));
        } catch { /* geocode is best-effort; the user can type instead */ }
      })();
    };
    // Pleasant Loop from the dashboard: fix first (pump → webview), then
    // the shared loop entry. Same origin discipline as onSetDest.
    const onCarLoop = (e: Event) => {
      const minutes = Number((e as CustomEvent).detail?.minutes ?? 60);
      void (async () => {
        const { carplayLastFix, anticipateOrigin } = await import("@/native/carplayBridge");
        let sc = carplayLastFix();
        if (!sc) {
          const fresh = await requestUserLocation({
            enableHighAccuracy: true,
            timeoutMs: 2_500,
            maximumAgeMs: 15_000,
          }).catch(() => null);
          sc = fresh?.coords ?? null;
        }
        if (!sc) {
          const { carplaySpeak } = await import("@/native/carplayBridge");
          carplaySpeak("I couldn't get a location fix — give it a few seconds and try again.", 1.0);
          return;
        }
        carLoopRef.current?.(anticipateOrigin(sc), minutes);
      })();
    };
    // Directions button on the car's route preview (Morgan: "click
    // details and it tells you all of the directions").
    const onCarDirections = (e: Event) => {
      const cls = String((e as CustomEvent).detail?.classification ?? "pleasant");
      const cards = displayedScoredRef.current ?? [];
      const card = cards.find((c) => c.classification === cls) ?? cards[0];
      if (!card) return;
      // Aug 19 units audit: was miles/feet inline. fmtDistance honours the
      // driver's chosen system, so the car list matches the phone instead of
      // contradicting it (same fix as NavigationView's car directions).
      const fmt = (m: number) => fmtDistance(m);
      const items = (card.route.steps ?? []).map((s) => ({
        instruction: s.instruction ?? "",
        detail: fmt(s.distance ?? 0),
      }));
      void import("@/native/carplayBridge").then((m) => m.carplayPushDirections(items)).catch(() => {});
    };
    window.addEventListener("pleasant:car-set-destination", onSetDest);
    window.addEventListener("pleasant:car-select-start", onSelectStart);
    window.addEventListener("pleasant:siri-navigate", onSiriNavigate);
    window.addEventListener("pleasant:car-loop", onCarLoop);
    window.addEventListener("pleasant:car-directions", onCarDirections);
    return () => {
      window.removeEventListener("pleasant:car-set-destination", onSetDest);
      window.removeEventListener("pleasant:car-select-start", onSelectStart);
      window.removeEventListener("pleasant:siri-navigate", onSiriNavigate);
      window.removeEventListener("pleasant:car-loop", onCarLoop);
      window.removeEventListener("pleasant:car-directions", onCarDirections);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SPARSE-REGION HONESTY (Morgan's final pre-2.2 batch): where no curated
  // great road exists within ~40 km, the magic is thinner — say so once
  // per ~50 km area, framed as an invitation, not an apology.
  useEffect(() => {
    if (!destination || displayedScored.length === 0) return;
    // LOADED ON DEMAND, NOT ON LAUNCH (Aug 20, measured). This check reads
    // ONE field of the great-drives store to decide whether to show a single
    // toast, but importing it at module scope pulled 3.97 MB / 4,810 records
    // into the entry bundle — parsed before first paint, on every visit,
    // for every user, most of whom never reach this branch. The app already
    // loads the same data properly elsewhere, state by state, only for the
    // trip's states (scenicRoads.ts). Nothing else in this file touches the
    // store, so the import moves in here, after cards already exist.
    let cancelled = false;
    void (async () => {
      try {
        const c = destination.coords;
        const cellKey = `pleasant.sparseNote.${Math.round(c[1] * 2)},${Math.round(c[0] * 2)}`;
        if (localStorage.getItem(cellKey) === "done") return;
        const { GREAT_DRIVES } = await import("@/content/greatDrives");
        if (cancelled) return;
        let curatedNearby = false;
        for (const d of GREAT_DRIVES as Array<{ point: [number, number] }>) {
          if (Array.isArray(d.point) && haversine(d.point, c) <= 40_000) {
            curatedNearby = true;
            break;
          }
        }
        if (curatedNearby) return;
        localStorage.setItem(cellKey, "done");
        toast("Pleasant is young around here", {
          description:
            "Fewer hand-picked roads in this area yet — your Gorgeous and Avoid ratings literally build it.",
          duration: 8000,
        });
      } catch { /* purely informational */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, displayedScored.length > 0]);

  // WEAK-SIGNAL HONESTY (Morgan: "starting from a place with bad service"):
  // if generation is still loading after 12s, say why — once per attempt,
  // on the phone AND out loud on the car (drivers can't read toasts).
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      toast("This is taking a while — make sure you have good service. Still trying…");
      void import("@/native/carplayBridge").then((m) => {
        if (m.isCarplayConnected()) {
          m.carplaySpeak("This is taking a while — make sure you have good service. I'm still trying.", 1.0);
        }
      }).catch(() => {});
    }, 12_000);
    return () => clearTimeout(t);
  }, [loading]);

  // (Morgan) One-time expectation note: after picking a destination, the
  // final three routes take a few seconds to settle. Dismiss-forever.
  useEffect(() => {
    if (!loading || !destination) return;
    try {
      if (localStorage.getItem("pleasant.routesEtaNotice.v1") === "done") return;
    } catch { return; }
    // NOT "three" (Morgan, Aug 31). The count is a promise the pipeline cannot
    // keep: the coherence gates drop candidates, the retrace gate is absolute
    // since Aug 30, and a starved pool routinely yields two. Naming a number
    // turns a normal outcome into a visible failure.
    toast.info("Your routes will appear in a few seconds — they get better as they settle.", {
      duration: 8000,
      action: {
        label: "Don't show again",
        onClick: () => {
          try { localStorage.setItem("pleasant.routesEtaNotice.v1", "done"); } catch { /* ok */ }
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Billing boot: configure RevenueCat with the signed-in identity and
  // adopt the server-side drive count (reinstalls don't reset the meter).
  useEffect(() => {
    void (async () => {
      const { configurePurchases, syncDriveCountFromProfile, syncGrandfatherFromAccount } =
        await import("@/lib/pleasantPlus");
      await configurePurchases(user?.id ?? null);
      await syncDriveCountFromProfile();
      await syncGrandfatherFromAccount();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    // Pipeline tracer (Xcode console): logs EVERY card-set change, BEFORE
    // any car gating — so "computed but not delivered" vs "never computed"
    // is decidable from one log. Cheap; remove with the Stage 0 ticker.
    if (displayedScoredWithDetours?.length) {
      console.log(
        `PLEASANT CARDS: ${displayedScoredWithDetours.map((c) => c.classification).join(", ")}` +
          `${loading ? " (still loading)" : ""}`,
      );
    }
    // A trip planned on the PHONE now reaches the dash too, not only one the
    // car asked for — see carShouldShowTrip.
    if (!carShouldShowTrip()) return;
    // A LONG TRIP TAKES LONGER — SAY SO, ALOUD AND IN WRITING (Morgan: "if
    // long trips take longer to load we just have to say that with a note and
    // with siri"). Boston is 191 miles; enrichment on a trip that size runs
    // past its budget, so the driver sits watching a label. Silence makes that
    // read as broken. carplayAnnounce writes it across the car screen AND
    // speaks it, which is the right pairing for someone who shouldn't look
    // down. Once per trip — a repeated apology is worse than the wait.
    const firstCard = displayedScoredWithDetours?.[0];
    if (routeGenStatus !== "ready" && firstCard) {
      const miles = firstCard.route.distance / 1609.344;
      const key = `${destination?.name ?? ""}|${Math.round(miles)}`;
      if (miles >= 60 && longTripNoticeRef.current !== key) {
        longTripNoticeRef.current = key;
        const where = destination?.name ? `${destination.name} is a long way. ` : "";
        carplayAnnounce(
          `${where}Finding the pleasant ways takes a little longer on a trip this size — I'll show them the moment they're ready.`,
          1.0,
          // Screen gets the short form. Spoken prose that runs 130 characters
          // is a paragraph on a dashboard, and it arrived clipped mid-word.
          destination?.name
            ? `${destination.name} is a long way — finding the pleasant ways`
            : "A long way — finding the pleasant ways",
        );
      }
    }
    // THE CAR WAITS FOR THE REAL CARD SET (Morgan: "it just says go before
    // pleasant routes load — show the route cards first").
    //
    // `loading` is NOT the finish line. generateRoutes clears it at the end of
    // the cheap first pass (setLoading(false) right after baseClassified, which
    // runs with scenicAnchorsLoaded: false), so the phone can fill its card
    // list progressively. The phone gets away with that because the user
    // watches cards arrive. The car cannot: Apple's preview panel opens on
    // whatever it's handed and puts a GO button on it — so a Fast-only
    // snapshot became a startable trip, and the pleasant option the driver
    // came for arrived after they'd already left.
    //
    // `routeGenStatus` is the honest signal and was already here: "partial"
    // marks that same cheap pass, "ready" the real set. Wait for ready. If a
    // trip genuinely has no Pleasant, ready still fires and the car shows the
    // options that do exist — the driver is never stranded, just never rushed.
    // TWO IS A CHOICE — STREAM IT (Morgan, Aug 5: "routes load a lot faster
    // on the phone than on carplay" → "yes do it"). The old gate ALSO waited
    // for routeGenStatus === "ready", which on long trips meant the car sat
    // in "Finding your routes" while the phone already showed cards. The
    // Boston lone-GO hazard was never about readiness — it was about a
    // single-route panel — and the `unordered.length < 2` guard below still
    // blocks that absolutely. So: push the moment TWO honest choices exist,
    // and let the third stream in (the car's "Found 2 routes — still looking
    // for more" banner + sig-keyed re-render were built for exactly this).
    if (!destination) return;
    // STALE-CARD GUARD (Morgan's "Fast · 40 min" on a 10-min trip): during
    // the new trip's streaming window, a leftover card from the PREVIOUS
    // search (the comparison baseline refreshes async) can ride along with
    // an absurd time and the wrong polyline. Any card whose route doesn't
    // actually END at the current destination never reaches the car.
    const destC = destination.coords;
    const endsAtDestination = (c: (typeof displayedScoredWithDetours)[number]) => {
      const coords = c.route.coordinates;
      const last = coords?.[coords.length - 1];
      // Also: a "route" with no real duration is a data hole, not an option
      // (Morgan's "Fast · 0 min" photo).
      return !!last && haversine(last, destC) <= 250 && (c.route.duration ?? 0) > 30;
    };
    const unordered = displayedScoredWithDetours.filter(endsAtDestination);
    if (!unordered || unordered.length === 0) return;
    // "ready" IS NOT ENOUGH ON A LONG TRIP. generateRoutes fires ready at the
    // enrichment CEILING too (`READY at ceiling — enrichment abandoned…`),
    // where its own comment says "the cards on screen stay". On a 191-mile
    // trip enrichment is precisely what runs long, so ready can arrive while
    // the only card is the cheap pass's Fast route — and the car builds a GO
    // panel out of it. That is the Boston case: a distance and a GO button,
    // pressed by anyone, while the engine is still looking.
    //
    // A PREVIEW MUST BE A CHOICE (Morgan: "this should never be a screen").
    //
    // CarPlay renders a one-choice trip with no selection rows at all — just
    // the destination, the distance and a green GO. That is the Boston
    // screenshot. showRouteChoices() in Swift guards against an EMPTY set but
    // not against a set of one, and my first pass at this only blocked a lone
    // card classified "fastest" — which Boston sails past, because there
    // Pleasant collapses onto Fast and arrives labelled "pleasant".
    //
    // So: fewer than two options is not a choice, it is a commitment dressed
    // as one. Hold the searching state until there is something to pick
    // between. Trips with two honest options (Fast + Scenic, no Pleasant)
    // are unaffected.
    // (Morgan, approved) Pleasant leads on the car — Apple's preview panel
    // opens on the FIRST route choice, so array order IS the default pick.
    const rank = (cls: string) => (cls === "pleasant" ? 0 : cls === "fastest" ? 2 : 1);
    // TWINS ARE ONE ROUTE (rule 3 + Morgan, Aug 5: streaming-at-2's biggest
    // hazard is the cheap-pass Pleasant riding Fast's exact geometry — two
    // pages of the same road is not a choice). Same duration (±45s), same
    // distance (±400m), AND same midpoint (<500m) = the same road; keep the
    // higher-ranked card. If dedupe leaves one, the <2 guard holds the car
    // in "still looking" — the banner does the communicating.
    const midpoint = (c: (typeof unordered)[number]): LngLat | null => {
      const coords = c.route.coordinates;
      return coords && coords.length > 2 ? coords[Math.floor(coords.length / 2)] : null;
    };
    const deduped: typeof unordered = [];
    for (const c of [...unordered].sort((a, b) => rank(a.classification) - rank(b.classification))) {
      const isTwin = deduped.some((d) => {
        if (Math.abs(d.route.duration - c.route.duration) > 45) return false;
        if (Math.abs(d.route.distance - c.route.distance) > 400) return false;
        const m1 = midpoint(d);
        const m2 = midpoint(c);
        return !m1 || !m2 || haversine(m1, m2) < 500;
      });
      if (!isTwin) deduped.push(c);
    }
    // ONE ROUTE AT READY IS AN ANSWER, NOT A HAZARD (Morgan, Aug 7,
    // Umbria: a 5-minute village hop never reached the car — Pleasant
    // rightly collapsed onto Fast, dedupe merged them to ONE, and this
    // guard held the car in "still looking" forever while the phone
    // showed the route). The Boston hazard was a lone card WHILE THE
    // ENGINE WAS STILL LOOKING — a commitment dressed as a choice. A lone
    // card once generation reports ready is simply the honest truth:
    // short trips often have exactly one sensible way to go. Block the
    // single card during "partial"; release it at "ready".
    if (deduped.length < 2) {
      if (routeGenStatus !== "ready" || loading) return;
      if (deduped.length === 0) return;
      console.log("[cards→car] single-route trip at ready — pushing the lone honest card");
    }
    const cards = deduped; // already rank-sorted
    const minDur = Math.min(...cards.map((c) => c.route.duration));
    // (Morgan, approved) Car cards never show a sentence that can truncate.
    // Send the phone card's SHORT headline ("Along the Pocantico River");
    // anything too long for the preview panel's ~2 lines is dropped so the
    // Swift side falls back to the time line instead of an ellipsis. Built
    // by buildRouteReason, so it is brand-free by construction — never the
    // long `explanation` prose ("we don't want a mention of google at all").
    const carPeers = (self: (typeof cards)[number]) =>
      cards
        .filter((p) => p !== self)
        .map((p) => ({
          features: p.features,
          durationS: p.route.duration,
          distanceM: p.route.distance,
          dominantRoad: dominantRoadLabel(p),
          classification: p.classification,
        }));
    const shortReason = (c: (typeof cards)[number]): string => {
      // Full story now — the screen shows what fits (fit-fallback
      // variants) and the VOICE reads the whole thing on selection.
      const headline = buildRouteReason(c, minDur, carPeers(c)).headline;
      // CAR-ONLY shortening (Morgan, Aug 8: "'The fastest way is also the
      // pleasant one here' seems too long — just make it say Fast & Pleasant
      // on carplay"). The phone keeps the full sentence.
      if (/fastest way is also the pleasant/i.test(headline)) return "Fast & Pleasant";
      const LIMIT = 110;
      if (headline.length <= LIMIT) return headline;
      // NEVER CUT MID-WORD, NEVER SEND AN ELLIPSIS (Morgan: "the descriptions
      // can't be so long that we can't read the whole thing"). The old
      // `slice(0, 107) + "…"` produced exactly the truncation the car code
      // says must never happen — CarPlaySceneDelegate builds fit-fallback
      // variants precisely so a row degrades to a shorter HONEST line rather
      // than a cut one, and then we handed it a pre-cut string anyway.
      //
      // Take whole words up to the limit, then prefer to end on a clause
      // boundary so the row reads as a finished thought instead of a
      // sentence that stops. (No regex lookbehind here — Safari only gained
      // it in 16.4 and a parse error would take down the whole bundle.)
      let acc = "";
      for (const w of headline.split(/\s+/)) {
        const next = acc ? `${acc} ${w}` : w;
        if (next.length > LIMIT) break;
        acc = next;
      }
      const boundary = Math.max(acc.lastIndexOf(","), acc.lastIndexOf(";"));
      if (boundary > LIMIT * 0.5) acc = acc.slice(0, boundary);
      return acc.replace(/[\s,;–—-]+$/, "") || headline.split(/\s+/)[0];
    };
    // Town context for the preview card's second line — never the street
    // address again (the "3 Macpherson Avenue / 3 Macpherson Avenue" dup).
    const destName = destination.name ?? destination.place ?? "Destination";
    const destPlace = (() => {
      const raw = (destination.place ?? "").trim();
      if (!raw || raw === destName) return "";
      // "3 Macpherson Ave, Sleepy Hollow, NY 10591, United States"
      //   → drop any leading component that repeats the name, drop the
      //     country, keep "Sleepy Hollow, NY 10591".
      const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
      const trimmed = parts
        .filter((p, i) => !(i === 0 && destName.toLowerCase().startsWith(p.toLowerCase())))
        .filter((p) => !/^(united states|usa|us)$/i.test(p));
      return trimmed.slice(0, 2).join(", ");
    })();
    carplayPushRouteChoices({
      destinationName: destName,
      destinationPlace: destPlace,
      // Generation done = the car stops saying "still looking" (Aug 7).
      final: routeGenStatus === "ready" && !loading,
      choices: cards.map((c) => ({
        classification: c.classification,
        title:
          // LONE COLLAPSED CARD says BOTH truths (Morgan, Aug 7, after the
          // village-hop fix: "these can say pleasant is also fastest") —
          // when Pleasant merged with Fast into one route, the single card
          // reads "Fast & Pleasant", matching the phone's phrasing.
          cards.length === 1 && c.classification === "pleasant"
            ? "Fast & Pleasant"
            : c.variantLabel ??
              (c.classification === "fastest" ? "Fast" : c.classification === "pleasant" ? "Pleasant" : "Scenic"),
        reason: shortReason(c),
        etaSeconds: c.route.duration,
        distanceMeters: c.route.distance,
        extraSeconds: Math.max(0, c.route.duration - minDur),
        coordinates: c.route.coordinates,
        selected: c.classification === "pleasant",
      })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // carSendNonce is in the deps so "Send to car" re-runs this on demand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedScoredWithDetours, loading, routeGenStatus, destination, carSendNonce]);

  // Resolve the currently-selected route from the displayed list (which
  // includes the Google carrier in place of the engine's hidden "fastest"
  // entry). When `selectedClass === "fastest"` the user is on the Google
  // slot, so the map polyline + downstream details all use Google's data.
  // WHAT WE RECOMMENDED, remembered for the rating (Morgan, Aug 31).
  // recommendedClass is computed deep inside the card-list render and is not
  // in scope at the feedback sheet. A 1-star on the card we pushed is a
  // different signal from a 1-star on one the driver chose over our advice,
  // and route_feedback could never tell them apart.
  const recommendedClassRef = useRef<string | null>(null);
  const selectedRoute =
    displayedScoredWithDetours.find((r) => r.classification === selectedClass) ??
    displayedScoredWithDetours.find((r) => r.classification === "pleasant") ??
    displayedScoredWithDetours[0];

  // [nav-trace] BOUNDARY 1 — what the tapped card resolves to, RIGHT NOW
  // (docs/OPEN_ETA_BUG.md / Aug 11 drive: card said ~4h, navigation opened at
  // ~7h). `selectedClass` is a string and this re-resolves every render, so
  // the resolved OBJECT can drift while the highlight doesn't. Logs only when
  // the resolved identity/numbers change, so reshuffles are visible without
  // per-render spam. Also prints which number the card is DISPLAYING: the
  // card shows trafficDurationS when valid (RouteCard), while navigation's
  // fallback ETA divides by route.duration — if those diverge, card and nav
  // screen are reading different numbers for the same route. Logging only —
  // no behaviour change.
  const navTraceSelectionRef = useRef("");
  useEffect(() => {
    if (!selectedRoute) return;
    const trafficS = validTrafficEtas[selectedRoute.classification]?.durationS;
    const line =
      `[nav-trace] card selection: selectedClass=${selectedClass} → ` +
      `${selectedRoute.classification} ${routeEtaFragment(selectedRoute.route, getRouteSource(selectedRoute.route))} ` +
      `trafficDurationS=${trafficS != null && Number.isFinite(trafficS) ? Math.round(trafficS) : "—"} (secondary — ignored) ` +
      `cardShows=route.duration (canonical this release)`;
    if (line === navTraceSelectionRef.current) return;
    navTraceSelectionRef.current = line;
    // eslint-disable-next-line no-console
    console.log(line);
  });

  // The offered-detours list, rendered INLINE on the selected route card
  // (Morgan) rather than in a separate panel below the cards. Only built for
  // the selected route; passed to that card via the `detourPanel` prop.
  const detourInlinePanel = selectedRoute && startCoords && destination ? (
    <div ref={detourPanelRef}>
      <button
        type="button"
        onClick={() => setDetoursExpanded(false)}
        className="w-full mb-1 px-1 flex items-center justify-end gap-1 text-[11px] text-muted-foreground hover:text-foreground"
      >
        Hide stops <ChevronUp className="w-3.5 h-3.5" />
      </button>
      <DriveByOptions
        routeCoords={selectedRoute.route.coordinates}
        routeKey={`${selectedRoute.classification}-${selectedRoute.route.coordinates.length}`}
        travelMode={mode}
        activeWaypointIds={detourByClass[selectedClass]?.picks.map((p) => p.id) ?? []}
        onPick={(place) => toggleDetourForSelected(place)}
        onPickMany={(places) => addDetoursForSelected(places)}
        onClear={() => {
          const cls = selectedClass;
          setDetourByClass((prev) => { const n = { ...prev }; delete n[cls]; return n; });
        }}
      />
    </div>
  ) : null;

  // Per-turn "color" notes for the selected route — a park, river, or
  // landmark this route passes attached to the turn whose road is named for
  // it. Real-drive note (#27): "add some color or interest to the directions."
  // Built once per route and shared by the trip-details directions list and
  // the in-drive all-directions sheet.
  const selectedColorNotes = useMemo(
    () =>
      selectedRoute
        ? buildRouteColorNotes(selectedRoute.route.steps, selectedRoute.features)
        : [],
    [selectedRoute],
  );

  // Auto-enable the Google polyline overlay when Google's route is loaded
  // AND it differs meaningfully from the currently-selected Pleasant route
  // (jaccard overlap < 0.7 on the polyline buckets). Only fires when the
  // user has never explicitly set the showGoogleRoute preference — once
  // they click the on-map toggle, their explicit choice wins from then on.
  // This is Option B from the "auto-draw Google" decision: the user sees
  // both lines side by side whenever there's something interesting to
  // compare, without having to discover the "Show Google's route" button.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Respect explicit preference — don't override if user has toggled.
    if (window.localStorage.getItem(GOOGLE_OVERLAY_KEY) !== null) return;
    if (!googleRoute?.coordinates || !selectedRoute?.route?.coordinates) return;
    const gBuckets = geometryBuckets(googleRoute.coordinates);
    const pBuckets = geometryBuckets(selectedRoute.route.coordinates);
    const overlap = jaccard(pBuckets, gBuckets);
    if (overlap < 0.7) {
      setShowGoogleRouteState(true);
    } else {
      setShowGoogleRouteState(false);
    }
    // selectedClass intentionally triggers recompute when user picks a
    // different card — Google's overlap with Adventure may differ from its
    // overlap with Wander.
  }, [googleRoute?.coordinates, selectedRoute?.route?.coordinates, selectedClass]);

  // ---- Highlights toast: fires once per new route result ----
  // Builds a short "what's on this route" preview from the selected
  // route's named features (water, parks, nature, historic, landmarks).
  //
  // It must fire ONLY after a genuine route REQUEST completes — i.e. when
  // `loading` goes true→false. A route that simply appears without a
  // loading cycle is a restore (resume-after-reload, or the offline
  // cache), and the user did NOT ask for it. Real bug: on app open the
  // resume feature restored the last trip, and this toast then announced
  // "Featured along your route" — a stale trip's ponds — on the landing
  // screen, before the user had requested anything.
  const highlightsPrevLoadingRef = useRef(false);
  useEffect(() => {
    const wasLoading = highlightsPrevLoadingRef.current;
    highlightsPrevLoadingRef.current = loading;
    if (loading || !selectedRoute) return;
    // No preceding loading cycle → the route was restored, not requested.
    if (!wasLoading) return;
    const f = selectedRoute.features;
    const groups: { Icon: typeof Droplets; items: string[] }[] = [
      { Icon: Droplets, items: f.waterNames ?? [] },
      { Icon: Trees,    items: [...(f.parkNames ?? []), ...(f.natureNames ?? [])] },
      { Icon: Landmark, items: [...(f.historicNames ?? []), ...(f.landmarkNames ?? [])] },
    ];
    const rows = groups
      .map((g) => {
        const uniq = Array.from(new Set(g.items.map((s) => s.trim()).filter(Boolean))).slice(0, 3);
        return uniq.length ? { Icon: g.Icon, text: uniq.join(", ") } : null;
      })
      .filter(Boolean) as { Icon: typeof Droplets; text: string }[];

    if (rows.length === 0) return;

    toast("Featured along your route", {
      description: (
        <div className="space-y-1 mt-1">
          {rows.map((r, i) => (
            <div key={i} className="text-sm flex items-start gap-2">
              <r.Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0">{r.text}</span>
            </div>
          ))}
        </div>
      ),
      duration: 7000,
      id: `highlights-${selectedRoute.classification}-${selectedRoute.route.coordinates.length}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, selectedRoute?.classification, selectedRoute?.route.coordinates.length]);

  // Lifted baseline resolution — needed both inside the card render loop
  // (for the +X min pill) AND by the BaselineDebugPanel below, so the
  // debug panel reflects exactly what the cards see.
  const baselineOptionsLifted = useMemo(
    () => ({
      google: googleRoute?.durationS,
      fastest: scored.find((r) => r.classification === "fastest")?.route.duration,
      pleasant: scored.find((r) => r.classification === "pleasant")?.route.duration,
    }),
    [googleRoute?.durationS, scored],
  );
  const resolvedBaselineLifted = useMemo(
    () => resolveBaseline(baselineMode, baselineOptionsLifted),
    [baselineMode, baselineOptionsLifted],
  );

  // Local-only debug panel — verifies that the +X min pill, HighwayBadge,
  // and explanation never disagree on which baseline they use. Off by
  // default; the toggle lives in a small floating button bottom-right.
  const [debugOpen, setDebugOpen] = useState(false);

  // Saved-place lookup for the current destination — drives the floating
  // pill: shows "★ Home" when this destination is already saved, otherwise
  // "+ Save this place".
  const savedAtDestination = useMemo(
    () => (destination ? findSavedPlaceNear(destination.coords) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [destination?.coords?.[0], destination?.coords?.[1], savedPlacesVersion],
  );

  // Now that all hooks have run, it's safe to short-circuit into other screens.
  if (!env.ok) {
    return (
      <main className="min-h-screen bg-hero flex items-center justify-center p-6">
        <div className="max-w-lg bg-card rounded-3xl p-8 shadow-elevated border border-border">
          <h1 className="heading text-3xl mb-2">Almost ready</h1>
          <p className="text-muted-foreground mb-6">Pleasant needs the following keys to fetch real routes and map data.</p>
          <ul className="space-y-3">
            {env.missing.map((m) => (
              <li key={m} className="flex items-start gap-3 p-3 rounded-xl bg-muted">
                <code className="font-mono text-sm text-primary">{m}</code>
              </li>
            ))}
          </ul>
        </div>
      </main>
    );
  }
  if (!profile || !introDismissed) {
    return (
      <Onboarding
        onComplete={(p) => {
          setProfile(p);
          setMode(p.mode);
          setIntroDismissed(true);
          // Persist for the session so navigating into a sub-route and back
          // doesn't re-show the intro.
          try { sessionStorage.setItem("pleasant.introDismissed", "1"); } catch { /* ignore */ }
        }}
      />
    );
  }
  if (showQuickSignIn) {
    return <QuickSignIn onDone={() => setSignInDismissed(true)} />;
  }

  // ---------- HOME / MAP screen ----------
  return (
    <main className="min-h-screen bg-background relative overflow-hidden">
      <h1 className="sr-only">Pleasant — Scenic walking, biking and driving route planner</h1>
      {/* Reaches EVERY user, signed in or not — the outage-comms channel
        * email can never be for an app whose users may skip sign-in
        * (Aug 19: 631 devices affected, 9 emailable). See NoticeBanner. */}
      <NoticeBanner onHeightChange={setNoticeBannerHeight} />
      <OfflineBanner
        hasCachedTrip={scored.length > 0}
        onImported={(snap) => {
          setScored(snap.scored);
          setOfflineSnapshot(snap);
          setOfflineRestored(true);
          setOfflinePanelDismissed(false);
        }}
      />
      {offlineRestored && offlineSnapshot && !offlinePanelDismissed && (
        <OfflineRouteDetailsPanel
          snapshot={offlineSnapshot}
          isOnline={isOnline}
          routeCount={scored.length}
          onSwitchToLive={switchToLiveRoutes}
          onDismiss={() => setOfflinePanelDismissed(true)}
          onCleared={() => {
            setScored([]);
            setOfflineRestored(false);
            setOfflineSnapshot(null);
            setOfflinePanelDismissed(false);
          }}
          onImported={(snap) => {
            setScored(snap.scored);
            setOfflineSnapshot(snap);
            setOfflineRestored(true);
            setOfflinePanelDismissed(false);
          }}
        />
      )}
      {/* Location permission is requested inline from the trip-start input
          rather than via a popup prompt. */}
      {/* (#175) Debug overlay — shows route source attribution + slot
          stats. Dismissible. Hidden for the TestFlight build (#200) but
          the diagnostics state + DebugOverlay component are kept in the
          codebase so we can re-enable by uncommenting this line. The
          `routeDiagnostics` state is still populated by generateRoutes;
          rendering it is the only thing we're turning off. */}
      {/* Re-enabled behind a ?debug=1 URL flag so we can inspect the candidate
          pool (which sources made it in, what each slot picked) on a live trip
          without shipping the overlay to everyone. */}
      {debugOverlayOn && <DebugOverlay diagnostics={routeDiagnostics} />}
      {/* Full-bleed map. top overridden inline (PLE-37) so a shown
       *  NoticeBanner pushes the map down instead of the banner floating on
       *  top of it; `inset-0`'s own top-0 is the no-notice fallback. */}
      <div className="absolute inset-0" style={{ top: `${noticeBannerHeight}px` }}>
        <MapView
          /* Pass displayedScored, not scored — otherwise the map draws
           * polylines for routes that have NO card on screen (the engine's
           * "fastest" classification is hidden from the card list because
           * Google is the canonical "fastest" reference). Real-drive bug
           * (#80): "two routes on the map but only one card, and the
           * second route isn't clickable." It was the hidden-fastest
           * polyline rendering on the map with no corresponding card.
           *
           * visibleMapRoutes also gates on `destination` — see PLE-32 in
           * that file for why: without it, closing the destination left
           * every route line stuck on the map with no card left to match. */
          routes={visibleMapRoutes(destination, cardsHeld, displayedScoredWithDetours)}
          selectedClassification={selectedClass}
          onSelectClassification={(cls) => setSelectedClass(cls)}
          start={destination && start ? startCoords : start?.coords}
          end={destination?.coords}
          userLocation={userLocation}
          communityTags={tags}
          showCommunity={showCommunity}
          focusedTagId={focusedTagId}
          travelMode={mode}
          heading={heading}
          headingUp={headingUp}
          onToggleHeadingUp={async () => {
            if (!headingUp) {
              // iOS requires DeviceOrientation permission via a user gesture.
              await requestHeadingPermission();
            }
            setHeadingUp((v) => !v);
          }}
          photoPins={showCommunityPins ? photoPins : []}
          onPhotoPinClick={(pin) => setOpenPin(pin)}
          tipPins={showCommunityPins ? tipPins : []}
          onTipPinClick={(t) => setOpenTip(t)}
          stops={stops.map((s) => ({ coords: s.coords, name: s.name }))}
          detourMarkers={
            // Per-card detours (the selected card's picks) show a star at each
            // stop. Fall back to the legacy appliedDetour markers (AI chat).
            (detourByClass[selectedClass]?.picks.length
              ? detourByClass[selectedClass]!.picks.map((p) => ({ coords: p.center, name: p.name }))
              : appliedDetour?.waypoints
                  .filter((w): w is { label: string; reason?: string; coord: LngLat } => !!w.coord)
                  .map((w) => ({ coords: w.coord, name: w.label, reason: w.reason })))
          }
          // When the FASTEST card carries a detour, the map draws Google's
          // polyline for that card — so swap in the detoured geometry/ETA too.
          googleRouteCoords={detourByClass.fastest?.coords ?? googleRoute?.coordinates ?? null}
          googleDurationS={detourByClass.fastest?.durationS ?? googleRoute?.durationS ?? null}
          googleDistanceM={detourByClass.fastest?.distanceM ?? googleRoute?.distanceM ?? null}
          selectedRouteDurationS={selectedRoute?.route.duration ?? null}
          selectedRouteDistanceM={selectedRoute?.route.distance ?? null}
          selectedRouteCoords={selectedRoute?.route.coordinates ?? null}
          selectedRouteLabel={selectedRoute?.classification === "pleasant" ? "Pleasant route" : selectedRoute?.classification}
          showGoogleRoute={showGoogleRoute}
          onToggleGoogleRoute={() => setShowGoogleRoute(!showGoogleRoute)}
          onLongPress={(p) => requireAuth("Sign in to add to this spot.", () => setLongPressChoice(p))}
          adjustablePin={pinAdjust?.coords ?? null}
          onAdjustablePinMove={(c) => setPinAdjust((cur) => (cur ? { ...cur, coords: c } : cur))}
          onMapClick={(p) => {
            setTapPoint(p);
            if (pickingOnMap) setPickingOnMap(false);
          }}
          className="absolute inset-0"
        />
        {/* Tag FAB — only show on the initial map view (no destination yet).
         *  Once the user picks a destination, the route panel slides up and
         *  the FAB ends up overlapping the route cards on the right side,
         *  making them hard to scroll/tap. Real-world ride note: "the tag
         *  button is in the wrong spot — it makes it hard to navigate the
         *  route cards." Tag-while-navigating lives in NavigationView. */}
        {!destination && (
          <ContributeFab
            defaultLocation={
              userLocation ??
              (selectedRoute && selectedRoute.route.coordinates.length > 0
                ? selectedRoute.route.coordinates[Math.floor(selectedRoute.route.coordinates.length / 2)]
                : startCoords ?? start?.coords)
            }
            mode={mode}
            mapTapPoint={tapPoint}
            onAddPhoto={() => requireAuth("Sign in to pin a photo to this spot.", () => {
              if (!userLocation) {
                toast.info("Locating you…", { description: "Allow location access to pin a photo here." });
                return;
              }
              setPendingPinLocation(userLocation);
              setPendingPinLabel("Your current location");
              setAddPinOpen(true);
            })}
            onAddTip={(coord, label) => requireAuth("Sign in to leave a tip here.", () => {
              setPendingPinLocation([coord[0], coord[1]]);
              setPendingPinLabel(label ?? "Selected spot");
              setAddTipOpen(true);
            })}
          />
        )}
      </div>

      {/* Long-press chooser: photo or tip? */}
      <Dialog open={!!longPressChoice} onOpenChange={(v) => { if (!v) setLongPressChoice(null); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Add to this spot</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              className="h-24 rounded-xl flex flex-col gap-1.5"
              onClick={() => {
                setPinAdjust({ kind: "photo", coords: longPressChoice! });
                setLongPressChoice(null);
              }}
            >
              <Camera className="w-6 h-6" />
              <span className="text-xs font-medium">Photo</span>
            </Button>
            <Button
              variant="outline"
              className="h-24 rounded-xl flex flex-col gap-1.5"
              onClick={() => {
                setPinAdjust({ kind: "tip", coords: longPressChoice! });
                setLongPressChoice(null);
              }}
            >
              <MessageSquare className="w-6 h-6" />
              <span className="text-xs font-medium">Tip</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* "Position the star" confirm bar — shows while the draggable star
       *  is on the map. Drag to fine-tune, then confirm to open the form. */}
      {pinAdjust && (
        <div className="fixed inset-x-0 bottom-safe-6 z-[70] px-4 flex justify-center pointer-events-none">
          <div className="pointer-events-auto bg-card/98 backdrop-blur-md rounded-2xl border border-border shadow-elevated px-4 py-3 max-w-sm w-full">
            <div className="text-sm font-medium text-foreground mb-2">
              Drag the star to the exact spot
            </div>
            <div className="flex gap-2">
              <Button
                className="flex-1 bg-pleasant text-primary-foreground rounded-full h-10"
                onClick={() => {
                  setPendingPinLocation(pinAdjust.coords);
                  setPendingPinLabel("Chosen spot");
                  if (pinAdjust.kind === "photo") setAddPinOpen(true);
                  else setAddTipOpen(true);
                  setPinAdjust(null);
                }}
              >
                Use this spot
              </Button>
              <Button
                variant="outline"
                className="rounded-full h-10"
                onClick={() => setPinAdjust(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Quick sign-in modal — pops up when an action requires auth, then
          re-runs the pending action so the user lands right back on it. */}
      <QuickAuthDialog
        open={!!authPrompt}
        onOpenChange={(v) => { if (!v) setAuthPrompt(null); }}
        reason={authPrompt?.reason}
        onSuccess={() => { /* resume handled by the user-changed effect */ }}
      />

      <Suspense fallback={null}>
        {addPinOpen && (
          <AddPhotoPinDialog
            open={addPinOpen}
            onOpenChange={setAddPinOpen}
            location={pendingPinLocation}
            locationLabel={pendingPinLabel}
            onCreated={(pin) => setPhotoPins((cur) => [pin, ...cur])}
          />
        )}
        {addTipOpen && (
          <AddTipDialog
            open={addTipOpen}
            onOpenChange={setAddTipOpen}
            location={pendingPinLocation}
            locationLabel={pendingPinLabel}
            onCreated={(tip) => setTipPins((cur) => [tip, ...cur])}
          />
        )}
        {openPin && (
          <PhotoPinDetailDialog
            pin={openPin}
            currentUserId={user?.id ?? null}
            onClose={() => setOpenPin(null)}
            onUpdated={(p) => {
              setPhotoPins((cur) => cur.map((x) => (x.id === p.id ? p : x)));
              setOpenPin(p);
            }}
            onDeleted={(id) => setPhotoPins((cur) => cur.filter((x) => x.id !== id))}
          />
        )}
        {openTip && (
          <TipDetailDialog
            tip={openTip}
            currentUserId={user?.id ?? null}
            onClose={() => setOpenTip(null)}
            onUpdated={(t) => {
              setTipPins((cur) => cur.map((x) => (x.id === t.id ? t : x)));
              setOpenTip(t);
            }}
            onDeleted={(id) => setTipPins((cur) => cur.filter((x) => x.id !== id))}
          />
        )}
      </Suspense>

      {/* Scrim behind the open header menu (App Review, Guideline 4): dims
       *  everything so the popover reads as the single focus on iPad. Same
       *  proven full-screen overlay pattern as FirstNavCoach (plain fixed
       *  inset-0 in the component tree — the portal variant only painted
       *  partially in the iPad WebView). z-[35]: above the bottom panel
       *  (z-30), below the popover content (z-50). */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-[35] bg-black/25 animate-fade-in"
          aria-hidden
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Top bar: centered logo on top row, centered search below.
       *  Help & Settings live in the right-side gutter so they don't
       *  pull the logo off-center. */}
      {/* pt-safe-3 (NOT pt-safe): App Review rejection (Guideline 4, iOS 26 /
       *  iPhone 17 Pro Max + iPad compat) showed the logo row CLIPPED under
       *  the status bar — env(safe-area-inset-top) reported too small inside
       *  the WebView. pt-safe-3's max(env()+0.75rem, 3rem) floor is the
       *  documented fix in index.css; the header just wasn't using it. */}
      {/* top overridden inline (PLE-37): was plain top-0, so a shown
       *  NoticeBanner (z-40, in-flow above this absolutely-positioned
       *  header) floated on top of the logo/Sign in/Settings row instead of
       *  pushing it down — those buttons were visible through the banner
       *  but not reachable. */}
      {/* No longer force-hidden when panelState === "max" (PLE-40). That
       *  opacity-0/invisible toggle predates the panel's z-30 stacking fix
       *  below, which already puts the panel's near-opaque background above
       *  this chrome whenever they actually overlap. Forcing it invisible
       *  regardless of overlap blanked the ENTIRE top bar — logo, search,
       *  Drive/Walk/Bike — any time the panel reached "max", even when the
       *  panel itself was short (e.g. just-picked destination, no route
       *  cards yet) and covered nothing. Real-world report: the carrot/
       *  chevron button near Start hid the full top UI. */}
      <div
        className="absolute left-1/2 -translate-x-1/2 w-full max-w-xl z-20 px-2 sm:px-4 pb-3 sm:pb-4 pt-safe-3 pl-safe pr-safe pointer-events-none flex flex-col items-center gap-2"
        style={{ top: `${noticeBannerHeight}px` }}
      >
        {/* Row 1 — logo on the left, action buttons on the right. On narrow
         *  phones we shrink button + logo sizes and gaps so everything fits. */}
        <div className="w-full flex items-center justify-between gap-2 sm:gap-2">
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Open menu"
                className="pointer-events-auto bg-card/95 backdrop-blur-md rounded-full h-9 sm:h-12 pl-2.5 sm:pl-5 pr-2 sm:pr-4 flex items-center gap-1.5 sm:gap-2 shadow-elevated border border-border shrink-0 hover:border-primary/40 hover:shadow-soft transition-smooth group"
              >
                <PleasantLogo size="sm" withPin={false} />
                {/* "Menu" tag is now visible on every screen size — without it
                 *  there's no affordance that the logo is tappable, which was
                 *  the real-world ride note: users didn't realise the logo
                 *  opened the menu. Smaller pill on phones to keep the header
                 *  row tight; the divider only appears at sm+ where there's
                 *  more breathing room. */}
                <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-primary/80 group-hover:text-primary transition-smooth sm:border-l sm:border-border sm:pl-2 pl-1 px-1.5 sm:px-0 py-0.5 sm:py-0 rounded-md sm:rounded-none bg-primary/10 sm:bg-transparent">
                  Menu
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-60 p-2">
              <div className="px-2 pt-1 pb-2">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">Pleasant Routes</p>
              </div>
              <Link
                to="/about"
                className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-mint-soft transition-smooth"
              >
                <Info className="w-4 h-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">About</div>
                  <div className="text-xs text-muted-foreground">What this is</div>
                </div>
              </Link>
              <Link
                to="/built-by-you"
                className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-mint-soft transition-smooth"
              >
                <Heart className="w-4 h-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Built by you</div>
                  <div className="text-xs text-muted-foreground">How the map is shaped</div>
                </div>
              </Link>
              {/* Thin separator before the legal / help rows — small visual
               *  break that says "this group is different from the main
               *  content above." Required for App Store: privacy + support
               *  must be reachable from inside the app. */}
              <div className="my-1 border-t border-border/60" aria-hidden />
              <Link
                to="/support"
                className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-mint-soft transition-smooth"
              >
                <LifeBuoy className="w-4 h-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Support</div>
                  <div className="text-xs text-muted-foreground">Help & contact</div>
                </div>
              </Link>
              <Link
                to="/privacy"
                className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-mint-soft transition-smooth"
              >
                <Shield className="w-4 h-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Privacy</div>
                  <div className="text-xs text-muted-foreground">What we collect, why</div>
                </div>
              </Link>
            </PopoverContent>
          </Popover>
          <div className="flex items-center justify-end gap-1 sm:gap-2 shrink-0">
            {!isMobile && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  aria-label="Help & contact"
                  className="pointer-events-auto bg-card/95 backdrop-blur-md rounded-full w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center shadow-elevated border border-border hover:shadow-soft transition-smooth shrink-0"
                >
                  <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-4">
                <p className="text-sm font-semibold mb-1">Reach out</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Questions, feedback, or if something's off — tell us and we'll make it better.
                </p>
                <div className="flex items-center gap-2 rounded-lg bg-muted/60 px-3 py-2 text-sm">
                  <Mail className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-medium truncate flex-1">support@pleasantroutes.com</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <a
                    href="mailto:support@pleasantroutes.com?subject=Pleasant%20support"
                    className="flex items-center justify-center gap-1.5 rounded-lg bg-primary text-primary-foreground hover:opacity-90 px-3 py-2 text-xs font-medium transition-smooth"
                  >
                    <Mail className="w-3.5 h-3.5" /> Email
                  </a>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText("support@pleasantroutes.com");
                        toast.success("Email copied");
                      } catch {
                        toast.error("Couldn't copy");
                      }
                    }}
                    className="flex items-center justify-center gap-1.5 rounded-lg bg-muted hover:bg-muted/70 px-3 py-2 text-xs font-medium transition-smooth"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                </div>
              </PopoverContent>
            </Popover>
            )}
            {user ? (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    aria-label="Account"
                    className="pointer-events-auto bg-card/95 backdrop-blur-md rounded-full w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center shadow-elevated border border-border hover:shadow-soft transition-smooth shrink-0"
                  >
                    <UserIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56 p-2">
                  <div className="px-2 py-1.5 text-xs text-muted-foreground truncate" title={user.email ?? ""}>
                    {user.email ?? "Signed in"}
                  </div>
                  <Link
                    to="/my-routes"
                    className="flex items-center gap-2 w-full rounded-md px-2 py-2 text-sm hover:bg-muted/60 transition-smooth"
                  >
                    <MapIcon className="w-4 h-4" /> My routes
                  </Link>
                  <button
                    type="button"
                    onClick={() => signOut()}
                    className="flex items-center gap-2 w-full rounded-md px-2 py-2 text-sm hover:bg-muted/60 transition-smooth text-left"
                  >
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                  <div className="my-1 border-t border-border" />
                  <DeleteAccountButton />
                </PopoverContent>
              </Popover>
            ) : (
              <Link
                to="/auth"
                aria-label="Sign in"
                title="Sign in"
                className="pointer-events-auto bg-card/95 backdrop-blur-md rounded-full h-9 sm:h-11 px-3 sm:px-4 flex items-center gap-1.5 shadow-elevated border border-border hover:shadow-soft transition-smooth shrink-0 text-xs sm:text-sm font-medium"
              >
                <LogIn className="w-4 h-4" />
                <span>Sign in</span>
              </Link>
            )}
            {!isMobile && (
              <Link
                to="/explore"
                aria-label="Explore"
                className="pointer-events-auto bg-card/95 backdrop-blur-md rounded-full w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center shadow-elevated border border-border hover:shadow-soft transition-smooth shrink-0"
              >
                <MapIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              </Link>
            )}
            {/* (#193) Top-right pill simplified to a plain Settings icon.
             *  Previously surfaced the user's detourTolerance preset
             *  ("Direct" / "Pleasant" / "Scenic") and offered taps to
             *  change. Under three-cards-always (#190), the user picks by
             *  tapping a card, so the tolerance label is misleading
             *  scaffolding. The icon stays as the only path into Settings
             *  (where Pleasantness Profile + travel mode still live). */}
            <button
              onClick={() => setSettingsOpen(true)}
              aria-label="Settings"
              title="Settings"
              className="pointer-events-auto bg-card/95 backdrop-blur-md rounded-full w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center shadow-elevated border border-border hover:shadow-soft transition-smooth shrink-0"
            >
              <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* PLE-13: first-run intro strip ("Every trip has a nicer way…") and
         *  the "You're early" card removed — see the comment near
         *  routeGenStatus above for why they're gone instead of fixed. */}

        {/* Row 2 — start/end search, centered with comfortable max-width */}
        <div className="w-full max-w-xl pointer-events-auto">
          <TripSearch
            userLocation={userLocation}
            start={start}
            end={destination}
            stops={stops}
            onChange={handleSearchChange}
            onSubmit={handleSearchSubmit}
            onBack={destination ? clearTrip : undefined}
            loading={loading}
            onRequestLocation={() => { void requestLocation(); }}
            isRequestingLocation={locating}
            locationStatus={locationStatus}
            onEditSavedPlace={(p) => setSavePlaceState({ mode: "edit", place: p })}
            onAddSavedPlace={() => {
              // Prefer the destination → then the start → then "search and
              // label" mode (no point preset, the dialog lets the user type
              // any business or address). This makes the button always work
              // instead of bailing out with a toast when nothing is set.
              const target: TripPoint | null = destination ?? start ?? null;
              if (target) {
                const existing = findSavedPlaceNear(target.coords);
                setSavePlaceState(
                  existing
                    ? { mode: "edit", place: existing }
                    : { mode: "create", point: target },
                );
                return;
              }
              setSavePlaceState({ mode: "create", point: undefined });
            }}
            onLabelPoint={(point) => {
              const existing = findSavedPlaceNear(point.coords);
              setSavePlaceState(
                existing
                  ? { mode: "edit", place: existing }
                  : { mode: "create", point },
              );
            }}
            currentSettings={
              profile
                ? {
                    mode,
                    pleasantnessProfile: profile.pleasantnessProfile,
                    detourTolerance: profile.detourTolerance,
                    loves: profile.loves,
                  }
                : undefined
            }
            onApplyTripSettings={(s) => {
              setMode(s.mode);
              if (profile) {
                const updated = saveProfile({
                  loves: s.loves,
                  mode: s.mode,
                  prioritizeCommunity: profile.prioritizeCommunity,
                  pleasantnessProfile: s.pleasantnessProfile,
                  detourTolerance: s.detourTolerance,
                });
                setProfile(updated);
              }
            }}
          />
        </div>

        {/* Mode selector — sits directly below the search bar so it stays
         *  visually attached even when the search collapses to one row. */}
        <div
          role="radiogroup"
          aria-label="Travel mode"
          className="pointer-events-auto bg-card/95 backdrop-blur-md rounded-full p-1 border border-border shadow-elevated flex"
        >
        {MODES.map(({ id, label, Icon }) => {
          const on = mode === id;
          return (
            <button
              key={id}
              role="radio"
              aria-checked={on}
              aria-label={`Travel mode: ${label}`}
              onClick={() => {
                if (id === mode) return;
                // Switch the highlight instantly — don't wait for routing.
                setMode(id);
                if (destination && startCoords) {
                  // A MODE SWITCH IS A NEW SET OF ROUTES, so drop the old one
                  // the instant it stops being true — exactly as
                  // handleSearchSubmit and clearTrip already do. Without this,
                  // everything the previous mode drew stayed on the map for the
                  // whole of the next generation: the three drive polylines, the
                  // per-card detour stars, and — because
                  // `detourByClass.fastest.coords` wins over `googleRoute`,
                  // which the comparison hook DOES clear on a mode change — a
                  // detoured DRIVING line the map had no other way to shed. The
                  // fit-bounds that follows the new routes then dragged all of
                  // it across the screen before it finally vanished. The trip
                  // itself (start / destination / stops / the chosen card) is
                  // unchanged, so none of that is touched.
                  setScored([]);
                  setAppliedDetour(undefined);
                  setDetourByClass({});
                  generationIdRef.current += 1;
                  void generate(startCoords, destination, stops.map((s) => s.coords), stopsToDetourMeta(stops), id);
                }
              }}
              className={`h-11 px-4 sm:px-5 rounded-full flex items-center gap-2 text-sm font-semibold transition-smooth ${
                on
                  ? "bg-pleasant text-primary-foreground shadow-glow scale-105"
                  : "text-foreground/70 hover:text-foreground hover:bg-muted/60"
              }`}
            >
              {on && loading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Icon className="w-4 h-4" />
              } <span>{label}</span>
            </button>
          );
        })}
        {/* LOOP — PARKED FOR 2.3 (Morgan, July 30: "let's save loops for a
         *  later date - drop for now"). Engine, snapping, closed-loop
         *  scoring and tests all remain; only the door is closed. Set
         *  SHOW_LOOP_BUTTON = true to reopen. */}
        {SHOW_LOOP_BUTTON && <button
          type="button"
          aria-label="Pleasant Loop — a drive back to where you started"
          onClick={() => setLoopPickerOpen((v) => !v)}
          className={`h-11 px-4 sm:px-5 rounded-full flex items-center gap-2 text-sm font-semibold transition-smooth ${
            loopPickerOpen || destination?.name === "Pleasant Loop"
              ? "bg-pleasant text-primary-foreground shadow-glow scale-105"
              : "text-foreground/70 hover:text-foreground hover:bg-muted/60"
          }`}
        >
          <RotateCw className="w-4 h-4" /> <span>Loop</span>
        </button>}
        </div>

        {/* Loop duration picker — three taps, no typing. */}
        {SHOW_LOOP_BUTTON && loopPickerOpen && (
          <div className="pointer-events-auto bg-card/95 backdrop-blur-md rounded-3xl p-3 border border-border shadow-elevated flex flex-col gap-2 w-64">
            <p className="text-xs text-muted-foreground px-1">
              How long have you got? We&apos;ll loop you through the pleasant
              roads nearby and bring you home.
            </p>
            <div className="flex gap-2">
              {LOOP_CHOICES.map((c) => (
                <button
                  key={c.minutes}
                  type="button"
                  onClick={() => startLoop(c.minutes)}
                  className="flex-1 h-10 rounded-full bg-pleasant text-primary-foreground text-xs font-semibold shadow-glow"
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* The standalone "+" tag launcher was merged into the blue
       *  ContributeFab — both surfaced the same "tag a spot" flow. */}

      {/* Hint banner shown while the user is "picking" a spot — disappears
       *  on the next map tap (handled by setPickingOnMap(false) above). */}
      {pickingOnMap && (
        <div className="absolute top-48 sm:top-44 left-1/2 -translate-x-1/2 z-30 bg-foreground text-background rounded-full px-4 py-2 text-xs shadow-elevated flex items-center gap-3 animate-fade-in">
          <span>Tap anywhere on the map to pick that spot.</span>
          <button
            onClick={() => setPickingOnMap(false)}
            className="text-background/80 hover:text-background underline underline-offset-2"
          >
            Cancel
          </button>
        </div>
            )}

      {/* Locating spinner */}
      {locating && !userLocation && (
        <div className="absolute bottom-safe-6 left-1/2 -translate-x-1/2 z-10 bg-card rounded-full px-4 py-2 text-xs shadow-soft border border-border flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Finding your location…
        </div>
      )}

      {/* One-time arrow pointing at the Scenic/Balanced/Fast pill in the
       *  top-right of the chrome — shown only to users who arrived via the
       *  Onboarding example trip ("?onboarded=1"). Waits until routes are
       *  actually rendered so the user has context for what Scenic is
       *  doing before we explain where to change it. Tap-to-dismiss + auto-
       *  clears + remembers it's been shown so it never reappears.
       *  Real-world request: "make a little pop up arrow that explains
       *  where [scenic] is selected on the map screen." */}

      {/* Friendly weather pill — sits at the bottom of the screen and
       *  shows conditions at the trip's start (or GPS before the user has
       *  picked one). Hidden while the bottom route panel is expanded so
       *  it doesn't double up on chrome. */}
      {(!destination || panelMinimized) && (
        /* Sits well above the bottom edge so it clears the small "Pleasant"
         *  brand pill that lives in the bottom-LEFT corner (custom Mapbox
         *  attribution replacement in index.css). At bottom-safe-6 the
         *  weather pill was at the same vertical height as the brand pill
         *  and overlapping on narrow iPhone widths. bottom-safe-20 (~80px)
         *  gives a comfortable gap.
         *  z-[5] keeps the weather pill (and its popover) BELOW the top
         *  chrome (z-20) so the Pleasant logo and Menu pill always sit on
         *  top if there's any visual collision. Real-world feedback: "fix
         *  the weather so it's not over the pleasant logo." */
        <div className="absolute left-1/2 -translate-x-1/2 z-[5] bottom-safe-20 flex flex-col items-center gap-2">
          {/* "Take me home" — evenings only + dismissible forever (the
           *  always-on version was removed July 17 at Morgan's request;
           *  this narrow form approved July 29 in the friction batch). */}
          {SHOW_GOING_HOME_CHIP && !destination && eveningNow() && !homeChipDismissed && (
            <div className="flex items-center gap-1.5">
              <GoingHomeChip
                loading={loading}
                onGoHome={(home) => {
                  const startCoords: LngLat | undefined =
                    (start?.coords) ?? userLocation ?? undefined;
                  if (!startCoords) {
                    // Without GPS or a manual start we can't plan a route. Ask
                    // for location instead of silently failing.
                    void requestLocation();
                    return;
                  }
                  const dest: TripPoint = {
                    name: home.label,
                    place: home.place,
                    coords: home.coords,
                  };
                  // Going home starts from an explicit start place if one is set,
                  // otherwise GPS (startPoint null).
                  handleSearchSubmit(startCoords, dest, [], start ?? null);
                }}
              />
              <button
                type="button"
                aria-label="Hide the evening home shortcut permanently"
                onClick={() => {
                  try { localStorage.setItem(HOME_CHIP_DISMISS_KEY, "1"); } catch { /* ok */ }
                  setHomeChipDismissed(true);
                }}
                className="w-6 h-6 rounded-full bg-card/90 border border-border text-muted-foreground text-xs flex items-center justify-center shadow-soft"
              >
                ✕
              </button>
            </div>
          )}
          {/* SAY WHERE TO — the moving driver's front door (Morgan). Only
           *  shows when the phone senses driving speed and no trip is
           *  active. One tap, speak, and the pleasant way starts itself. */}
          {movingLikely && !destination && (
            <button
              type="button"
              onClick={() => { void sayWhereTo(); }}
              className="pointer-events-auto h-13 px-6 py-3 rounded-full bg-pleasant text-primary-foreground text-base font-semibold shadow-glow flex items-center gap-2.5"
              aria-label="Say a destination to start driving there"
            >
              {sayWhereBusy
                ? <Loader2 className="w-5 h-5 animate-spin" />
                : <Mic className="w-5 h-5" />}
              Say where to
            </button>
          )}
          <WeatherBadge coord={start?.coords ?? userLocation} />
        </div>
      )}

      {destination && (
        <section
          // z-30 so the panel sits ABOVE the top chrome (logo + search +
          // Drive/Walk/Bike toggle at z-20). Previously the chrome covered the
          // panel's top section whenever they visually overlapped (especially
          // in max state, but also on smaller phones in default state).
          // Real-world feedback: "the routes are still covered by drive walk
          // bike — they should not go above them."
          //
          // PLE-21 follow-up (patrick): "the top bit of the panel is white
          // and then the section with the cards is translucent." Measured,
          // not guessed — `bg-background/98` generated NO CSS rule at all.
          // Tailwind's bare `/N` opacity modifier only matches values on its
          // theme.opacity scale (multiples of 5: ...90, 95, 100...); 98 isn't
          // one, so the class silently produced zero background-color,
          // leaving this section fully transparent apart from the blur —
          // next to the sticky header below, which uses plain `bg-background`
          // (no suffix) and renders fully opaque. That 0%-vs-100% split is
          // the "white vs translucent" seam. Dropping the suffix makes the
          // whole panel one solid surface, matching the header and every
          // card inside it (see the bg-card notes below).
          className={`absolute inset-x-0 bottom-0 z-30 bg-background backdrop-blur-md border-t border-border rounded-t-3xl shadow-elevated pb-safe transition-all duration-300 ${
            panelState === "min"
              ? "max-h-[112px] overflow-hidden"
              : panelState === "max"
                ? "max-h-[calc(100dvh-3rem)] overflow-y-auto"
                : "max-h-[45vh] overflow-y-auto"
          }`}
        >
          <div
            role="button"
            tabIndex={0}
            // Three-state cycle: min → default → max → min. Lets users
            // expand the route list to fill the whole screen for browsing.
            // Real-world ride note: "be able to maximize route options so
            // it's the whole screen."
            onClick={() =>
              setPanelState((s) => (s === "min" ? "default" : s === "default" ? "max" : "min"))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setPanelState((s) => (s === "min" ? "default" : s === "default" ? "max" : "min"));
              }
            }}
            aria-label={panelState === "min" ? "Expand route panel" : panelState === "default" ? "Maximize route panel" : "Minimize route panel"}

            className="w-full px-4 sm:px-5 pt-3 pb-2 sticky top-0 bg-background backdrop-blur-md text-left shadow-[0_1px_0_0_hsl(var(--border)/0.4)]"
          >
            <div className="w-12 h-1.5 rounded-full bg-muted mx-auto mb-2" aria-hidden />

            {/* Destination name on its OWN row above the action buttons —
             *  previously the buttons (star, preview, start) crowded a long
             *  destination like "777 Old Sleepy Hollow Rd Ext" into a single
             *  truncated word. With the action row below, the destination
             *  gets the full width to breathe. Real-world ride note:
             *  "Preview and star cover where you are going." */}
            <div className="mb-2">
              <div className="text-xs text-muted-foreground">Going to</div>
              <div className="font-medium text-base sm:text-lg truncate">{destination.name}</div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <div className="hidden">{/* spacer placeholder removed — kept for diff stability */}</div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!panelMinimized && (start || userLocation) && (
                  <button
                    type="button"
                    aria-label="Save this trip"
                    title="Save this trip"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!user) {
                        toast.info("Sign in to save trips");
                        // Come back to THIS trip, not to a blank map. The URL was
                        // cleaned after the search ran (so back/forward doesn't
                        // replay it), so stashing window.location here returned the
                        // user to /app with no params and everything they had just
                        // searched for was gone. Rebuild the trip link from live
                        // state instead -- the same format a shared link uses, so
                        // the mount effect rehydrates it and re-runs the route.
                        try {
                          sessionStorage.setItem("postAuthReturnTo", tripReturnPath());
                        } catch { /* ignore */ }
                        navigate("/auth");
                        return;
                      }
                      // NO window.prompt HERE. It is a native modal that
                      // freezes the whole WebView -- 22.4 s of blocked touch
                      // and a killed content process on Aug 30. Dialog only.
                      const startPoint: TripPoint =
                        start ?? { name: "Current location", place: "Current location", coords: userLocation! };
                      // GPS origin has no resolved address yet, so the default
                      // name would otherwise read the literal placeholder
                      // "Current location" (PLE-39). Reverse-geocode it so the
                      // default reads like "423 S 900 E -> Laird Park" instead.
                      // Best-effort and awaited BEFORE the dialog opens -- never
                      // updated after, so it can't clobber a name the user is
                      // mid-typing. On failure/timeout the placeholder stands.
                      let originLabel = startPoint.name;
                      if (!start && userLocation) {
                        originLabel = (await reverseGeocode(userLocation)) ?? originLabel;
                      }
                      setNameTripDefault(`${originLabel} → ${destination.name}`);
                      setNameTripOpen(true);
                    }}
                    className="h-9 w-9 rounded-full bg-muted hover:bg-muted/70 flex items-center justify-center text-foreground/70 hover:text-foreground transition-smooth"
                  >
                    <Star className="w-4 h-4" />
                  </button>
                )}
                {/* SEND TO CARPLAY (Morgan, Aug 1). Only when a car is
                 *  attached. Automatic mirroring is silent when it fails — a
                 *  button either works or visibly doesn't, and it puts the
                 *  driver in charge of when the dash changes. */}
                {carAttached && displayedScored.length > 0 && (
                  <button
                    type="button"
                    aria-label="Send these routes to CarPlay"
                    title="Send to CarPlay"
                    onClick={(e) => {
                      e.stopPropagation();
                      carplaySendCurrentTrip();
                      setCarSendNonce((n) => n + 1);
                      // "Sending", not "Sent" (Morgan, Aug 7, Umbria: the
                      // old success toast fired even when the lone-card
                      // guard then swallowed the push — the button claimed
                      // victory over a delivery that never happened).
                      toast.success("Sending to CarPlay…");
                    }}
                    className="h-9 px-3 rounded-full bg-pleasant text-primary-foreground text-xs font-semibold shadow-glow flex items-center gap-1.5 transition-smooth"
                  >
                    <Car className="w-4 h-4" /> Send to car
                  </button>
                )}
                {/* The cinematic "Preview" flyover was retired — on iOS it
                 *  repeatedly caused critical issues (a frozen screen, audio
                 *  that wouldn't stop, a motionless preview). It's a delight
                 *  feature, not core, so the entry point is removed rather
                 *  than left to sabotage real drives. RoutePreviewOverlay /
                 *  usePreviewAudio remain in the repo, unreferenced, if we
                 *  ever want to revisit it. */}
                {/* Start is visible regardless of panel minimization state so
                 *  the "tap a route line on the map → tap Start" flow doesn't
                 *  require expanding the panel first. Real-world ride note:
                 *  "I should be able to touch the line of a route and then say
                 *  start and have it just go." */}
                {selectedRoute && (
                  <Button
                    // While route generation is still settling ("partial"),
                    // starting navigation means starting a route that may be
                    // about to be replaced by a better candidate (Greenwich
                    // feedback). Gated for the first few seconds only —
                    // startGated caps at START_GATE_MS so a slow background
                    // enrichment can't hold Start hostage ("comparing goes
                    // way too long").
                    disabled={startGated || cardsHeld}
                    onClick={(e) => {
                      e.stopPropagation();
                      // iOS Safari/WebView requires DeviceOrientation permission
                      // to be requested from a real user gesture. Tap-to-Start
                      // is the cleanest gesture context we have — if we wait
                      // until NavigationView mounts the prompt silently fails
                      // and the compass never streams, leaving the map stuck
                      // pointing north regardless of which way the user is
                      // facing. Fire it inline here; ignore on browsers that
                      // don't gate the event.
                      try {
                        const Ev = DeviceOrientationEvent as unknown as {
                          requestPermission?: () => Promise<"granted" | "denied">;
                        };
                        if (typeof Ev.requestPermission === "function") {
                          void Ev.requestPermission().catch(() => {});
                        }
                      } catch { /* unsupported — proceed */ }
                      // Unlock the browser speech engine from inside this tap so
                      // the first turn cue is audible (web voice is otherwise
                      // silently dropped until a user-gesture-initiated speak).
                      primeWebSpeech();
                      void guardedStartNav(false);
                    }}
                    className="h-11 rounded-full px-5 bg-pleasant text-primary-foreground shadow-glow gap-2 disabled:opacity-70"
                  >
                    {startGated || cardsHeld
                      ? (<><Loader2 className="w-4 h-4 animate-spin" /> Comparing…</>)
                      : (<><Navigation className="w-4 h-4" /> Start</>)}
                  </Button>
                )}
                <span
                  className="h-9 w-9 rounded-full bg-muted flex items-center justify-center text-foreground/70"
                  aria-hidden
                  title={panelState === "min" ? "Expand" : panelState === "default" ? "Maximize" : "Minimize"}
                >
                  <ChevronUp
                    className={`w-4 h-4 transition-transform ${
                      panelState === "min" ? "" : panelState === "max" ? "rotate-90" : "rotate-180"
                    }`}
                  />
                </span>
              </div>
            </div>
          </div>

          {/* Grid items default to `align-self: stretch`, which on a single
              column with auto row heights still stretches each card down to
              fill the panel's available height — the exact "huge empty white
              card" symptom from the real-world ride. We pin items to the top
              on mobile and only switch on stretch + auto-rows-fr at md+ where
              we actually want equal-height side-by-side cards. */}
          {/* Card grid: 1 col on phones, 2 cols from md upward. The previous
           *  xl:grid-cols-3 packed three cards across on wide desktops and
           *  squeezed titles like "Wander" so they broke into "Wan-der" mid-
           *  word while badge text overlapped. Two columns gives every card
           *  enough horizontal space for the title row + meta badges.
           *  Real-world feedback: "don't let the cards be 3 across — it
           *  squishes the text." */}
          {/* PLE-21 follow-up (patrick): "the padding on the top edge of the
           *  cards looks weird." Measured: this grid had no top padding of
           *  its own (gridPaddingTop was 0px) — the only separation from the
           *  sticky header above was the header's own 8px bottom padding, a
           *  fraction of the 20px bottom / 16-20px side padding this same
           *  grid already uses. pt-3 (12px) matches the header's own pt-3 at
           *  the top of the panel, giving the card grid symmetric breathing
           *  room instead of borrowing the header's. */}
          <div className="px-4 sm:px-5 pt-3 pb-5 grid grid-cols-1 md:grid-cols-2 gap-3 items-start md:items-stretch md:auto-rows-fr">
            {loading && (
              <>
                {/* Pleasant-slot placeholder (Morgan): a fast-returning Direct
                    route shouldn't make it look like there's no calmer option.
                    Show an obvious "searching" card in the hero/Pleasant slot
                    until the pleasant route arrives.
                    PLE-21: bg-primary/5 (~5% fill) read as no background at
                    all next to the solid RouteCard/GoogleRouteSlot cards this
                    sits beside — "solid for some of it, translucent for the
                    rest." bg-card matches every other card/slot in this
                    panel; the border-primary/30 ring + icon still mark it as
                    the searching slot. */}
                <div className="relative overflow-hidden rounded-2xl border-2 border-primary/30 bg-card p-4 min-h-[132px]">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary animate-pulse shrink-0" />
                    <span className="font-semibold text-foreground">Finding your most pleasant route…</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                    <span className={slowWalkHint ? "" : "truncate"}>
                      {slowWalkHint
                        ? "Working through thousands of curated walk segments — this one takes a little longer"
                        : "Checking back roads, scenic detours and great roads"}
                    </span>
                  </div>
                  <div className="mt-4 space-y-2" aria-hidden="true">
                    <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
                    <div className="h-3 w-1/2 rounded bg-muted animate-pulse" />
                  </div>
                  <button
                    onClick={cancelGeneration}
                    className="mt-4 h-8 px-3 rounded-full text-xs font-medium border border-border bg-card hover:bg-muted transition-smooth"
                  >
                    Cancel
                  </button>
                </div>
                {/* Direct-slot placeholder. PLE-21: bg-card/40 was the same
                    near-invisible-background issue as the Pleasant-slot
                    placeholder above — solid bg-card to match. */}
                <div className="hidden md:flex rounded-2xl border border-border bg-card p-4 min-h-[132px] items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                  <span>Comparing with the direct route…</span>
                </div>
              </>
            )}

            {/* LATE-ARRIVING Pleasant slot (Morgan: "why did no pleasant
             *  route load? … oh wow it just loaded but it took way too
             *  long"). When the fast card lands first and generation is
             *  still resolving the calm candidates, the lineup LOOKS final
             *  with no pleasant option. Keep the searching card visible in
             *  the slot until a pleasant/scenic card actually arrives. */}
            {!loading && scored.length > 0 &&
              (cardsHeld || (refining &&
                !scored.some((s) => s.classification === "pleasant" || s.classification === "scenic"))) && (
              <div className="relative overflow-hidden rounded-2xl border-2 border-primary/30 bg-card p-4 min-h-[132px]">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary animate-pulse shrink-0" />
                  <span className="font-semibold text-foreground">Finding your most pleasant route…</span>
                </div>
                <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                  <span className="truncate">Checking back roads, scenic detours and great roads</span>
                </div>
                <div className="mt-4 space-y-2" aria-hidden="true">
                  <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
                  <div className="h-3 w-1/2 rounded bg-muted animate-pulse" />
                </div>
              </div>
            )}

            {/* THE SETTLING NOTE (Morgan, Aug 10 — amends the Aug 6 hold; see
             *  the `cardsHeld` comment). Cards now appear as soon as they exist,
             *  so the lineup can still improve underneath the driver. Saying so
             *  is the honest alternative to either hiding the cards or letting
             *  them rearrange silently.
             *
             *  Shown only while generation is genuinely unfinished, and it
             *  disappears on its own. Deliberately quiet — a thin line, not a
             *  banner: this is a "worth waiting a moment" nudge, not an alarm,
             *  and the routes already on screen are real and usable now. */}
            {!loading && scored.length > 0 && routeGenStatus === "partial" && (
              <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                <span>Still looking — a nicer way may still turn up.</span>
              </div>
            )}

            {!loading && scored.length === 0 && routeGenStatus !== "idle" && (
              <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground text-sm text-center px-6">
                {routeGenStatus === "error" && routeGenError ? (
                  // Show the specific, actionable reason (e.g. "too far to walk —
                  // switch to Drive") rather than a generic miss. Falls back to
                  // the generic copy only when we have no message.
                  <span>{routeGenError}</span>
                ) : (
                  <>
                    <span>No routes yet for this destination.</span>
                    <span className="text-xs">Try picking a different spot, or check your connection and search again.</span>
                  </>
                )}
              </div>
            )}


            {/* Greenwich-drive feedback (Morgan): while candidates are still
             *  resolving, the early cards LOOK final and impatient users tap
             *  Start on a route that's about to be replaced. Make the in-flux
             *  state unmissable: a highlighted banner (not a whisper of muted
             *  text) + the card list pulses while status === "partial". Start
             *  itself is also gated below. */}
            {refining && scored.length > 0 && (
              // PLE-37: same no-op bg-pleasant/10 as the intro tile above —
              // this "unmissable" banner had no fill at all. bg-mint-soft.
              <div
                className="flex items-center gap-2 mx-1 mb-2 px-3 py-2 rounded-lg border border-mint/40 bg-mint-soft text-xs font-medium text-foreground"
                aria-live="polite"
              >
                <Loader2 className="w-4 h-4 animate-spin shrink-0 text-pleasant" />
                <span>Still comparing routes — these options may change in a moment</span>
              </div>
            )}

            {/* (The interim "not final yet" text line lived here for about
             *  an hour on Aug 6 — superseded by the full display hold:
             *  provisional cards aren't shown at all, so there's nothing to
             *  caveat. The searching card above is the communication.) */}

            {!cardsHeld && scored.length > 0 && (() => {
              // Use the captured baseline (the *raw* fastest route's pleasant
              // score, before classifyRoutes potentially relabeled it as
              // "pleasant"). Falls back to the lowest pleasant score among
              // surfaced cards if we somehow don't have it yet.
              const baselinePleasant = fastestPleasantBaseline
                ?? (scored.length ? Math.min(...scored.map((s) => s.scores.pleasant ?? 0)) : undefined);
              // At "Scenic" detour tolerance, the user has told us the
              // journey itself is the point — so the LONGEST surfaced
              // route (typically the Adventure/scenic loop) becomes the
              // "For you" recommendation. Otherwise we recommend the
              // pleasant pick as before.
              const isScenicBaseline = profile.detourTolerance === "scenic";
              const epicPleasant = scored.find((r) =>
                r.classification === "pleasant" && (r.route.epicHighwayMeters ?? 0) > 1000,
              );
              // Recommended = the route with the best Pleasant score — the
              // SAME number the user reads on the card it badges. Real-drive
              // note (#28): "the recommendation and the score disagree" — a
              // FOR YOU pick scored 28 sat above a 36. Recommending by the
              // displayed Pleasant score makes that contradiction impossible.
              //
              // Live traffic is now applied at SCORING time (#65) — the
              // pleasant score on each card already reflects the current
              // delay. So the recommender just sorts by pleasant directly;
              // routes that are crawling have already lost points and
              // can't win the FOR YOU badge. This used to be a duplicate
              // TRAFFIC_PENALTY_PER_MIN deduction here on top of the
              // scoring layer, which double-counted traffic for any
              // route that had it.
              const byScore = [...scored].sort((a, b) => {
                // Higher Pleasant score wins.
                const aScore = a.scores.pleasant ?? 0;
                const bScore = b.scores.pleasant ?? 0;
                if (Math.abs(aScore - bScore) > 0.5) return bScore - aScore;
                // Near-tie on score: prefer the lower-highway route, then the
                // route the engine actually labeled "pleasant".
                const aHwy = a.features?.highwayPct ?? 100;
                const bHwy = b.features?.highwayPct ?? 100;
                if (Math.abs(aHwy - bHwy) > 1) return aHwy - bHwy;
                if (a.classification === "pleasant" && b.classification !== "pleasant") return -1;
                if (b.classification === "pleasant" && a.classification !== "pleasant") return 1;
                return 0;
              });
              // RECOMMEND THE PLEASANT ROUTE (Morgan: "we should be advertising
              // pleasant"). The "FOR YOU / Pleasant route" badge must sit on the
              // route the classifier labeled Pleasant — the one featuring the
              // curated great roads — not whichever route posts the highest raw
              // pleasant score. A faster, blander route can out-score a curated
              // backroad once its arterial penalty lands, which is exactly how a
              // Direct route ended up wearing the "Pleasant route" badge while
              // Round Hill got demoted to "Alternate". byScore stays a fallback
              // for when no distinct Pleasant route exists.
              // Read the DISPLAYED array, not `scored`. The engine's
              // classifyThreeCards can label a scenic-corridor route "scenic",
              // while the display layer re-classifies it "pleasant". Computing
              // the recommendation off `scored` then made it miss the Pleasant
              // card entirely and fall back to recommending the highway
              // "fastest" route — which stole the "Pleasant route / For you"
              // brand (the 777 Old Sleepy Hollow → Rowayton bug). (Morgan)
              const pleasantPick = displayedScoredWithDetours.find((r) => r.classification === "pleasant");
              const baseRecommendedClass =
                isScenicBaseline
                  ? scored.reduce((a, b) => (a.route.duration >= b.route.duration ? a : b)).classification
                  : (pleasantPick?.classification
                      ?? epicPleasant?.classification
                      ?? byScore[0]?.classification
                      ?? "pleasant");
              // Traffic-aware override: if the route we'd normally recommend
              // gets hammered by live traffic and there's a comparably pleasant
              // alternative with much less traffic impact, swap to it. This is
              // the direct fix for the "route on top takes me through a road
              // that is busy most of the time" complaint from real-world rides.
              const recommendedClass = (() => {
                // P0, Aug 11 — DISABLED. This is item 1 in its worst form: the
                // secondary traffic estimate here does not merely change a
                // displayed number, it changes WHICH ROUTE IS RECOMMENDED.
                //
                // `baseDelta = baseTraffic.durationS - baseRoute.route.duration`
                // reads as "how much traffic adds", but route.duration already
                // came from the driving-traffic profile (routing.ts:113). So the
                // delta is the DOUBLE-COUNT, not congestion — and any delta over
                // 5 minutes flipped the recommendation on the strength of it.
                //
                // Re-enable only with a signal that is genuinely independent of
                // the planned duration. Until then the engine's own
                // traffic-aware ordering stands.
                return baseRecommendedClass;
                // eslint-disable-next-line no-unreachable
                const baseTraffic = validTrafficEtas[baseRecommendedClass];
                const baseRoute = scored.find((r) => r.classification === baseRecommendedClass);
                if (!baseTraffic || !baseRoute) return baseRecommendedClass;
                const baseDelta = baseTraffic.durationS - baseRoute.route.duration;
                // Only override if traffic adds more than 5 minutes — otherwise
                // we'd flip recommendations on minor congestion fluctuations.
                if (baseDelta <= 300) return baseRecommendedClass;
                const basePleasant = baseRoute.scores.pleasant ?? 0;
                // Find a candidate with much less traffic AND comparable
                // pleasantness (>= 80% of the base pick). Prefer the candidate
                // with the smallest absolute traffic-adjusted duration.
                let best: { cls: string; trafficDur: number } | null = null;
                for (const r of scored) {
                  if (r.classification === baseRecommendedClass) continue;
                  const t = validTrafficEtas[r.classification];
                  if (!t) continue;
                  const delta = t.durationS - r.route.duration;
                  if (delta > baseDelta - 120) continue; // not meaningfully better
                  if ((r.scores.pleasant ?? 0) < basePleasant * 0.8) continue;
                  if (!best || t.durationS < best.trafficDur) {
                    best = { cls: r.classification, trafficDur: t.durationS };
                  }
                }
                const chosen = best?.cls ?? baseRecommendedClass;
                // The "long way" (scenic, or a scenic-corridor discovery) is an
                // optional detour — it must NEVER wear the "For you / Pleasant
                // route" badge. The recommendation belongs to a genuine Pleasant
                // middle-ground; if none exists, recommend Direct.
                const chosenRoute = scored.find((r) => r.classification === chosen);
                const chosenIsLongWay =
                  chosen === "scenic" ||
                  (chosenRoute && getRouteSource(chosenRoute.route) === "scenic-corridor");
                if (chosenIsLongWay) {
                  // Prefer the DISPLAYED Pleasant card if one exists — never
                  // fall back to recommending the highway "fastest" route when a
                  // genuine Pleasant card is on screen.
                  return displayedScoredWithDetours.some((r) => r.classification === "pleasant") ? "pleasant" : "fastest";
                }
                return chosen;
              })();
              recommendedClassRef.current = recommendedClass;
              const internalFastestFeatures = scored.find((r) => r.classification === "fastest")?.features;
              // When Google Directions is available, use IT as the comparison
              // baseline for "+X min" and "% time on busy roads" deltas, so
              // every alternative is measured against the route Google Maps
              // would actually give.
              // Available reference durations for the "+X min" pill. Each
              // is undefined when we couldn't compute it for this trip.
              // Use the lifted baseline (declared at component scope) so
              // the BaselineDebugPanel and the cards see *exactly* the
              // same resolved values — no risk of drift between what the
              // user sees and what the diagnostic reports.
              const baselineOptions = baselineOptionsLifted;
              const resolvedBaseline = resolvedBaselineLifted;
              const baselineDuration = resolvedBaseline.duration ?? fastestDuration;
              const fastestFeatures = googleRoute && internalFastestFeatures
                ? { ...internalFastestFeatures, highwayPct: googleHwyPct ?? internalFastestFeatures.highwayPct }
                : internalFastestFeatures;
              // Disambiguate cards that share the same classification so we
              // never render two cards with identical titles + subtitles.
              // Iterates the *displayed* list (which has the Google carrier
              // in place of the engine's hidden "fastest" entry).
              const classCounts: Record<string, number> = {};
              displayedScoredWithDetours.forEach((s) => { classCounts[s.classification] = (classCounts[s.classification] ?? 0) + 1; });
              // Pre-compute the duplicate index for each card based on the
              // ORIGINAL (engine) ordering — so reordering the list to put
              // the selected card on top doesn't shuffle "Alternate 1/2"
              // labels around between renders.
              const classSeenOrig: Record<string, number> = {};
              const dupIndexByOrigIdx: number[] = displayedScoredWithDetours.map((s) => {
                classSeenOrig[s.classification] = (classSeenOrig[s.classification] ?? 0) + 1;
                return classSeenOrig[s.classification];
              });
              // Card order:
              //   1. The SELECTED card is always at the top. Real-drive
              //      note (#77): "when you select a card, that card
              //      should be highlighted and go to the top." Yesterday
              //      we briefly pinned FOR YOU there instead, but on
              //      drives the user wants the card they just tapped to
              //      be the one in the hero slot — that's the route
              //      they're investigating.
              //   2. The recommended ("FOR YOU") card next, when distinct
              //      from the selected one.
              //   3. Everything else in natural order.
              //   4. Google "Fastest" pinned at the bottom as the
              //      comparison baseline.
              const orderedScored = (() => {
                const indexed = displayedScoredWithDetours.map((r, i) => ({ r, origIdx: i }));
                const selectedPin = indexed.filter(
                  ({ r }) => r.classification === selectedClass,
                );
                const recommendedPin = indexed.filter(
                  ({ r }) =>
                    r.classification === recommendedClass &&
                    r.classification !== selectedClass,
                );
                const fastestPin = indexed.filter(
                  ({ r }) =>
                    r.classification === "fastest" &&
                    r.classification !== recommendedClass &&
                    r.classification !== selectedClass,
                );
                const middle = indexed.filter(
                  ({ r }) =>
                    r.classification !== selectedClass &&
                    r.classification !== recommendedClass &&
                    r.classification !== "fastest",
                );
                return [...selectedPin, ...recommendedPin, ...middle, ...fastestPin];
              })();
              // When the Google fetch is still in flight or has errored,
              // surface a placeholder card in the slot so the comparison
              // promise ("one option always = Google Maps") is visible
              // regardless of network state.
              const googlePlaceholder =
                googleStatus !== "ready" && googleStatus !== "idle" ? (
                  <GoogleRouteSlot
                    key="google-slot-placeholder"
                    state={googleStatus === "loading" ? "loading" : "error"}
                    onRetry={googleStatus === "error" ? retryGoogle : undefined}
                  />
                ) : null;
              // ISOLATED-FASTEST FRAMING: when displayedScored is the engine's
              // fastest fallback (because nothing meaningfully differed from
              // Google), the single card we surface gets framed honestly as
              // "the most direct way — no calmer route on this trip" rather
              // than being marketed as a pleasant alternative.
              const isFallbackOnlyDirect =
                displayedScored.length === 1 &&
                displayedScored[0].classification === "fastest";
              return [
                ...orderedScored.map(({ r, origIdx }) => {
                const isWhyOpen = whyOpenClass === r.classification && selectedClass === r.classification;
                const dupIndex = dupIndexByOrigIdx[origIdx];
                const isDup = classCounts[r.classification] > 1 && dupIndex > 1;
                const displayRoute = isDup
                  ? {
                      ...r,
                      variantLabel: r.variantLabel ?? `Alternate ${dupIndex - 1}`,
                      variantSub: r.variantSub ?? "A different path with a similar feel",
                    }
                  : r;
                return (
                  <RouteCard
                    key={`${r.classification}-${origIdx}`}
                    route={displayRoute}
                    /* Copy only — picks the verb and the car-free-surface
                       wording so a bike card never calls itself a walk. */
                    travelMode={mode}
                    /* optionNumber omitted: surface the semantic label
                       (Direct route / Wander / Adventure) instead of a
                       meaningless "Option N" so users can tell at a glance
                       which card is which. */
                    selected={selectedClass === r.classification}
                    onClick={() => {
                      userTouchedSelectionRef.current = true;
                      setSelectedClass(r.classification);
                    }}
                    onOpenDetails={() => {
                      userTouchedSelectionRef.current = true;
                      setSelectedClass(r.classification);
                      setDetailsOpen(true);
                    }}
                    onOpenDetours={() => {
                      userTouchedSelectionRef.current = true;
                      setSelectedClass(r.classification);
                      setDetoursExpanded(true);
                    }}
                    detourBadgeCount={detourByClass[selectedClass]?.picks.length ?? 0}
                    detourPanel={
                      detoursExpanded && selectedClass === r.classification
                        ? detourInlinePanel
                        : undefined
                    }
                    fastestDuration={baselineDuration}
                    baselineMode={resolvedBaseline.mode}
                    baselineOptions={baselineOptions}
                    onBaselineModeChange={setBaselineMode}
                    profile={profile.pleasantnessProfile}
                    fastestPleasantScore={baselinePleasant}
                    detourCount={appliedDetour?.waypoints.length ?? 0}
                    // (#137) Only the FIRST card of the recommended class
                    // gets the "FOR YOU" pill. Under the per-tolerance model
                    // a single class can produce 2 cards (a primary + an
                    // alternate); marking BOTH as recommended made both
                    // render with the same "Pleasant route — FOR YOU"
                    // label. dupIndex === 1 means this is the primary card
                    // of its classification; subsequent cards in the same
                    // class get dupIndex 2, 3, etc.
                    recommended={r.classification === recommendedClass && dupIndex === 1}
                    fastestFeatures={fastestFeatures}
                    fastestRouteCoords={displayedScored.find((x) => x.classification === "fastest")?.route.coordinates ?? undefined}
                    // The OTHER surfaced routes, so the card's one-liner can
                    // describe what's *different* about this one vs its peers.
                    peers={displayedScored
                      .filter((p) => p !== r)
                      .map((p) => ({
                        features: p.features,
                        durationS: p.route.duration,
                        distanceM: p.route.distance,
                        dominantRoad: dominantRoadLabel(p),
                        classification: p.classification,
                      }))}
                    googleHighwayDeltaPP={
                      googleHwyPct != null ? (r.features.highwayPct ?? 0) - googleHwyPct : null
                    }
                    thisHighwayPct={r.features.highwayPct ?? null}
                    googleHighwayPct={googleHwyPct ?? null}
                    googleDurationS={googleRoute?.durationS ?? null}
                    googleDistanceM={googleRoute?.distanceM ?? null}
                    trafficDurationS={validTrafficEtas[r.classification]?.durationS ?? null}
                    relaxHints={profile ? {
                      hasNoHighways: profile.loves.includes("no-highways"),
                      detourTolerance: profile.detourTolerance,
                      onRemoveNoHighways: () => togglePref("no-highways"),
                      onIncreaseTolerance: () => {
                        const next = profile.detourTolerance === "fast"
                          ? "balanced"
                          : profile.detourTolerance === "balanced"
                            ? "scenic"
                            : "scenic";
                        if (next !== profile.detourTolerance) {
                          // Signal the effect below that we want a fresh
                          // generation once the new tolerance has
                          // committed. Updating the profile synchronously
                          // and calling generate() in the same tick uses
                          // the stale routeGenCtx (memoized on profile),
                          // so the regenerate is deferred to a useEffect.
                          // Real-drive note (#49): "Try Scenic should
                          // automatically signal the app to then run it
                          // through scenic and show results."
                          pendingToleranceRegenRef.current = true;
                          updateProfileField("detourTolerance", next);
                        }
                      },
                    } : undefined}
                    googleOverride={
                      r.classification === "fastest" && googleRoute && mode === "driving"
                        ? {
                            distanceM: googleRoute.distanceM,
                            durationS: googleRoute.durationS,
                            highwayPct: googleHwyPct ?? 0,
                          }
                        : null
                    }
                    isOnlyOption={isFallbackOnlyDirect}
                    /* NO MIDDLE OPTION (Morgan, Aug 9 — Back Bay → New City).
                     * Every candidate on that drive was 94–100% motorway:
                     *   [pleasant] top: mapbox-direct/223m/hwy100%
                     *            | mapbox-direct/224m/hwy100%
                     *            | valhalla-balanced/296m/hwy94%
                     * There is no back road between Boston and New City, and
                     * the 296m option is the same highway 73 minutes slower —
                     * not a nicer one. Rather than invent a third card, the
                     * Fastest card says so. Honest copy: if a route isn't
                     * better, say so. Only when a Scenic card exists, so it
                     * reads as "no middle", not "no options". */
                    noMiddleOption={
                      r.classification === "fastest" &&
                      !displayedScoredWithDetours.some((c) => c.classification === "pleasant") &&
                      displayedScoredWithDetours.some((c) => c.classification === "scenic")
                    }
                    /* "Why this route?" RETURNS (Morgan, Aug 19: "Can we do
                     * that now?" -> "ok let's do it"). The 2024-era panel was
                     * retired for being compelling and often wrong; this one
                     * is grounded - the server checks every proper noun
                     * against the route's own data before a word streams
                     * (route-explain v48 + verify.ts), so the failure mode
                     * that got it pulled cannot recur. Button hides while
                     * refining: an explanation of a route that is seconds
                     * from being replaced explains the wrong route. */
                    whyOpen={isWhyOpen}
                    onToggleWhy={refining ? undefined : () => {
                      userTouchedSelectionRef.current = true;
                      setSelectedClass(r.classification);
                      setWhyOpenClass((prev) => (prev === r.classification ? null : r.classification));
                    }}
                    whyContent={isWhyOpen && profile ? (
                      <Suspense fallback={null}>
                        <RouteExplainChat
                          route={displayRoute}
                          prefs={profile.loves}
                          pleasantnessProfile={profile.pleasantnessProfile}
                          mode={mode}
                          fastestDuration={baselineDuration}
                          start={startCoords ?? undefined}
                          end={destination?.coords}
                          appliedDetour={appliedDetour}
                          userChoices={{
                            detourTolerance: profile.detourTolerance,
                            prioritizeCommunity,
                            selectedClassification: selectedClass,
                            alternatives: displayedScored.map((p) => ({
                              classification: p.classification,
                              distanceMi: p.route.distance / 1609.344,
                              durationMin: p.route.duration / 60,
                              selected: p.classification === selectedClass,
                              label: p.variantLabel,
                            })),
                          }}
                          onApplyDetour={applyAiDetour}
                          embedded
                          onClose={() => setWhyOpenClass(null)}
                        />
                      </Suspense>
                    ) : undefined}
                  />
                );
              }),
              googlePlaceholder,
              ];
            })()}

            {/* (#196) "Try a different pleasant route" button removed.
             *  Morgan: "take away the option to try a different pleasant
             *  route." Under three-cards-always (#190) we surface
             *  Direct/Pleasant/Adventure on every trip — the user already
             *  has three options visible, so a separate "try again"
             *  button just adds noise to the bottom panel. handleTryAgain
             *  is kept on the page for the recap screen (it's still
             *  passed to onTryAgain there). */}

            {/* Tap-to-jump list of community signals along the chosen route.
             *  Only renders when the Pleasant Map layer is on AND we have
             *  signals near the selected polyline. */}
            {showCommunity && selectedRoute && tags.length > 0 && (
              <SignalsAlongRoute
                tags={tags}
                routeCoords={selectedRoute.route.coordinates}
                onFocus={focusTag}
              />
            )}

            {/* "My saved tags" — pulls the signed-in user's own contributions
             *  from `community_tags` and lists the ones near this route so
             *  they can see (and re-focus on the map) what they've previously
             *  reported here. Self-hides when not signed in or no matches. */}
            {selectedRoute && (
              <MySavedTagsAlongRoute
                routeCoords={selectedRoute.route.coordinates}
                onFocus={focusTag}
              />
            )}

            {/* Detours now render INLINE on the selected route card (Morgan) —
             *  see the `detourPanel` prop passed to RouteCard above. The old
             *  standalone bottom panel was removed so the offered options are
             *  listed right on the card. */}

            {/* "Why this route?" now lives inside the selected RouteCard. */}

            {/* Soundtrack feature temporarily disabled. */}

            {/* Trip details + Detours now live ON the route cards (Morgan).
             *  Only Share remains in the bottom bar.
             *
             *  PLE-21: this bar is a plain grid item, same as the cards above
             *  it — on md+ with only ONE card on screen (e.g. a trip with no
             *  calm alternative yet), it lands as that card's row-mate in the
             *  2-column grid and `md:items-stretch md:auto-rows-fr` (set for
             *  the cards, see the note above the grid) stretched it to the
             *  card's full height, leaving a tall mostly-empty cell around a
             *  small button. `md:col-span-2` keeps it on its own full-width
             *  row so it never pairs with a card; `self-start` stops it from
             *  stretching even if auto-placement ever puts something next to
             *  it again. */}
            {!loading && selectedRoute && (
              <div className="flex gap-2 md:col-span-2 self-start">
                <ShareTripButton
                  start={start}
                  end={destination}
                  stops={stops}
                  mode={mode}
                  className="flex-1"
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Trip details sheet — only mount when actually opened so its heavy
       *  ScoreBreakdown + DirectionsView code is excluded from the initial
       *  bundle on mobile. */}
      {detailsOpen && (
        <Sheet open={detailsOpen} onOpenChange={setDetailsOpen}>
          <SheetContent side="bottom" className="rounded-t-3xl max-h-[80vh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="heading text-2xl">Trip details</SheetTitle>
            </SheetHeader>
            {selectedRoute && (
              <Suspense fallback={<div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>}>
                <div className="space-y-4 mt-4">
                  {/* Trip details = the directions, written out as a plain list.
                   *  The old score-bar / "why this route" breakdown was removed
                   *  (the raw 0–100 scores were misleading). */}
                  <DirectionsView
                    steps={selectedRoute.route.steps}
                    stopStepIndices={selectedRoute.route.stopStepIndices}
                    stopLabels={stops.map((s) => s.name).filter(Boolean)}
                    colorNotes={selectedColorNotes}
                  />
                  <Button
                    variant="outline"
                    onClick={() => { setDetailsOpen(false); setFeedbackOpen(true); }}
                    className="w-full h-12 rounded-2xl"
                  >
                    Rate this trip
                  </Button>
                </div>
              </Suspense>
            )}
          </SheetContent>
        </Sheet>
      )}

      {/* Settings: profile, tolerance, loves, mode */}
      <Sheet
        open={settingsOpen}
        onOpenChange={(open) => {
          setSettingsOpen(open);
          if (!open) setSettingsSubPage(null);
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center gap-2">
              {settingsSubPage && (
                <button
                  type="button"
                  onClick={() => setSettingsSubPage(null)}
                  aria-label="Back to preferences"
                  className="w-9 h-9 -ml-2 rounded-full inline-flex items-center justify-center hover:bg-muted transition-smooth"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <SheetTitle className="heading text-2xl">
                {settingsSubPage === "pleasantness"
                  ? "Pleasantness profile"
                  : settingsSubPage === "voice"
                  ? "Navigation voice"
                  : settingsSubPage === "narration"
                  ? "Scenic narration"
                  : settingsSubPage === "locale"
                  ? "Language"
                  : settingsSubPage === "units"
                  ? "Distance units"
                  : settingsSubPage === "nudge"
                  ? "Pleasant nudge"
                  : "Your preferences"}
              </SheetTitle>
            </div>
          </SheetHeader>

          {/* MAIN VIEW — the one thing that matters most is up top
           *  (Detour tolerance), then everything else is a clickable row
           *  that opens its own sub-screen. Real-drive direction: "just
           *  quickest, balanced and scenic — the other settings you can
           *  click on once you're in." */}
          {settingsSubPage === null && (
            <div className="mt-6 space-y-6">
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setSettingsOpen(false);
                    toast("Preferences updated", {
                      description: "Your new settings will apply to the next route.",
                    });
                  }}
                  className="flex items-center justify-center gap-2 w-full h-11 rounded-full bg-pleasant text-primary-foreground text-sm font-medium shadow-glow hover:brightness-110 transition-smooth"
                >
                  <Check className="w-4 h-4" />
                  Update
                </button>
              </div>

              {/* (#165) Detour tolerance picker removed. Under three-cards-
               *  always (#129), every trip returns Direct + Pleasant +
               *  Scenic as separate cards. The tolerance preset is now a
               *  no-op for selection — kept on the profile as "balanced"
               *  for any back-compat reads still in generateRoutes.ts,
               *  but never user-facing. */}

              {/* Drill-down rows for the rest. */}
              <div className="space-y-1.5 pt-2 border-t border-border">
                {/* Trip journal (#68) — sits at the top of the drill-down
                 *  list because it's a feature surface, not a setting. */}
                <button
                  type="button"
                  onClick={() => {
                    setSettingsOpen(false);
                    setJournalOpen(true);
                  }}
                  className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl border border-primary/30 bg-pleasant/8 hover:bg-pleasant/12 transition-smooth text-left"
                >
                  <span className="text-sm font-medium text-foreground">Your trips</span>
                  <ChevronRight className="w-4 h-4 text-primary shrink-0" />
                </button>
                {/* Community pins on the map — OFF by default (Morgan). */}
                <button
                  type="button"
                  onClick={() =>
                    setShowCommunityPins((v) => {
                      const next = !v;
                      try { localStorage.setItem("pleasant.showCommunityPins", next ? "1" : "0"); } catch { /* ignore */ }
                      return next;
                    })
                  }
                  className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl hover:bg-muted transition-smooth text-left"
                >
                  <span className="text-sm font-medium text-foreground">Show community tips &amp; photos on the map</span>
                  <span
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                      showCommunityPins ? "bg-pleasant text-primary-foreground" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {showCommunityPins ? "On" : "Off"}
                  </span>
                </button>
                {/* Drive log — always reachable (Morgan's wrong-way drive had
                 *  no recap, so the evidence was unreachable; the chaotic
                 *  drives are exactly the ones we need the recorder for). */}
                <button
                  type="button"
                  onClick={async () => {
                    const { lastDriveLogText } = await import("@/utils/driveLog");
                    const text = lastDriveLogText();
                    if (!text) { toast.info("No drive recorded yet — take one navigation trip first."); return; }
                    try {
                      if (typeof navigator !== "undefined" && navigator.share) {
                        await navigator.share({ title: "Pleasant drive log", text });
                      } else if (navigator.clipboard) {
                        await navigator.clipboard.writeText(text);
                        toast.success("Drive log copied — paste it anywhere.");
                      }
                    } catch { /* user cancelled the share sheet */ }
                  }}
                  className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl border border-border bg-card hover:bg-muted/50 transition-smooth text-left"
                >
                  <span className="text-sm font-medium text-foreground">Share last drive log</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
                <button
                  type="button"
                  onClick={() => setSettingsSubPage("pleasantness")}
                  className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl border border-border bg-card hover:bg-muted/50 transition-smooth text-left"
                >
                  <span className="text-sm font-medium text-foreground">Pleasantness profile</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
                <button
                  type="button"
                  onClick={() => setSettingsSubPage("voice")}
                  className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl border border-border bg-card hover:bg-muted/50 transition-smooth text-left"
                >
                  <span className="text-sm font-medium text-foreground">Navigation voice</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
                <button
                  type="button"
                  onClick={() => setSettingsSubPage("narration")}
                  className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl border border-border bg-card hover:bg-muted/50 transition-smooth text-left"
                >
                  <span className="text-sm font-medium text-foreground">Scenic narration</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
                {/* Language row REMOVED (Morgan): the locale system only
                 *  translates route-explanation copy, not the app — picking
                 *  French left the UI in English, which over-promises.
                 *  Restore when real i18n ships. LocaleSwitcher + sub-page
                 *  remain in the codebase, unreachable. */}
                <button
                  type="button"
                  onClick={() => setSettingsSubPage("units")}
                  className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl border border-border bg-card hover:bg-muted/50 transition-smooth text-left"
                >
                  <span className="text-sm font-medium text-foreground">Distance units</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
                <button
                  type="button"
                  onClick={() => setSettingsSubPage("nudge")}
                  className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl border border-border bg-card hover:bg-muted/50 transition-smooth text-left"
                >
                  <span className="text-sm font-medium text-foreground">Pleasant nudge</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
                {/* "Show intro again" removed — the multi-step onboarding intro
                    no longer exists; the app opens straight to the route search. */}
              </div>
            </div>
          )}

          {/* SUB-PAGES — full controls for whichever setting was tapped. */}
          {settingsSubPage === "pleasantness" && (
            <div className="mt-6">
              <p className="text-xs text-muted-foreground mb-3">
                What kind of pleasant route do you want?
              </p>
              <PleasantnessProfilePicker
                selected={profile.pleasantnessProfile}
                onChange={(p) => updateProfileField("pleasantnessProfile", p)}
              />
            </div>
          )}
          {settingsSubPage === "voice" && (
            <div className="mt-6">
              <p className="text-xs text-muted-foreground mb-3">
                The voice that reads your turns aloud while you drive.
              </p>
              <VoicePicker />
            </div>
          )}
          {settingsSubPage === "narration" && (
            <div className="mt-6">
              <p className="text-xs text-muted-foreground mb-3">
                Spoken notes about the parks, water, and places you pass.
                Turn it off for a drive you already know well.
              </p>
              <NarrationPicker />
            </div>
          )}
          {settingsSubPage === "locale" && (
            <div className="mt-6">
              <LocaleSwitcher />
            </div>
          )}
          {settingsSubPage === "units" && (
            <div className="mt-6">
              <p className="text-xs text-muted-foreground mb-3">
                Miles or kilometres — on your route cards, in the directions,
                and in the voice that reads your turns aloud.
              </p>
              <UnitsPicker />
            </div>
          )}
          {settingsSubPage === "nudge" && (
            <div className="mt-6">
              <WeekendNudgePicker />
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Suspense fallback={null}>
        {(feedbackOpen || tripEndFeedbackOpen) && (
          <TripEndSheet
            open={feedbackOpen || tripEndFeedbackOpen}
            onClose={() => {
              setFeedbackOpen(false);
              setTripEndFeedbackOpen(false);
            }}
            mode={mode}
            /* No more start/end from live GPS (Sep 25, Heagany report —
             * rating from home put the pin at his apartment). The rated
             * route's own coordinates are the only honest source; if there
             * isn't one (trip never started), the rating saves with no
             * location rather than falling back to userLocation. */
            routeCoordinates={selectedRoute?.route.coordinates ?? null}
            onTryAgain={destination ? handleTryAgain : undefined}
            /* The defendant (Morgan, Aug 3): which card was driven, who built
             * its route, and seconds over the fastest displayed option — so a
             * "felt off" names what actually felt off. */
            routeMeta={selectedRoute ? {
              classification: selectedRoute.classification,
              source: getRouteSource(selectedRoute.route),
              extraSeconds: displayedScoredWithDetours.length > 1
                ? Math.max(0, Math.round(
                    selectedRoute.route.duration -
                    Math.min(...displayedScoredWithDetours.map((r) => r.route.duration)),
                  ))
                : null,
              /* WHICH route, not just which endpoints (Aug 31).
               * MUST be route.coordinates, never navCoordinates: the via
               * points that key the directions cache are sampled from
               * exactly this array by useTrafficEtas ->
               * fetchGoogleTimedEta(r.route.coordinates). Sampling the
               * full-resolution nav geometry instead yields a different
               * 12 points, so the signature would no longer reconstruct
               * the cache key and the replay would silently miss. */
              routeWaypoints: routeWaypointSignature(selectedRoute.route.coordinates),
              pleasantScore: selectedRoute.scores?.pleasant ?? null,
              isRecommended: recommendedClassRef.current == null
                ? null
                : selectedRoute.classification === recommendedClassRef.current,
              routeDistanceM: selectedRoute.route.distance ?? null,
            } : null}
          />
        )}
        {/* Trip journal (#68) — full-screen list of past drives, reachable
         *  from the Settings sheet. */}
        {journalOpen && (
          <TripJournal
            open={journalOpen}
            onClose={() => setJournalOpen(false)}
            /* onDriveAgain intentionally not passed — the button routed
             * "destination from wherever you are" for GPS-started trips
             * (no recorded origin), which misrepresented the remembered
             * drive (Morgan). */
          />
        )}
        {/* Post-drive recap (#63) — full-screen celebratory moment that
         *  fires only when the user actually arrived. The thin TripEndSheet
         *  above still handles manual-close and "rate this route" entry
         *  points; the recap is for the emotional arrival payoff. */}
        {recapOpen && recapSnapshot && (
          <TripRecap
            open={recapOpen}
            snapshot={recapSnapshot}
            onClose={() => {
              setRecapOpen(false);
              // Pays a BANKED rating ask only (a Nice/Gorgeous tag made
              // mid-drive). The recap itself never prompts anymore —
              // retired Aug 6 after the bare-star 1-star reviews.
              void maybeRequestReview();
            }}
            // PLE-19: this was never wired up — the prop stayed undefined
            // since the recap shipped (May 29), so `disabled={!onSave}` in
            // TripRecap left the button permanently greyed out. Same
            // sign-in-then-name flow as the pre-drive "Save this trip" star
            // (above): both end at NameTripDialog -> saveSavedTrip.
            onSave={(destination && (start || userLocation)) ? () => {
              if (!user) {
                toast.info("Sign in to save trips");
                try {
                  sessionStorage.setItem("postAuthReturnTo", tripReturnPath());
                } catch { /* ignore */ }
                navigate("/auth");
                return;
              }
              const startPoint: TripPoint =
                start ?? { name: "Current location", place: "Current location", coords: userLocation! };
              setNameTripDefault(`${startPoint.name} → ${destination.name}`);
              setNameTripOpen(true);
            } : undefined}
            onShare={destination ? async () => {
              // Use the native Web Share API directly (Capacitor wires
              // it to the iOS share sheet automatically). The URL
              // mirrors ShareTripButton's format so a recipient who
              // opens the link in Pleasant Routes gets the same trip
              // pre-filled. Start is optional — if the user navigated
              // from GPS without an explicit start point, the share
              // URL still works (just no pre-filled origin).
              const endParam = `${destination.coords[0]},${destination.coords[1]}|${encodeURIComponent(destination.name ?? destination.place ?? "")}`;
              const startParam = start
                ? `&start=${start.coords[0]},${start.coords[1]}|${encodeURIComponent(start.name ?? start.place ?? "")}`
                : "";
              const url = `https://pleasantroutes.com/app?end=${endParam}${startParam}&mode=${mode}`;
              try {
                if (typeof navigator !== "undefined" && navigator.share) {
                  await navigator.share({
                    title: "A pleasant route",
                    text: `A pleasant route to ${destination.name ?? destination.place ?? "this spot"} — try it.`,
                    url,
                  });
                } else if (typeof navigator !== "undefined" && navigator.clipboard) {
                  await navigator.clipboard.writeText(url);
                  toast.success("Link copied — paste it anywhere.");
                }
              } catch {
                /* user cancelled the share sheet — silent */
              }
            } : undefined}
            onPlanAnother={() => {
              // Closing the recap returns the user to the route-list. We
              // also clear the destination so the search panel is ready
              // for a new trip rather than re-suggesting the one they
              // just finished.
              setDestination(null);
            }}
          />
        )}
        <CarPlayAnnouncement
          eligible={carplayCardEligible}
          busy={navOpen || recapOpen || tripEndFeedbackOpen}
        />
        <PleasantPlusPaywall
          open={paywallOpen}
          onClose={() => setPaywallOpen(false)}
          onSubscribed={() => {
            setPaywallOpen(false);
            // The drive they were trying to start proceeds immediately.
            void guardedStartNavRef.current(pendingStartFromCarRef.current);
          }}
        />
        {navOpen && selectedRoute && destination && (
          <NavigationView
            open={navOpen}
            onClose={() => setNavOpen(false)}
            initialRoute={selectedRoute.route}
            mode={mode}
            end={destination.coords}
            destinationName={destination.name || destination.place}
            loves={effectiveLoves}
            suggestions={selectedRoute.googlePlaces}
            stopLabels={stops.map((s) => s.name).filter(Boolean)}
            // Friendly route name shown above the pleasantness bar so the
            // driver always knows which of the surfaced options they're on.
            routeLabel={
              // The pleasant pick always reads "Pleasant route" — never a
              // variant name — to match the route card.
              selectedRoute.classification === "pleasant"
                ? "Pleasant route"
                : selectedRoute.variantLabel ??
                  (selectedRoute.classification === "fastest" ? "Fastest" :
                   selectedRoute.classification.charAt(0).toUpperCase() + selectedRoute.classification.slice(1))
            }
            // The stable key for bail-rate analytics — see the prop's comment.
            // routeLabel above is a display string and can be a variant name.
            routeClassification={selectedRoute.classification}
            routeFeatureNames={selectedRoute.features}
            // The spoken "why this route" welcome — the thesis made audible
            // at drive start. Null for a direct route (no justification
            // needed). NavigationView speaks it once, via the premium voice.
            introNarration={
              fastestDuration != null
                ? buildRouteIntroNarration(selectedRoute, fastestDuration)
                : null
            }
            onTripEnd={(info) => {
              // Arrival is a clean finish — clear the active-trip marker so a
              // later cold load doesn't resurrect a completed drive. Closing
              // nav early (info.arrived false) keeps the trip restorable.
              if (info.arrived) clearActiveTrip();
              setNavOpen(false);
              // Any finished drive, arrived or not, makes the CarPlay card
              // eligible; it still waits for the recap / end sheet to close.
              if (mode === "driving") {
                markCarplayCardEligible();
                setCarplayCardEligible(true);
              }
              // On ARRIVAL, snapshot enough state to render the post-drive
              // recap (route, ratings, elapsed time, vs-Google numbers) so
              // the recap survives even if the parent state churns afterward.
              // On manual close (not arrived), fall back to the older thumbs-
              // up/down feedback prompt — the user bailed, no celebration. */
              if (info.arrived && selectedRoute && destination) {
                // Rating prompt bookkeeping — an arrived trip is the unit of
                // "the app delivered". The actual ask happens when the recap
                // closes (see TripRecap onClose), gated inside rateApp.
                recordCompletedTrip();
                const snapshot = {
                  route: selectedRoute,
                  elapsedMs: info.elapsedMs,
                  ratings: info.ratings,
                  destinationName: destination.name ?? destination.place ?? "your destination",
                  googleDurationS: googleRoute?.durationS ?? null,
                  googleHighwayPct: googleHwyPct ?? null,
                  mode,
                };
                setRecapSnapshot(snapshot);
                // Persist the trip to history (#68) — same data the recap
                // shows, plus the polyline so the journal can render a
                // thumbnail. Wrapped in try/catch so a localStorage quota
                // problem never blocks the recap.
                try {
                  recordTrip({
                    destinationName: snapshot.destinationName,
                    startName: start?.name ?? start?.place,
                    startCoords: start?.coords,
                    endCoords: destination.coords,
                    mode,
                    distanceM: selectedRoute.route.distance,
                    durationS: Math.round(info.elapsedMs / 1000),
                    pleasantScore: Math.max(0, Math.round(selectedRoute.scores?.pleasant ?? 0)),
                    ratings: info.ratings,
                    highlights: buildTripHistoryHighlights(selectedRoute),
                    coordinates: selectedRoute.route.coordinates.length <= 500
                      ? selectedRoute.route.coordinates
                      : undefined,
                    routeClass: selectedRoute.classification,
                  });
                } catch {
                  /* never block recap on history-write failure */
                }
                // Repeat-destination offer (Morgan's friction batch): the
                // 3rd arrival at an unlabeled spot earns a "save it?" —
                // once per spot, never blocking the recap.
                try {
                  const destC = destination.coords;
                  const spotKey = `pleasant.labelNudge.${destC[0].toFixed(3)},${destC[1].toFixed(3)}`;
                  if (
                    localStorage.getItem(spotKey) !== "done" &&
                    !findSavedPlaceNear(destC) &&
                    listTrips().filter((t) =>
                      Array.isArray(t.endCoords) && haversine(t.endCoords, destC) <= 150,
                    ).length >= 3
                  ) {
                    localStorage.setItem(spotKey, "done");
                    const nudgePoint: TripPoint = {
                      name: snapshot.destinationName,
                      place: destination.place ?? snapshot.destinationName,
                      coords: destC,
                    };
                    toast(`You've been to ${snapshot.destinationName} a few times`, {
                      description: "Save it with a label and it's one tap from everywhere.",
                      duration: 10000,
                      action: {
                        label: "Save it",
                        onClick: () => setSavePlaceState({ mode: "create", point: nudgePoint }),
                      },
                    });
                  }
                } catch { /* nudge is optional by definition */ }
                window.setTimeout(() => setRecapOpen(true), 600);
              } else {
                window.setTimeout(() => setTripEndFeedbackOpen(true), 2000);
              }
            }}
          />
        )}
        {/* The pre-drive cinematic preview was retired — see the note where
         *  its button used to be. */}
      </Suspense>

      {savePlaceState !== null && (
        <Suspense fallback={null}>
          <SavePlaceDialog
            open={savePlaceState !== null}
            onOpenChange={(open) => { if (!open) setSavePlaceState(null); }}
            point={savePlaceState?.mode === "create" ? (savePlaceState.point ?? destination) : null}
            existing={savePlaceState?.mode === "edit" ? savePlaceState.place : null}
            userLocation={userLocation}
          />
        </Suspense>
      )}

      {/* Baseline debug panel hidden — toggle removed per request.
          Component import retained for future re-enable. */}
      <NameTripDialog
        open={nameTripOpen}
        onOpenChange={setNameTripOpen}
        title="Name this trip"
        defaultValue={nameTripDefault}
        onConfirm={(label) => {
          if (!destination) return;
          const startPoint: TripPoint =
            start ?? { name: "Current location", place: "Current location", coords: userLocation! };
          import("@/utils/savedTrips").then(({ saveSavedTrip }) => {
            saveSavedTrip({
              label,
              start: startPoint,
              end: destination,
              stops,
              settings: profile ? {
                mode,
                pleasantnessProfile: profile.pleasantnessProfile,
                detourTolerance: profile.detourTolerance,
                loves: profile.loves,
              } : undefined,
            });
            toast.success(`Saved "${label}"`);
          });
        }}
      />

    </main>
  );
};

export default Index;
